export const en = {
  // Roles
  'role.viewer': 'viewer',
  'role.contributor': 'contributor',
  'role.editor': 'editor',
  'role.admin': 'admin',

  // Common nouns
  'common.taskSingular': 'task',
  'common.taskPlural': 'tasks',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.unknown': 'Unknown',
  'common.unassigned': 'Unassigned',
  'common.close': 'Close',

  // Production-row delete (shared two-step confirm — Biomass logs + Database table)
  'productionRow.delete.confirm': 'Confirm delete',
  'productionRow.delete.confirmWithEvents': 'Confirm delete ({count} linked events)',
  'productionRow.delete.reasonPlaceholder': 'Reason (required)',
  'productionRow.delete.reasonRequired': 'Write why the linked records are deleted.',
  'productionRow.delete.auditReason': 'Deleted with its production row: {reason}',
  'productionRow.delete.noLinkedReason': 'no reason given',
  'datatable.duplicateMerge.auditReason': 'Duplicate rows merged in Database',

  // App shell
  'app.title': 'Sunday app',
  'app.loading': 'Loading Sunday App...',
  'app.team': 'Team',
  'app.user': 'User',
  'app.settings': 'Settings',
  'app.teamName': 'Team name',
  'app.save': 'Save',
  'app.signOut': 'Sign out',
  'app.newTask': 'New task',
  'app.builtWith': 'Built with Vite + React',

  // Sidebar
  'sidebar.heading': 'Sunday app',
  'sidebar.ariaMainNav': 'Main navigation',
  'sidebar.openNav': 'Open navigation menu',
  'sidebar.closeNav': 'Close navigation menu',
  'sidebar.menu': 'Menu',
  'sidebar.close': 'Close',

  // Nav items
  'nav.home': 'Home',
  'nav.database': 'Database',
  'nav.water': 'Water',
  'nav.feed': 'Feed',
  'nav.warehouse': 'Warehouse',
  'nav.sampling': 'Biomass',
  'nav.lots': 'Lots',
  'nav.tasks': 'Tasks',
  'nav.reports': 'Reports',
  'nav.costs': 'Costs',
  'nav.collaboration': 'Collaboration',
  'nav.dashboard': 'Dashboard',
  'nav.mortalityMatrix': 'Mortality Matrix',
  'nav.sfrMatrix': 'SFR Matrix',
  'nav.crewMode': 'Crew Mode',

  // Quick actions
  'qa.signIn': 'Sign in',
  'qa.imports': 'Imports',
  'qa.collaboration': 'Collaboration',
  'qa.communityProfile': 'Community profile',
  'qa.communityDeliveries': 'Delivered lot stories',
  'qa.ownerConsole': 'Owner Console',
  'qa.newFarm': 'New Farm',
  'qa.editFarm': 'Edit facility',
  'qa.mortalityMatrix': 'Mortality Matrix',
  'qa.sfrMatrix': 'SFR Matrix',

  'communityProfile.title': 'Community profile',
  'communityProfile.subtitle': 'Set the presentation identity that future trusted partner sharing can display without changing your tenant membership or permissions.',
  'communityProfile.loading': 'Loading profile…',
  'communityProfile.error.loadFailed': 'Failed to load community profile',
  'communityProfile.error.saveFailed': 'Failed to save community profile',
  'communityProfile.status.saved': 'Profile saved.',
  'communityProfile.identity.title': 'Current identity',
  'communityProfile.identity.accountName': 'Account name:',
  'communityProfile.identity.actor': 'Actor:',
  'communityProfile.identity.role': 'Membership role:',
  'communityProfile.identity.membership': 'Membership ID:',
  'communityProfile.editor.title': 'Presentation profile',
  'communityProfile.editor.subtitle': 'This is the presentation layer for community collaboration. Permissions still come from tenant membership and group membership rules.',
  'communityProfile.editor.displayNameLabel': 'Public display name',
  'communityProfile.editor.displayNamePlaceholder': 'How partners should see your name',
  'communityProfile.editor.displayNameHint': 'Leave blank to keep using the current account display name.',
  'communityProfile.editor.publicBioLabel': 'Public bio',
  'communityProfile.editor.publicBioPlaceholder': 'Short context about your role, specialty, or trusted collaboration scope',
  'communityProfile.editor.publicBioHint': 'Keep this short and factual. It is presentation identity, not an approval or permission rule.',
  'communityProfile.editor.note': 'Community profile data stays online-first and does not alter artifact visibility on its own.',
  'communityProfile.action.save': 'Save profile',
  'communityProfile.action.saving': 'Saving…',
  'communityProfile.access.title': 'Current access context',
  'communityProfile.access.subtitle': 'This is the explicit membership context that future sharing rules can consume without guessing from the token alone.',
  'communityProfile.access.privateContext': 'Private context',
  'communityProfile.access.membershipRole': 'Tenant role',
  'communityProfile.access.activeGroups': 'Active groups',
  'communityProfile.access.invitedGroups': 'Invited groups',
  'communityProfile.access.revokedGroups': 'Revoked groups',
  'communityProfile.access.empty': 'No group contexts yet. Current access is tenant-only.',
  'communityProfile.access.groupId': 'Group ID',
  'communityProfile.access.groupRole': 'Group role',
  'communityProfile.access.membershipStatus': 'Membership status',
  'communityProfile.access.groupMembershipId': 'Group membership ID',
  'communityProfile.access.partnerOrganization': 'Partner organization',
  'communityProfile.access.none': 'None',
  'communityDeliveries.title': 'Delivered lot stories',
  'communityDeliveries.subtitle': 'Review the snapshots that your active partner-group memberships can currently receive through the narrow GROUP delivery boundary.',
  'communityDeliveries.error.loadFailed': 'Failed to load delivered lot stories',
  'communityProfile.groupDeliveries.title': 'Delivered lot stories',
  'communityProfile.groupDeliveries.subtitle': 'Use one active group context to discover the currently promoted lot-story snapshots available through the receiver delivery boundary.',
  'communityProfile.groupDeliveries.noActiveGroups': 'No active group delivery context is available yet.',
  'communityProfile.groupDeliveries.groupSelect': 'Active delivery group',
  'communityProfile.groupDeliveries.loading': 'Loading delivered lot stories…',
  'communityProfile.groupDeliveries.empty': 'No delivered lot stories are currently available for this active group.',
  'communityProfile.groupDeliveries.count': '{count} delivered lot stories available',
  'communityProfile.groupDeliveries.publishedAt': 'Published at',
  'communityProfile.groupDeliveries.open': 'Open delivery snapshot',
  'communityProfile.groupDeliveries.opening': 'Opening…',
  'communityProfile.groupDeliveries.opened': 'Snapshot opened',
  'communityProfile.groupDeliveries.placeholder': 'Select one delivered lot story to open its current snapshot.',
  'communityProfile.groupDeliveries.context.title': 'Current delivery access context',
  'communityProfile.groupDeliveries.context.subtitle': 'This shows the live receiver identity and selected group context before any delivered snapshot is opened.',
  'communityProfile.groupDeliveries.context.selectedGroup': 'Current selected group',
  'communityProfile.groupDeliveries.context.selectedGroupRole': 'Current selected group role',
  'communityProfile.groupDeliveries.list.selectedGroup': 'Live selected group',
  'communityProfile.groupDeliveries.list.selectedGroupRole': 'Live selected group role',
  'communityProfile.groupDeliveries.list.grantedGroup': 'Stored delivery group',
  'communityProfile.groupDeliveries.list.grantedGroupRole': 'Stored granted group role',
  'communityProfile.groupDeliveries.list.partner': 'Live partner organization',
  'communityProfile.groupDeliveries.list.publishedBy': 'Stored published by',
  'communityProfile.groupDeliveries.list.selectedMatch': 'Selected group matches delivery',
  'communityProfile.groupDeliveries.snapshot.title': 'Snapshot summary',
  'communityProfile.groupDeliveries.snapshot.readiness': 'Snapshot readiness',
  'communityProfile.groupDeliveries.error.listFailed': 'Failed to load delivered lot stories',
  'communityProfile.groupDeliveries.error.detailFailed': 'Failed to load the delivered lot story snapshot',
  'collaborationLibrary.title': 'Collaboration',
  'collaborationLibrary.subtitle': 'Artifact library for Collaboration. This slice lists published report snapshots and promoted group lot stories.',
  'collaborationLibrary.loading': 'Loading collaboration artifacts…',
  'collaborationLibrary.error.loadFailed': 'Could not load the collaboration artifact library right now.',
  'collaborationLibrary.searchLabel': 'Search artifacts',
  'collaborationLibrary.searchPlaceholder': 'Search by title, context, or summary',
  'collaborationLibrary.statusFilterLabel': 'Filter by status',
  'collaborationLibrary.statusFilter.all': 'All statuses',
  'collaborationLibrary.visibilityFilterLabel': 'Filter by visibility',
  'collaborationLibrary.visibilityFilter.all': 'All visibilities',
  'collaborationLibrary.refresh': 'Refresh library',
  'collaborationLibrary.count': '{visible} visible · {total} total',
  'collaborationLibrary.type.reportSnapshot': 'Report snapshot',
  'collaborationLibrary.type.lotStory': 'Lot story',
  'collaborationLibrary.status.draft': 'Draft',
  'collaborationLibrary.status.published': 'Published',
  'collaborationLibrary.status.archived': 'Archived',
  'collaborationLibrary.status.revoked': 'Revoked',
  'collaborationLibrary.visibility.private': 'Private',
  'collaborationLibrary.visibility.group': 'Group',
  'collaborationLibrary.visibility.agora': 'Agora',
  'collaborationLibrary.meta.version': 'Current version:',
  'collaborationLibrary.meta.visibility': 'Visibility:',
  'collaborationLibrary.meta.ownerTenant': 'Owner tenant:',
  'collaborationLibrary.meta.publishedAt': 'Published at:',
  'collaborationLibrary.meta.updatedAt': 'Updated at:',
  'collaborationLibrary.meta.notPublishedYet': 'Not published yet',
  'collaborationLibrary.meta.unknown': 'Unknown',
  'collaborationLibrary.action.openDetail': 'Open artifact detail',
  'collaborationLibrary.action.openSource': 'Open source screen',
  'collaborationLibrary.action.detailComingSoon': 'Artifact detail comes next.',
  'collaborationLibrary.empty.title': 'No artifacts yet',
  'collaborationLibrary.empty.subtitle': 'Publish from Reports or promote a lot story from Lots, then come back here to organize and find Collaboration artifacts.',
  'collaborationLibrary.empty.openReports': 'Open Reports',
  'collaborationLibrary.empty.openLots': 'Open Lots',
  'collaborationLibrary.empty.noResultsTitle': 'No matching artifacts',
  'collaborationLibrary.empty.noResultsSubtitle': 'Try a different search term, status filter, visibility filter, or refresh the library.',
  'collaborationDetail.action.backToLibrary': 'Back to artifact library',
  'collaborationDetail.loading': 'Loading artifact detail…',
  'collaborationDetail.notFound.title': 'Artifact not found',
  'collaborationDetail.notFound.subtitle': 'This artifact detail is not available in the current tenant library.',
  'collaborationDetail.section.overview': 'Overview',
  'collaborationDetail.section.provenance': 'Provenance',
  'collaborationDetail.section.preview': 'Snapshot preview',
  'collaborationDetail.section.versions': 'Versions',
  'collaborationDetail.section.actions': 'Lifecycle actions',
  'collaborationDetail.meta.dataset': 'Dataset:',
  'collaborationDetail.meta.generatedAt': 'Generated at:',
  'collaborationDetail.meta.rows': 'Rows:',
  'collaborationDetail.meta.columns': 'Columns:',
  'collaborationDetail.preview.scopeTitle': 'Saved scope',
  'collaborationDetail.preview.sampleTitle': 'Sample rows',
  'collaborationDetail.preview.sampleEmpty': 'No preview rows were captured for this snapshot.',
  // Published snapshots deliberately cap the sample at a handful of rows
  // (evidence only, not a live export) — this ties the visible sample back
  // to the full row count shown above so it doesn't read as truncated by
  // accident. See docs/community-platform-plan.md.
  'collaborationDetail.preview.sampleTruncatedNote': 'Showing {shown} of {total} rows.',
  'collaborationDetail.preview.sampleTruncatedLink': 'View the full report in Reports',
  'collaborationDetail.preview.scopeNone': 'No saved scope',
  'collaborationDetail.preview.scopeFrom': 'From {date}',
  'collaborationDetail.preview.scopeTo': 'Until {date}',
  'collaborationDetail.preview.scopeFacilities': '{count} facilities',
  'collaborationDetail.preview.scopeProductionUnits': '{count} production units',
  'collaborationDetail.preview.scopeLots': '{count} lots',
  'collaborationDetail.provenance.source': 'Source screen:',
  'collaborationDetail.provenance.sourceEntity': 'Source entity:',
  'collaborationDetail.provenance.publishedBy': 'Published by:',
  'collaborationDetail.provenance.notAvailable': 'Not available',
  'collaborationDetail.provenance.lifecycle': 'Lifecycle history',
  'collaborationDetail.versions.publishedAt': 'Published at',
  'collaborationDetail.versions.notPublished': 'Not published',
  'collaborationDetail.versionStatus.published': 'Published',
  'collaborationDetail.versionStatus.superseded': 'Superseded',
  'collaborationDetail.versionStatus.revoked': 'Revoked',
  'collaborationDetail.actions.none': 'No lifecycle actions are currently available for this artifact.',
  'collaborationDetail.actions.note': 'Archive and revoke are wired on this detail slice. Other lifecycle controls remain planned.',
  'collaborationDetail.actions.ownerManagedInSource': 'Lot story lifecycle and owner-managed actions stay in Lots.',
  'collaborationDetail.actions.createDraft': 'Create draft',
  'collaborationDetail.actions.refreshDraft': 'Refresh draft snapshot',
  'collaborationDetail.actions.publish': 'Publish',
  'collaborationDetail.actions.revoke': 'Revoke',
  'collaborationDetail.actions.archive': 'Archive',
  'collaborationDetail.actions.revokeSuccess': 'Artifact revoked. The published snapshot remains readable for history and review.',
  'collaborationDetail.actions.revokeError': 'Could not revoke this artifact right now.',
  'collaborationDetail.actions.archiveSuccess': 'Artifact archived. It remains available in the library with archived status.',
  'collaborationDetail.actions.archiveError': 'Could not archive this artifact right now.',

  // Start
  'start.title': 'Start',
  'start.welcomeNamed': 'Welcome, {name}',
  'start.welcomeAnon': 'Welcome — ready to set up your facility?',
  'start.createFacility': 'Create new facility',

  // New farm
  'newFarm.title': 'New Facility Setup',
  'newFarm.subtitle': 'Create your aquaculture facility profile to start tracking production, monitoring water quality, and managing your fish stock.',
  'newFarm.progress.step': 'Step {current} of {total}: {label}',
  'newFarm.progress.step1Label': 'Facility Information',

  'newFarm.alert.readOnlyCreate': 'Read-only access: this token cannot create facilities.',
  'newFarm.alert.enterName': 'Please enter a facility name',
  'newFarm.alert.enterTimeZone': 'Please enter a facility time zone',
  'newFarm.alert.invalidTimeZone': 'Please enter a valid IANA time zone (e.g. America/Chicago)',
  'newFarm.alert.saveFailed': 'Could not save this facility locally. Please try again.',

  'newFarm.field.name.label': 'Facility Name',
  'newFarm.field.name.placeholder': 'e.g. Riverside Aquaculture Facility',
  'newFarm.field.name.hint': 'Choose a descriptive name for your aquaculture operation',

  'newFarm.field.facilityType.label': 'Facility type',
  'newFarm.field.facilityType.hint': 'Choose whether this facility operates as a farm or as a processing plant.',
  'newFarm.facilityType.farm': 'Farm',
  'newFarm.facilityType.processingPlant': 'Processing plant',

  'newFarm.field.classification.label': 'Facility Classification',
  'newFarm.field.classification.hint': 'Select the intensity level of your aquaculture operation',
  'newFarm.field.classification.other.label': 'Custom Classification',
  'newFarm.field.classification.other.placeholder': 'Enter your custom classification',
  'newFarm.field.classification.other.hint': 'Describe your unique facility classification',

  'newFarm.classification.extensive': 'Extensive - Low density, natural feeding',
  'newFarm.classification.semiIntensive': 'Semi-Intensive - Moderate density, supplemental feeding',
  'newFarm.classification.intensive': 'Intensive - High density, complete artificial feeding',
  'newFarm.classification.other': 'Other - Custom classification',

  'newFarm.field.geography.label': 'Geographic Environment',
  'newFarm.field.geography.hint': 'Select the water environment where your facility operates',
  'newFarm.field.geography.other.label': 'Custom Geography',
  'newFarm.field.geography.other.placeholder': 'Enter your custom geography',
  'newFarm.field.geography.other.hint': 'Describe your unique geographic environment',

  'newFarm.geography.lake': 'Lake - Freshwater lake environment',
  'newFarm.geography.river': 'River - Flowing freshwater system',
  'newFarm.geography.ocean': 'Ocean - Marine saltwater environment',
  'newFarm.geography.estuary': 'Estuary - Brackish water environment',
  'newFarm.geography.inlandSeawater': 'Inland Seawater - Land-based saltwater',
  'newFarm.geography.inlandFreshwater': 'Inland Freshwater - Land-based freshwater',
  'newFarm.geography.other': 'Other - Custom geography',

  'newFarm.field.altitude.label': 'Facility Altitude',
  'newFarm.field.altitude.hint': 'Elevation in meters above sea level (affects dissolved oxygen calculations)',

  'newFarm.field.timeZone.label': 'Time zone',
  'newFarm.field.timeZone.placeholder': 'e.g. America/Chicago',
  'newFarm.field.timeZone.errorInvalid': 'Invalid IANA time zone (e.g. America/Chicago)',
  'newFarm.field.timeZone.hint': 'Used for day/week bucketing across devices; defaults to your browser time zone',

  'newFarm.button.creating': 'Creating Facility...',
  'newFarm.button.createContinue': 'Create Facility & Continue',
  'newFarm.button.backHome': '← Back to Home',
  'newFarm.help.alert': 'Help: This form creates your facility profile for aquaculture management.',
  'newFarm.status.createdLocal': 'Facility saved locally. Server sync is pending.',
  'newFarm.status.createdOffline': 'Facility saved locally while offline. It will sync when you reconnect.',
  'newFarm.status.createdSynced': 'Facility created and synced.',

  'newFarm.card.nextSteps.title': 'Next Steps',
  'newFarm.card.nextSteps.bodyFarm': "After creating your facility, you'll set up culture types, select species, and configure production units.",
  'newFarm.card.nextSteps.bodyProcessingPlant': 'After creating your processing plant, you can register processing sites and start receiving lot transfers without going through farm-only setup.',
  'newFarm.card.security.title': 'Data Security',
  'newFarm.card.security.body': 'Your facility data is stored locally and securely. You maintain full control over your information.',

  // Edit farm
  'editFarm.title': 'Edit Facility',
  'editFarm.subtitle': 'Manage facilities, production units, and offline data.',

  'editFarm.action.export': 'Export',
  'editFarm.action.import': 'Import',

  'editFarm.alert.readOnlyExport': 'Read-only access: this token cannot export facility data.',
  'editFarm.alert.readOnlyImport': 'Read-only access: this token cannot import facility data.',

  'editFarm.alert.exportSuccess': '✅ Data exported successfully! Save this file to import in other browsers.',
  'editFarm.alert.exportFailed': '❌ Export failed. Check console for details.',
  'editFarm.confirm.tenantMismatch':
    '⚠️ This export was created for tenant "{importTenantKey}".\nYou are currently in tenant "{tenantKey}".\n\nImport into the current tenant anyway?',
  'editFarm.confirm.importSummary':
    '📥 Import data with:\n• {facilities} facilities\n• {tasks} tasks\n• {water} water datasets\n• {other} other data items\n\nThis will replace all current data.',
  'editFarm.alert.importSuccess': '✅ Data imported successfully! All facility, database, and water data restored.',
  'editFarm.alert.invalidDataFormat': '❌ Invalid data format. Please select a valid Sunday App export file.',
  'editFarm.alert.importFailed': '❌ Import failed. Please check the file format.',
  'editFarm.status.changeOffline': 'Changes saved locally while offline. They will sync when you reconnect.',
  'editFarm.status.changePending': 'Changes saved locally. Server sync is pending.',
  'editFarm.status.changeSynced': 'Changes saved and synced.',
  'editFarm.status.deleteOffline': 'Deletion saved locally while offline. It will sync when you reconnect.',
  'editFarm.status.deletePending': 'Deletion saved locally. Server sync is pending.',
  'editFarm.status.deleteSynced': 'Deletion synced.',
  'editFarm.status.refreshing': 'Syncing pending changes with the server...',
  'editFarm.status.refreshComplete': 'Pending changes synced.',
  'editFarm.status.saveFailed': 'Could not save the facility changes. Please try again.',
  'editFarm.status.deleteFailed': 'Could not delete the facility data. Please try again.',
  'editFarm.sync.blocked': '{count} facility change(s) are blocked by sync errors.',
  'editFarm.sync.blockedWithReason': '{count} facility change(s) are blocked: {reason}',
  'editFarm.sync.offline': 'Facility changes are queued locally while this device is offline.',
  'editFarm.sync.pending': 'Facility changes are queued and waiting to sync.',
  'editFarm.sync.retryAction': 'Retry sync',
  'editFarm.sync.retrying': 'Retrying sync...',
  'editFarm.sync.retrySynced': 'Facility sync completed.',
  'editFarm.sync.retryOffline': 'Still offline. Facility changes remain queued locally.',
  'editFarm.sync.retryPending': 'Facility changes are still pending sync.',
  'editFarm.sync.retryResolvedMissing': '{count} stale missing change(s) were skipped during retry.',
  'editFarm.sync.retryFailed': 'Unable to retry facility sync right now.',
  'editFarm.sync.refreshAction': 'Refresh facilities',
  'editFarm.sync.refreshing': 'Refreshing facilities...',
  'editFarm.sync.refreshComplete': 'Facilities refreshed.',
  'editFarm.sync.refreshFailed': 'Unable to refresh facilities right now.',
  'editFarm.sync.lastResult': 'Last sync result: {result}',
  'editFarm.sync.lastAttempted': 'Last attempted: {time}',

  'editFarm.confirm.sensitiveChange': '⚠️ This change can impact calculations and sync. Save changes to "{field}"?',
  'editFarm.confirm.confirmCta': 'Confirm',
  'editFarm.confirm.importCta': 'Import',
  'editFarm.sensitiveField.facilityType': 'Facility type',
  'editFarm.sensitiveField.altitude': 'Altitude',
  'editFarm.sensitiveField.timeZone': 'Time zone',
  'editFarm.sensitiveField.speciesPreset': 'Species preset',
  'editFarm.alert.demoCreated': '✅ Demo facility created successfully!',

  'editFarm.chip.facilitySpecies': 'Facility: {facility} • Species: {species}',
  'editFarm.chip.processingPlant': 'Facility: {facility} • Processing plant',
  'editFarm.panel.facilitiesCount': 'Facilities ({count})',
  'editFarm.block.identity': 'Facility identity',
  'editFarm.block.sensitiveConfig': 'Sensitive configuration',
  'editFarm.block.productionUnits': 'Production units',
  'editFarm.block.maintenance': 'Maintenance actions',
  'editFarm.tabs.ariaLabel': 'Facility sections',
  'editFarm.tab.general': 'General',
  'editFarm.tab.units': 'Units',
  'editFarm.tab.culture': 'Culture',
  'editFarm.tab.advanced': 'Advanced',
  'editFarm.general.discard': 'Discard changes',
  'editFarm.general.unsaved': 'There are unsaved changes.',
  'editFarm.units.openHint': 'Click a unit to edit it, set its Crew plans or delete it.',
  'editFarm.units.col.name': 'Name',
  'editFarm.units.col.type': 'Type',
  'editFarm.units.col.status': 'Status',
  'editFarm.units.col.crewOrder': 'Crew Mode order',
  'editFarm.units.status.active': 'Active',
  'editFarm.units.panelTitle': 'Unit: {unit}',
  'editFarm.units.delete': 'Delete unit',
  'editFarm.culture.defaultsTitle': 'Culture defaults',
  'editFarm.culture.defaultsHelp': 'Values new lots in this facility start from: lot projection and egg incubation. Below, which Crew Mode streams are on.',
  'editFarm.advanced.syncTitle': 'Sync and status',
  'editFarm.advanced.dangerTitle': 'Danger zone',
  'editFarm.advanced.dangerHelp': 'Deleting a facility cannot be undone. A facility that holds records cannot be deleted: mark it Inactive instead.',
  'editFarm.search.placeholder': 'Search facilities by name',
  'editFarm.search.clear': 'Clear',
  'editFarm.empty.noFacilities': 'No facilities found',
  'editFarm.fallback.firstFarmShown': 'No facility selection found in the URL or active context. Showing the first facility in the list — pick another from the left if this is not what you intended.',

  'editFarm.item.pusCount': 'PUs: {count}',
  'editFarm.item.created': 'Created: {date}',
  'editFarm.common.na': 'N/A',

  'editFarm.details.title': 'Facility Details',
  'editFarm.details.deleteFacility': 'Delete Facility',
  'editFarm.delete.needsConnection': 'Deleting needs a connection: the server checks first whether it still holds records.',
  'editFarm.delete.checkFailed': 'Could not check whether it holds records. Nothing was deleted — try again.',
  'editFarm.delete.blockedTitle': 'It still holds records',
  'editFarm.delete.blockedFarmBody': 'This facility still holds {count} records (feed, mortality, samplings, water, stock, costs, lots…). It cannot be deleted.',
  'editFarm.delete.blockedUnitBody': 'This unit still holds {count} records (feed, mortality, samplings, water, stock, costs, lots…). It cannot be deleted.',
  'editFarm.delete.blockedWhy': 'Records are kept for at least 5 years. Mark it Inactive instead: its history stays in reports and no new records can be added to it.',
  'editFarm.delete.markInactive': 'Mark as inactive',
  'editFarm.status.inactiveFacility': 'Inactive facility',
  'editFarm.status.inactiveFacilityHelp': 'Keeps all its records and history; it is no longer offered in Crew Mode.',
  'editFarm.status.inactiveUnit': 'Inactive',
  'editFarm.status.inactiveUnitHelp': 'Keeps all its records and history; it is no longer offered in Crew Mode.',
  'editFarm.status.unitHasActiveLot': 'Has an active lot ({lots}): harvest or move it before setting the unit inactive.',
  'editFarm.crew.hidden': 'Hidden in Crew Mode',
  'editFarm.crew.order': 'Crew Mode order: {position}',
  'editFarm.crew.moveUp': 'Move up in Crew Mode',
  'editFarm.crew.moveDown': 'Move down in Crew Mode',
  'editFarm.plans.title': 'Crew Mode plans',
  'editFarm.plans.edit': 'Edit plans',
  'editFarm.plans.feedLabel': 'Feed plan',
  'editFarm.plans.samplingLabel': 'Planned sampling',
  'editFarm.plans.waterLabel': 'Water parameters',
  'editFarm.plans.feedNone': 'none (the crew chooses; the last feed used is suggested)',
  'editFarm.plans.feedSummary': '{kg} kg/day of {feed} in {feedings} feedings ({ration} kg per feeding)',
  'editFarm.plans.feedSummaryPerFeeding': '{feeds} per day in {feedings} feedings',
  'editFarm.plans.feedSummaryMode': '{feeds} per day · {mode}',
  'editFarm.plans.secondFeed': 'Second feed (optional)',
  'editFarm.plans.noSecondFeed': 'None',
  'editFarm.plans.error.secondFeed': 'The second feed must be a different feed with kg per day above 0 (or leave it as None).',
  'editFarm.plans.mode': 'How the crew records feed',
  'editFarm.plans.mode.perFeeding': 'Per feeding',
  'editFarm.plans.mode.dayTotal': 'Day total',
  'editFarm.plans.mode.weighed': 'Daily weighing',
  'editFarm.plans.mode.help': 'Per feeding: the crew records each feeding (as before). Day total: one entry for the day. Daily weighing: in the morning they record what they weighed and at the end of the day what was left; Sunflow works out what was fed.',
  'editFarm.plans.missingFeed': '(feed not available)',
  'editFarm.plans.staleFeed': 'this feed is no longer available, Crew Mode ignores the plan',
  'editFarm.plans.samplingNone': 'none',
  'editFarm.plans.samplingSummary': '{count} fish, weight only',
  'editFarm.plans.samplingSummaryLength': '{count} fish, weight and length',
  'editFarm.plans.waterAll': 'all four',
  'editFarm.plans.feed': 'Feed or mix',
  'editFarm.plans.noPlan': 'No plan',
  'editFarm.plans.kgPerDay': 'kg per day',
  'editFarm.plans.feedingsPerDay': 'Feedings per day',
  'editFarm.plans.fishCount': 'Number of fish',
  'editFarm.plans.measureLength': 'Measure length',
  'editFarm.plans.samplingLot': 'Lot',
  'editFarm.plans.anyLot': 'Current lot',
  'editFarm.plans.waterHelp': 'None ticked = the crew sees temperature, pH, oxygen and salinity; flow only when ticked.',
  'editFarm.plans.error.feed': 'Choose the feed for the plan, or clear kg per day and feedings.',
  'editFarm.plans.error.kgPerDay': 'Enter kg per day greater than 0.',
  'editFarm.plans.error.feedings': 'Feedings per day must be a whole number from 1 to 24.',
  'editFarm.plans.error.fishCount': 'Number of fish must be a whole number from 1 to 1000.',
  'editFarm.details.facilityName': 'Facility name',
  'editFarm.details.facilityType.label': 'Facility type',
  'editFarm.details.facilityType.meta': 'Controls which setup surfaces are shown here',
  'editFarm.details.facilityType.farm': 'Grow-out facility',
  'editFarm.details.facilityType.processingPlant': 'Processing plant',
  'editFarm.details.facilityType.helpFarm': 'Grow-out facilities use production units and species defaults for grow-out operations.',
  'editFarm.details.facilityType.helpProcessingPlant': 'Processing plants skip grow-out-facility-only species and production-unit setup. Use processing sites below to define receiving, staging, or dispatch areas.',

  'editFarm.details.altitude.label': 'Altitude',
  'editFarm.details.altitude.unit': 'meters above sea level',
  'editFarm.details.altitude.help': 'Used for dissolved oxygen calculations in water quality monitoring.',

  'editFarm.details.timeZone.label': 'Time zone',
  'editFarm.details.timeZone.meta': 'IANA (e.g. America/Chicago)',
  'editFarm.details.timeZone.invalid': 'Invalid time zone. Use an IANA name (e.g. America/Chicago).',
  'editFarm.details.timeZone.help': 'Used for day/week bucketing; if missing/invalid, the app falls back to browser-local.',

  // Server-Side Flag Enforcement v0 follow-up — admin-surface Crew Mode toggle (Charter O-4)
  'editFarm.crewMode.title': 'Crew Mode',
  'editFarm.crewMode.subtitle': 'Turn on the field-capture streams operators can use for this farm.',
  'editFarm.crewMode.loading': 'Loading Crew Mode settings…',
  'editFarm.crewMode.loadError': 'Could not load Crew Mode settings. Try again.',
  'editFarm.crewMode.saveError': 'Could not save that change. Try again.',

  'editFarm.details.processingSites.label': 'Processing sites',
  'editFarm.details.processingSites.meta': 'One entry per line: site-id or site-id | Display name',
  'editFarm.details.processingSites.placeholder': 'processing-site-a | Main Processor\nprocessing-site-b',
  'editFarm.details.processingSites.invalid': 'Fix the {count} invalid processing-site line(s) before leaving this field.',
  'editFarm.details.processingSites.help': 'Registered processing sites power transfer destinations and lot-story readiness checks for this facility.',

  'editFarm.details.speciesPreset.label': 'Species preset',
  'editFarm.details.speciesPreset.meta': 'Defaults labels + KPIs',
  'editFarm.details.speciesPreset.helpFallback': 'Choose a preset that matches this facility.',

  'editFarm.productionUnits.title': 'Production Units ({count})',
  'editFarm.productionUnits.addPu': 'Add PU',
  'editFarm.productionUnits.duplicateName': 'This facility already has a unit called “{name}”. Use another name.',
  'editFarm.productionUnits.empty': 'No production units found',
  'editFarm.productionUnits.puName': 'PU name',
  'editFarm.productionUnits.volume': 'Volume',
  'editFarm.productionUnits.capacityPlaceholder': 'Capacity',
  'editFarm.productionUnits.notSet': 'Not set',
  'editFarm.productionUnits.editVolume': 'Edit volume',
  'editFarm.productionUnits.typePrefix': 'Type: {type}',

  'editFarm.empty.selectFacilityTitle': 'Select a facility to edit',
  'editFarm.empty.selectFacilityBody': 'Choose a facility from the left list.',

  'editFarm.modal.confirmDeletionTitle': 'Confirm Deletion',
  'editFarm.modal.confirmDeleteFarmBody':
    'Are you sure you want to delete this facility? This will also delete all its production units and associated data (database records, water data, etc.).',
  'editFarm.modal.confirmDeletePuBody':
    'Are you sure you want to delete this production unit? This will also delete all associated data (database records, water data, etc.).',
  'editFarm.modal.cannotBeUndone': 'This action cannot be undone.',

  // SFR management
  'sfrManagement.title': 'SFR Matrix',
  'sfrManagement.subtitle': 'Manage the Specific Feeding Rate matrix for aquaculture calculations.',

  // Mortality management
  'mortalityManagement.title': 'Mortality Matrix',
  'mortalityManagement.subtitle': 'Manage the expected mortality matrix used for mortality-vs-condition planning.',
  'mortalityManagement.note.placeholderDefaults': 'Configure expected mortality rates for each temperature and weight range before using them for projections. Trout ships with a starting curve; other species start at 0 until configured.',
  'mortalityManagement.matrixTitle': 'Expected Mortality Matrix',
  'mortalityManagement.rowLabel': 'Weight',
  'mortalityManagement.columnLabel': 'Temperature',
  'mortalityManagement.valueLabel': 'Expected mortality (%)',
  'mortalityManagement.action.refreshMatrix': 'Refresh matrix',
  'mortalityManagement.action.retry': 'Retry',
  'mortalityManagement.action.resetDefaults': 'Reset to defaults',
  'mortalityManagement.action.resetting': 'Resetting...',
  'mortalityManagement.empty.title': 'This matrix has no values yet',
  'mortalityManagement.empty.body': 'Every cell is still 0. Those zeros are placeholders, not measured mortality — enter the expected rate for each weight and temperature before using this matrix for projections.',
  'mortalityManagement.empty.bodyServer': 'The server has no saved matrix for this species yet. The zeros below are placeholders, not measured mortality — enter the expected rate for each weight and temperature to configure it.',
  'mortalityManagement.confirm.resetDefaults': 'Reset this mortality matrix back to its default values? This cannot be undone.',
  'mortalityManagement.confirm.resetCta': 'Confirm reset',
  'mortalityManagement.status.loading': 'Loading mortality matrix...',
  'mortalityManagement.status.saving': 'Saving changes locally...',
  'mortalityManagement.status.refreshing': 'Refreshing mortality matrix from server...',
  'mortalityManagement.status.offline': 'Changes saved locally while offline. They will sync when you reconnect.',
  'mortalityManagement.status.pending': 'Changes saved locally. Server sync is pending.',
  'mortalityManagement.status.synced': 'Changes synced.',
  'mortalityManagement.status.refreshFailed': 'Showing the local mortality matrix because the server refresh failed.',
  'mortalityManagement.status.refreshFailedBlank': 'Could not load the saved mortality matrix from the server. Nothing is cached locally, so there is nothing to show yet — retry when you are back online.',
  'mortalityManagement.status.saveFailed': 'Could not save the mortality matrix locally. Please try again.',

  'mortalityManagement.action.exportCsv': 'Export CSV',

  'mortalityManagement.profile.label': 'Facility profile',
  'mortalityManagement.profile.option.speciesDefault': 'Species default matrix',
  'mortalityManagement.profile.help.noFacility': 'Select a facility to create reusable mortality profiles and apply them to production units or lots.',
  'mortalityManagement.profile.help.facility': 'Profiles are stored on the selected facility. Choose one here to edit it, or stay on the species default matrix.',
  'mortalityManagement.profile.summary.editingProfile': 'Editing facility profile: {name}.',
  'mortalityManagement.profile.summary.editingSpeciesDefault': 'Editing the shared species-default matrix for {species}.',
  'mortalityManagement.profile.summary.appliedLot': 'Current context resolves from lot {lot}: {profile}.',
  'mortalityManagement.profile.summary.appliedProductionUnit': 'Current context resolves from production unit {productionUnit}: {profile}.',
  'mortalityManagement.profile.summary.appliedFacilityDefault': 'Current context resolves from facility default at {facility}: {profile}.',
  'mortalityManagement.profile.summary.appliedSpeciesDefault': 'Current context is still using the species-default matrix for {species}.',
  'mortalityManagement.profile.summary.facilityDefaultHint': 'Facility default profile: {profile}.',
  'mortalityManagement.profile.summary.productionUnitHint': 'Production unit override for {productionUnit}: {profile}.',
  'mortalityManagement.profile.summary.lotHint': 'Lot override for {lot}: {profile}.',
  'mortalityManagement.profile.action.saveAsFacilityProfile': 'Save as facility profile',
  'mortalityManagement.profile.action.setFacilityDefault': 'Set as facility default',
  'mortalityManagement.profile.action.clearFacilityDefault': 'Clear facility default',
  'mortalityManagement.profile.action.applyToProductionUnit': 'Apply to production unit',
  'mortalityManagement.profile.action.clearProductionUnit': 'Clear production unit override',
  'mortalityManagement.profile.action.applyToLot': 'Apply to lot',
  'mortalityManagement.profile.action.clearLot': 'Clear lot override',
  'mortalityManagement.profile.prompt.name': 'Name this facility mortality profile',
  'mortalityManagement.profile.alert.selectFacility': 'Select a facility before managing mortality profiles.',
  'mortalityManagement.profile.alert.selectProductionUnit': 'Select a production unit before applying a profile override.',
  'mortalityManagement.profile.alert.selectLot': 'Select a lot before applying a lot-specific profile override.',
  'mortalityManagement.profile.alert.activeLotRequired': 'Select an active lot before applying a lot-specific profile override.',
  'mortalityManagement.profile.confirm.resetProfile': 'Reset facility profile {profile} to the {species} species default matrix? This will overwrite the profile values.',

  'mortalityManagement.alert.invalidWeightTemp': 'Please enter valid numbers for weight and temperature',
  'mortalityManagement.alert.invalidNewWeight': 'Enter a valid weight in grams (positive number)',
  'mortalityManagement.alert.weightExists': 'That weight already exists in the matrix',
  'mortalityManagement.alert.minWeightRows': 'At least one weight row is required',
  'mortalityManagement.alert.invalidNewTemperature': 'Enter a valid temperature in °C',
  'mortalityManagement.alert.temperatureExists': 'That temperature already exists in the matrix',
  'mortalityManagement.alert.minTemperatureColumns': 'At least one temperature column is required',

  'mortalityManagement.calculator.title': 'Mortality Calculator',
  'mortalityManagement.calculator.weightLabel': 'Weight:',
  'mortalityManagement.calculator.weightPlaceholder': 'grams',
  'mortalityManagement.calculator.temperatureLabel': 'Temperature:',
  'mortalityManagement.calculator.temperaturePlaceholder': 'celsius',
  'mortalityManagement.calculator.calculate': 'Calculate',
  'mortalityManagement.calculator.result': 'Expected mortality: {value}%',
  'mortalityManagement.calculator.help': 'This calculator uses interpolation to find expected mortality values between matrix points.',

  'mortalityManagement.tools.title': 'Matrix Structure Tools',
  'mortalityManagement.tools.weightRows': 'Weight rows',
  'mortalityManagement.tools.addWeightPlaceholder': 'Add new weight (g)',
  'mortalityManagement.tools.addRow': 'Add row',
  'mortalityManagement.tools.temperatureColumns': 'Temperature columns',
  'mortalityManagement.tools.addTemperaturePlaceholder': 'Add new temp (°C)',
  'mortalityManagement.tools.addColumn': 'Add column',
  'mortalityManagement.tools.remove': 'Remove',

  'mortalityManagement.usage.title': 'Usage',
  'mortalityManagement.usage.item1': 'Click any cell in the matrix to edit expected mortality values.',
  'mortalityManagement.usage.item2': 'Facility profiles can be applied to a whole facility, a production unit, or a single lot.',
  'mortalityManagement.usage.item3': 'Expected mortality values are automatically interpolated for precise calculations.',
  'mortalityManagement.usage.item4': 'Export to CSV for backup or external analysis.',

  'sfrManagement.speciesPreset.label': 'Species preset',
  'sfrManagement.species.descriptionFallback': 'Preset description coming soon.',

  'sfrManagement.species.trout.label': 'Trout',
  'sfrManagement.species.trout.description': 'Uses the Rainbow Trout SFR preset.',
  'sfrManagement.species.salmon.label': 'Salmon',
  'sfrManagement.species.salmon.description': 'Uses the Atlantic Salmon SFR preset.',
  'sfrManagement.species.tilapia.label': 'Tilapia',
  'sfrManagement.species.tilapia.description': 'Warmwater preset optimised for 16-22°C range.',
  'sfrManagement.species.shrimp.label': 'Pacific White Shrimp (PWS)',
  'sfrManagement.species.shrimp.description': 'Warmwater shrimp preset (default: 27°C and 32°C columns).',

  'sfrManagement.action.refreshMatrix': 'Refresh matrix',
  'sfrManagement.action.exportCsv': 'Export CSV',
  'sfrManagement.action.resetDefaults': 'Reset to defaults',

  'sfrManagement.profile.label': 'Facility profile',
  'sfrManagement.profile.option.speciesDefault': 'Species default matrix',
  'sfrManagement.profile.help.noFacility': 'Select a facility to create reusable SFR profiles and apply them to production units or lots.',
  'sfrManagement.profile.help.facility': 'Profiles are stored on the selected facility. Choose one here to edit it, or stay on the species default matrix.',
  'sfrManagement.profile.summary.editingProfile': 'Editing facility profile: {name}.',
  'sfrManagement.profile.summary.editingSpeciesDefault': 'Editing the shared species-default matrix for {species}.',
  'sfrManagement.profile.summary.appliedLot': 'Current context resolves from lot {lot}: {profile}.',
  'sfrManagement.profile.summary.appliedProductionUnit': 'Current context resolves from production unit {productionUnit}: {profile}.',
  'sfrManagement.profile.summary.appliedFacilityDefault': 'Current context resolves from facility default at {facility}: {profile}.',
  'sfrManagement.profile.summary.appliedSpeciesDefault': 'Current context is still using the species-default matrix for {species}.',
  'sfrManagement.profile.summary.facilityDefaultHint': 'Facility default profile: {profile}.',
  'sfrManagement.profile.summary.productionUnitHint': 'Production unit override for {productionUnit}: {profile}.',
  'sfrManagement.profile.summary.lotHint': 'Lot override for {lot}: {profile}.',
  'sfrManagement.profile.action.saveAsFacilityProfile': 'Save as facility profile',
  'sfrManagement.profile.action.setFacilityDefault': 'Set as facility default',
  'sfrManagement.profile.action.clearFacilityDefault': 'Clear facility default',
  'sfrManagement.profile.action.applyToProductionUnit': 'Apply to production unit',
  'sfrManagement.profile.action.clearProductionUnit': 'Clear production unit override',
  'sfrManagement.profile.action.applyToLot': 'Apply to lot',
  'sfrManagement.profile.action.clearLot': 'Clear lot override',
  'sfrManagement.profile.prompt.name': 'Name this facility SFR profile',
  'sfrManagement.profile.alert.selectFacility': 'Select a facility before managing SFR profiles.',
  'sfrManagement.profile.alert.selectProductionUnit': 'Select a production unit before applying a profile override.',
  'sfrManagement.profile.alert.selectLot': 'Select a lot before applying a lot-specific profile override.',
  'sfrManagement.profile.alert.activeLotRequired': 'Select an active lot before applying a lot-specific profile override.',
  'sfrManagement.profile.confirm.resetProfile': 'Reset facility profile {profile} to the {species} species default matrix? This will overwrite the profile values.',

  'sfrManagement.status.loading': 'Loading SFR matrix...',
  'sfrManagement.status.saving': 'Saving changes locally...',
  'sfrManagement.status.refreshing': 'Refreshing SFR matrix from server...',
  'sfrManagement.status.offline': 'Changes saved locally while offline. They will sync when you reconnect.',
  'sfrManagement.status.pending': 'Changes saved locally. Server sync is pending.',
  'sfrManagement.status.synced': 'Changes synced.',
  'sfrManagement.status.refreshFailed': 'Showing the local SFR matrix because the server refresh failed.',
  'sfrManagement.status.saveFailed': 'Could not save the SFR matrix locally. Please try again.',

  'sfrManagement.alert.invalidWeightTemp': 'Please enter valid numbers for weight and temperature',
  'sfrManagement.confirm.sharedPresetNote': ' (shared by Trout and Salmon)',
  'sfrManagement.confirm.resetDefaults': 'Reset this SFR preset{sharedPresetNote} back to its default matrix? This cannot be undone.',
  'sfrManagement.confirm.resetCta': 'Confirm reset',
  'sfrManagement.alert.resetFailed': 'Unable to reset the SFR matrix. Please try again or contact support.',

  'sfrManagement.alert.invalidNewWeight': 'Enter a valid weight in grams (positive number)',
  'sfrManagement.alert.weightExists': 'That weight already exists in the matrix',
  'sfrManagement.alert.minWeightRows': 'At least one weight row is required',
  'sfrManagement.alert.invalidNewTemperature': 'Enter a valid temperature in °C',
  'sfrManagement.alert.temperatureExists': 'That temperature already exists in the matrix',
  'sfrManagement.alert.minTemperatureColumns': 'At least one temperature column is required',

  'sfrManagement.calculator.title': 'SFR Calculator',
  'sfrManagement.calculator.weightLabel': 'Weight:',
  'sfrManagement.calculator.weightPlaceholder': 'grams',
  'sfrManagement.calculator.temperatureLabel': 'Temperature:',
  'sfrManagement.calculator.temperaturePlaceholder': 'celsius',
  'sfrManagement.calculator.calculate': 'Calculate',
  'sfrManagement.calculator.result': 'SFR: {value}%',
  'sfrManagement.calculator.diagnosticsTitle': 'Calculation details',
  'sfrManagement.calculator.diagnosticsCurrent': 'Current calculator path: selected species temperature profile, effective interpolation temperature {temperature}°C.',
  'sfrManagement.calculator.diagnosticsReference': 'Selected preset reference ({profile}): effective interpolation temperature {temperature}°C, interpolated SFR {value}%.',
  'sfrManagement.calculator.help': 'This calculator uses interpolation to find SFR values between matrix points.',

  'sfrManagement.tools.title': 'Matrix Structure Tools',
  'sfrManagement.tools.weightRows': 'Weight rows',
  'sfrManagement.tools.addWeightPlaceholder': 'Add new weight (g)',
  'sfrManagement.tools.addRow': 'Add row',
  'sfrManagement.tools.temperatureColumns': 'Temperature columns',
  'sfrManagement.tools.addTemperaturePlaceholder': 'Add new temp (°C)',
  'sfrManagement.tools.addColumn': 'Add column',
  'sfrManagement.tools.remove': 'Remove',

  'sfrManagement.usage.title': 'Usage',
  'sfrManagement.usage.item1': 'Click any cell in the matrix to edit SFR values.',
  'sfrManagement.usage.item2': 'Changes require confirmation before being saved.',
  'sfrManagement.usage.item3': 'Weight data from Database + Temperature data from Water → SFR lookup.',
  'sfrManagement.usage.item4': 'SFR values are automatically interpolated for precise calculations.',
  'sfrManagement.usage.item5': 'Export to CSV for backup or external analysis.',

  // Owner console
  'ownerConsole.title': 'Owner Console',
  'ownerConsole.subtitle': 'Create and revoke API tokens for field-test users. Tokens are tenant-scoped.',

  'ownerConsole.nav.members': 'Team members',
  'ownerConsole.nav.tokens': 'Tokens',
  'ownerConsole.nav.organizations': 'Organizations',
  'ownerConsole.nav.groups': 'Groups',

  'ownerConsole.me.tenant': 'Tenant:',
  'ownerConsole.me.role': 'Role:',
  'ownerConsole.me.accountName': 'Account name:',
  'ownerConsole.me.username': 'Username:',
  'ownerConsole.me.email': 'Email:',
  'ownerConsole.me.membership': 'Membership ID:',
  'ownerConsole.me.user': 'User ID:',
  'ownerConsole.me.actor': 'Actor:',
  'ownerConsole.me.currentToken': 'Current token ID:',
  'ownerConsole.me.compatibilityMember': 'Compatibility member ID:',

  'ownerConsole.notice.notAdmin': 'This account is not an admin, so member and token management are disabled.',

  'ownerConsole.error.failedToLoad': 'Failed to load',
  'ownerConsole.error.enterLabel': 'Enter a label (e.g. “Team A - Alice”).',
  'ownerConsole.error.createFailed': 'Create failed',
  'ownerConsole.error.revokeFailed': 'Revoke failed',
  'ownerConsole.error.memberUpdateFailed': 'Member update failed',
  'ownerConsole.error.memberRevokeFailed': 'Member revoke failed',
  'ownerConsole.error.enterGroupName': 'Enter a group name.',
  'ownerConsole.error.groupCreateFailed': 'Group create failed',
  'ownerConsole.error.enterGroupMember': 'Select a team member to add to the group.',
  'ownerConsole.error.groupMembershipCreateFailed': 'Group membership update failed',
  'ownerConsole.error.enterPartnerOrganizationName': 'Enter a partner organization name.',
  'ownerConsole.error.partnerOrganizationCreateFailed': 'Partner organization create failed',

  'ownerConsole.members.title': 'Team members',
  'ownerConsole.members.addMember': 'Add team member',
  'ownerConsole.members.subtitle': 'Filter active and revoked memberships, then change role or revoke linked tokens where allowed.',
  'ownerConsole.members.loading': 'Loading members…',
  'ownerConsole.members.empty': 'No active members yet.',
  'ownerConsole.members.emptyFiltered': 'No members match this filter.',
  'ownerConsole.members.note': 'Admin memberships stay read-only here. Use the token table below for individual token revoke.',
  'ownerConsole.members.migrationNote': 'Activation required means the member still needs first-time human credentials. Create an activation link for that state; use password reset only after credentials already exist.',
  'ownerConsole.members.revokedAtPrefix': 'Revoked',
  'ownerConsole.members.neverUsed': 'Never used',
  'ownerConsole.members.activeTokens': 'Active tokens',
  'ownerConsole.members.revokedTokens': 'Revoked tokens',
  'ownerConsole.members.activationRequired': 'Activation required',
  'ownerConsole.members.credentialsReady': 'Credentials ready',
  'ownerConsole.members.status.pinReady': 'Active · PIN set',
  'ownerConsole.members.status.noPin': 'Active · No PIN',
  'ownerConsole.members.status.noPinHint': 'Set a PIN in this row so they can record in Crew Mode.',
  'ownerConsole.members.status.canSignIn': 'Active · Can sign in',
  'ownerConsole.members.status.neverSignedIn': 'Active · Never signed in',
  'ownerConsole.members.status.needsActivation': 'Active · Needs activation',
  'ownerConsole.members.status.needsActivationHint': 'Send an activation link so they can choose a password.',
  'ownerConsole.members.searchLabel': 'Search members',
  'ownerConsole.members.searchPlaceholder': 'Search by member, actor, user, membership ID, or status',
  'ownerConsole.members.filter.all': 'All',
  'ownerConsole.members.filter.active': 'Active',
  'ownerConsole.members.filter.revoked': 'Revoked',
  'ownerConsole.passwordReset.title': 'Password reset link (showing once)',
  'ownerConsole.passwordReset.activationTitle': 'Account activation link (showing once)',
  'ownerConsole.passwordReset.expiresAt': 'Expires',
  'ownerConsole.passwordReset.copy': 'Copy link',
  'ownerConsole.passwordReset.dismiss': 'Dismiss',

  'ownerConsole.partnerOrganizations.title': 'Partner organizations',
  'ownerConsole.partnerOrganizations.subtitle': 'Create explicit organization identities that group memberships can represent later during trusted partner sharing.',
  'ownerConsole.partnerOrganizations.loading': 'Loading partner organizations…',
  'ownerConsole.partnerOrganizations.empty': 'No partner organizations yet.',
  'ownerConsole.partnerOrganizations.nameLabel': 'Organization name',
  'ownerConsole.partnerOrganizations.namePlaceholder': 'Harvest buyer north',
  'ownerConsole.partnerOrganizations.create': 'Create organization',
  'ownerConsole.partnerOrganizations.note': 'These identities stay tenant-owned for now and do not change artifact visibility.',
  'ownerConsole.partnerOrganizations.status.active': 'Active',
  'ownerConsole.partnerOrganizations.status.inactive': 'Inactive',

  'ownerConsole.groups.title': 'Partner groups',
  'ownerConsole.groups.subtitle': 'Create owner-tenant groups and attach existing team memberships. This prepares group identity for future GROUP sharing without sharing artifacts yet.',
  'ownerConsole.groups.loading': 'Loading groups…',
  'ownerConsole.groups.empty': 'No groups yet.',
  'ownerConsole.groups.nameLabel': 'Group name',
  'ownerConsole.groups.namePlaceholder': 'Partner circle alpha',
  'ownerConsole.groups.create': 'Create group',
  'ownerConsole.groups.note': 'This surface manages group identity groundwork only. Artifact visibility stays unchanged.',
  'ownerConsole.groups.createdBy': 'Created by',
  'ownerConsole.groups.activeMembers': 'Active',
  'ownerConsole.groups.invitedMembers': 'Invited',
  'ownerConsole.groups.revokedMembers': 'Revoked',
  'ownerConsole.groups.noMemberships': 'No memberships yet.',
  'ownerConsole.groups.addMemberLabel': 'Add team member',
  'ownerConsole.groups.addMemberPartnerOrganizationLabel': 'Partner organization',
  'ownerConsole.groups.addMemberRoleLabel': 'Group role',
  'ownerConsole.groups.addMember': 'Add to group',
  'ownerConsole.groups.noAvailableMembers': 'All active team members are already attached.',
  'ownerConsole.groups.selectMemberPlaceholder': 'Select team member',
  'ownerConsole.groups.selectPartnerOrganizationPlaceholder': 'No partner organization',
  'ownerConsole.groups.partnerOrganizationAssigned': 'Represents',
  'ownerConsole.groups.status.active': 'Active',
  'ownerConsole.groups.status.archived': 'Archived',
  'ownerConsole.groups.role.owner': 'owner',
  'ownerConsole.groups.role.editor': 'editor',
  'ownerConsole.groups.role.viewer': 'viewer',
  'ownerConsole.groups.membershipStatus.invited': 'Invited',
  'ownerConsole.groups.membershipStatus.active': 'Active',
  'ownerConsole.groups.membershipStatus.revoked': 'Revoked',

  'ownerConsole.create.title': 'Create user token',
  'ownerConsole.create.openOnboarding': 'Open member onboarding',
  'ownerConsole.create.label': 'Label',
  'ownerConsole.create.labelPlaceholder': 'Team A - Alice',
  'ownerConsole.create.role': 'Role',
  'ownerConsole.create.roleOption.viewer': 'viewer (read-only)',
  'ownerConsole.create.roleOption.contributor': 'contributor (can create tasks only)',
  'ownerConsole.create.roleOption.editor': 'editor (can write)',
  'ownerConsole.create.createToken': 'Create token',
  'ownerConsole.create.refresh': 'Refresh',

  'ownerConsole.createdToken.title': 'Token (showing once)',
  'ownerConsole.createdToken.copy': 'Copy',
  'ownerConsole.createdToken.dismiss': 'Dismiss',

  'ownerConsole.existing.title': 'Existing tokens',
  'ownerConsole.existing.loading': 'Loading…',
  'ownerConsole.existing.empty': 'No tokens yet.',

  'ownerConsole.table.member': 'Member',
  'ownerConsole.table.membership': 'Membership ID',
  'ownerConsole.table.user': 'User ID',
  'ownerConsole.table.role': 'Role',
  'ownerConsole.table.actor': 'Actor',
  'ownerConsole.table.compatibilityMember': 'Compatibility member ID',
  'ownerConsole.table.lastUsed': 'Last used',
  'ownerConsole.table.status': 'Status',
  'ownerConsole.table.actions': 'Actions',

  'ownerConsole.status.revoked': 'Revoked',
  'ownerConsole.status.active': 'Active',
  'ownerConsole.action.revoke': 'Revoke',
  'ownerConsole.action.saveRole': 'Save role',
  'ownerConsole.members.unsavedChangesChip': 'Unsaved changes',
  'ownerConsole.members.unsavedChangesTitle': 'You changed this role but have not saved yet. Click “Save role” to persist, or navigate away to discard.',
  'ownerConsole.action.revokeMember': 'Revoke member',
  'ownerConsole.action.issueActivation': 'Create activation link',
  'ownerConsole.action.issuePasswordReset': 'Create password link',
  'ownerConsole.action.issuingPasswordReset': 'Creating link…',
  'ownerConsole.action.setPin': 'Set PIN',
  'ownerConsole.action.settingPin': 'Saving…',
  'ownerConsole.action.clearPin': 'Clear PIN',
  'ownerConsole.action.clearingPin': 'Clearing…',
  'ownerConsole.pin.placeholder': '4-digit PIN',
  'ownerConsole.pin.invalid': 'PIN must be exactly 4 digits.',
  'ownerConsole.pin.setSuccess': 'PIN saved for {label}.',
  'ownerConsole.pin.clearSuccess': 'PIN cleared for {label}.',
  'ownerConsole.error.pinSetFailed': 'Could not save the PIN.',
  'ownerConsole.error.pinClearFailed': 'Could not clear the PIN.',

  // Crew Mode Charter D-4 shared identity mechanism — OperatorGate
  'crewMode.operatorGate.title': 'Who is capturing?',
  'crewMode.operatorGate.switchOperator': 'Switch',
  'crewMode.operatorGate.error.invalidPin': 'PIN not recognized. Try again.',
  'crewMode.operatorGate.lockedOut': 'Too many attempts. Try again in a minute.',

  // Fast Entry v0 — minimal shell wrapping every /crew* route
  'crewMode.shell.confirmSignOut': 'Sign out of this device?',
  'crewMode.shell.backToMenu': 'Back to menu',
  'crewMode.shell.permissionBlocked': 'This phone is not allowed to save records. Tell your administrator. The records stay saved on the phone and will be sent automatically once it is allowed.',
  'syncRefused.title.withWaiting': 'One record could not be sent and is holding {count} more change(s) on this device.',
  'syncRefused.title.alone': 'One record could not be sent.',
  'syncRefused.reason.unitMismatch': 'On that date lot {lot} was not in {unit}. Check the date of the record.',
  'syncRefused.reason.lotNotActive': 'Lot {lot} is no longer active.',
  'syncRefused.reason.lotNotFound': 'The lot of this record no longer exists.',
  'syncRefused.reason.other': 'The server refused it: {message}',
  'syncRefused.action.office': 'Delete or fix it in {screen}; the other changes will then be sent.',
  'syncRefused.action.crew': 'Tell the office. The records stay saved on the phone.',
  'syncRefused.open': 'Open',
  'syncRefused.kind.farm': 'Facility',
  'syncRefused.kind.productionUnit': 'Production unit',
  'syncRefused.kind.lot': 'Lot',
  'syncRefused.kind.lotMove': 'Lot move',
  'syncRefused.kind.lotSplit': 'Lot split',
  'syncRefused.kind.lotMerge': 'Lot merge',
  'syncRefused.kind.waterLog': 'Water quality',
  'syncRefused.kind.productionRecord': 'Production record',
  'syncRefused.kind.feedLog': 'Feeding',
  'syncRefused.kind.mortalityLog': 'Mortality',
  'syncRefused.kind.weightSampleLog': 'Sampling',
  'syncRefused.kind.fishStockLog': 'Fish count',
  'syncRefused.kind.harvestLog': 'Harvest',
  'syncRefused.kind.lotCostEvent': 'Cost',
  'syncRefused.kind.inventoryItem': 'Inventory item',
  'syncRefused.kind.task': 'Task',
  'syncRefused.kind.matrix': 'Reference table',
  'syncRefused.kind.other': 'Record',
  'syncRefused.screen.editFarm': 'Edit facility',
  'syncRefused.screen.lots': 'Lots',
  'syncRefused.screen.water': 'Water',
  'syncRefused.screen.database': 'Database',
  'syncRefused.screen.feed': 'Feed',
  'syncRefused.screen.biomass': 'Biomass',
  'syncRefused.screen.costs': 'Costs',
  'syncRefused.screen.inventory': 'Inventory',
  'syncRefused.screen.tasks': 'Tasks',
  'syncRefused.screen.matrix': 'the reference table',
  'syncRefused.screen.home': 'Home',
  'syncRefused.rowTag': 'Not sent',

  // WaterLog v0 Stage 4 capture screen
  'crewMode.water.productionUnit': 'Pond',
  'crewMode.water.selectParameter': 'What are you measuring?',
  'crewMode.water.parameter.temperature': 'Temperature',
  'crewMode.water.parameter.ph': 'pH',
  'crewMode.water.parameter.phUnit': 'units',
  'crewMode.water.parameter.dissolvedOxygen': 'Dissolved oxygen',
  'crewMode.water.parameter.salinity': 'Salinity',
  'crewMode.water.error.invalidValue': 'Enter a number.',
  'crewMode.water.error.outOfRange': 'Value must be between {min} and {max} {unit}.',
  'crewMode.water.warning.duplicate': 'A reading for this pond and parameter was saved recently. Tap Confirm again to save anyway.',
  'crewMode.water.confirm': 'Confirm',
  'crewMode.water.confirmAnyway': 'Confirm anyway',
  'crewMode.water.sync.local': 'Saved on device',
  'crewMode.water.sync.pending': 'Syncing…',
  'crewMode.water.sync.synced': 'Synced',
  'crewMode.water.sync.error': 'Sync failed',
  'crewMode.water.retry': 'Retry',
  'crewMode.water.notEnabledForFarm': 'Crew Mode water capture is not enabled for this farm yet. Ask your administrator.',

  'crewMode.feed.pond': 'Pond',
  'crewMode.feed.lot.choose': 'Which lot?',
  'crewMode.feed.lot.none': 'No active lot in this pond. Check with your supervisor.',
  'crewMode.feed.lot.current': 'Lot: {lotKey}',
  'crewMode.feed.selectItem': 'What did you feed?',
  'crewMode.feed.noItems': 'No feed items configured for this farm yet. Ask your administrator.',
  'crewMode.feed.quantity': 'Quantity',
  'crewMode.feed.quantityUnit': 'kg',
  'crewMode.feed.error.invalidValue': 'Enter a quantity greater than 0.',
  'crewMode.feed.warning.largeValue': "That's a large amount. Tap Confirm again to save anyway.",
  'crewMode.feed.warning.duplicate': 'A feeding for this lot was saved recently. Tap Confirm again to save anyway.',
  'crewMode.feed.confirm': 'Confirm',
  'crewMode.feed.confirmAnyway': 'Confirm anyway',
  'crewMode.feed.sync.local': 'Saved on device',
  'crewMode.feed.sync.pending': 'Syncing…',
  'crewMode.feed.sync.synced': 'Synced',
  'crewMode.feed.sync.error': 'Sync failed',
  'crewMode.feed.retry': 'Retry',
  'crewMode.feed.notEnabledForFarm': 'Crew Mode feed capture is not enabled for this farm yet. Ask your administrator.',
  'crewMode.feed.giveDayTotal': 'Today: {feeds}',
  'crewMode.feed.giveDayTotalDetail': 'Day total · tap to record it',
  'crewMode.feed.weighed.title': 'Daily weighing',
  'crewMode.feed.weighed.morningHelp': 'Record what you weighed for today. At the end of the day you record what was left.',
  'crewMode.feed.weighed.planHint': 'Plan: {kg} kg',
  'crewMode.feed.weighed.weighedLabel': 'Weighed {feed} (kg)',
  'crewMode.feed.weighed.saveWeighed': 'Save weighed',
  'crewMode.feed.weighed.weighedToday': 'Weighed today: {kg} kg {feed}',
  'crewMode.feed.weighed.leftoverLabel': 'Leftover {feed} (kg)',
  'crewMode.feed.weighed.leftoverHelp': 'At the end of the day weigh what was left. Empty = nothing left.',
  'crewMode.feed.weighed.saveLeftover': 'Save leftover',
  'crewMode.feed.weighed.fedToday': 'Fed today: {fed} kg {feed} (weighed {weighed} − leftover {leftover})',
  'crewMode.feed.weighed.error.weighed': 'Enter what you weighed: a number above 0 for at least one feed.',
  'crewMode.feed.weighed.error.leftover': 'The leftover must be between 0 and what was weighed.',
  'crewMode.feed.givePlan': 'Fed per plan: {kg} kg {feed}',
  'crewMode.feed.mixBadge': 'Mix',

  'crewMode.mortality.pond': 'Pond',
  'crewMode.mortality.lot.choose': 'Which lot?',
  'crewMode.mortality.lot.none': 'No active lot in this pond. Check with your supervisor.',
  'crewMode.mortality.lot.current': 'Lot: {lotKey}',
  'crewMode.mortality.count': 'How many dead?',
  'crewMode.mortality.error.invalidValue': 'Enter a whole number (0 or more).',
  'crewMode.mortality.warning.largeValue': "That's a large number. Tap Confirm again to save anyway.",
  'crewMode.mortality.warning.duplicate': 'A mortality entry for this lot was saved recently. Tap Confirm again to save anyway.',
  'crewMode.mortality.cause.prompt': 'Cause (optional)',
  'crewMode.mortality.cause.predation': 'Predation',
  'crewMode.mortality.cause.disease': 'Disease',
  'crewMode.mortality.cause.handling': 'Handling',
  'crewMode.mortality.cause.waterQuality': 'Water quality',
  'crewMode.mortality.cause.unknown': 'Unknown',
  'crewCorrection.button': 'Correct…',
  'crewCorrection.title': 'Correct a captured entry',
  'crewCorrection.resource.water-log': 'Water reading',
  'crewCorrection.resource.feed-log': 'Feed entry',
  'crewCorrection.resource.mortality-log': 'Mortality entry',
  'crewCorrection.resource.weight-sample-log': 'Weight sample',
  'crewCorrection.field.temperatureC': 'Temperature',
  'crewCorrection.field.ph': 'pH',
  'crewCorrection.field.dissolvedOxygenMgL': 'Dissolved oxygen',
  'crewCorrection.field.dissolvedOxygenSaturationPct': 'Dissolved oxygen (saturation)',
  'crewCorrection.field.manualDissolvedOxygenMgL': 'Manual dissolved oxygen',
  'crewCorrection.field.manualDissolvedOxygenSaturationPct': 'Manual dissolved oxygen (saturation)',
  'crewCorrection.field.salinityPpt': 'Salinity',
  'crewCorrection.field.recordedAt': 'Recorded at',
  'crewCorrection.field.notes': 'Notes',
  'crewCorrection.field.feedKg': 'Feed quantity',
  'crewCorrection.field.feedType': 'Feed type',
  'crewCorrection.field.sfrPercent': 'SFR',
  'crewCorrection.field.biomassKg': 'Biomass',
  'crewCorrection.field.fcr': 'FCR',
  'crewCorrection.field.feedingResponse': 'Feeding response',
  'crewCorrection.field.mortalityCount': 'Dead count',
  'crewCorrection.field.observedCause': 'Observed cause',
  'crewCorrection.field.weightGrams': 'Sampled weight',
  'crewCorrection.field.sampleCount': 'Individuals sampled',
  'crewCorrection.field.countedStock': 'Stock',
  'crewCorrection.field.stockMethod': 'Method',
  'crewCorrection.reason.label': 'Reason for the correction',
  'crewCorrection.reason.labelRequired': 'Reason (required)',
  'crewCorrection.reason.placeholder': 'e.g. operator transposed the reading',
  'crewCorrection.error.noChange': 'Change at least one value before saving.',
  'crewCorrection.error.reasonRequired': 'A reason is required.',
  'crewCorrection.error.generic': 'The correction could not be saved. Your entries are kept — try again.',
  'crewCorrection.error.conflict': 'This entry changed since you opened it. Close this and reopen it to see the current values before correcting.',
  'crewCorrection.review.title': 'Review the correction',
  'crewCorrection.review.field': 'Field',
  'crewCorrection.review.priorValue': 'Prior value',
  'crewCorrection.review.newValue': 'New value',
  'crewCorrection.review.empty': '(empty)',
  'crewCorrection.button.review': 'Review changes',
  'crewCorrection.button.back': 'Back',
  'crewCorrection.button.confirm': 'Confirm and save',
  'crewCorrection.button.cancel': 'Cancel',
  'crewCorrection.button.close': 'Close',
  'crewCorrection.observedCause.none': '— none —',
  'crewCorrection.feedType.none': 'Keep current',
  'crewCorrection.feedType.loading': 'Loading feed catalog…',
  'crewCorrection.field.lot': 'Lot · Unit',
  'crewCorrection.field.lotName': 'Lot',
  'crewCorrection.field.productionUnit': 'Unit',
  'crewCorrection.field.lengthMm': 'Length',
  'crewCorrection.reason.deletePlaceholder': 'e.g. logged twice',
  'crewCorrection.reason.operatorUndo': 'Undone by the operator (Crew Mode)',
  'crewCorrection.mode.edit': 'Correct values',
  'crewCorrection.mode.move': 'Move to another lot/unit',
  'crewCorrection.mode.moveUnit': 'Move to another unit',
  'crewCorrection.mode.delete': 'Delete',
  'crewCorrection.move.current': 'Now in',
  'crewCorrection.move.targetLot': 'Move to (Lot · Unit)',
  'crewCorrection.move.targetUnit': 'Move to unit',
  'crewCorrection.move.pick': '— choose —',
  'crewCorrection.move.currentTag': 'current',
  'crewCorrection.move.guardHint': 'The lot must have been in that unit on the date of the record; otherwise the move is rejected.',
  'crewCorrection.delete.warning': 'The record will be removed. A full copy is kept with the reason in the correction history.',
  'crewCorrection.delete.reviewTitle': 'Review the delete',
  'crewCorrection.delete.confirm': 'Delete record',
  'crewCorrection.delete.steps': 'To delete: 1) type the reason · 2) Review deletion · 3) Delete record.',
  'crewCorrection.delete.review': 'Review deletion',
  'crewCorrection.delete.done': 'Record deleted. The copy stays in the correction history.',
  'crewCorrection.unsynced.note': 'This record has not reached the server yet (it is still waiting to sync on this device), so it can only be deleted here. Values can be corrected once it syncs.',
  'crewCorrection.error.pickMoveTarget': 'Choose where the record should go.',
  'crewCorrection.error.moveRejected': 'The move was rejected: that lot was not in that unit on the date of the record, or the lot/unit belongs to another farm.',
  'crewCorrection.history.empty': 'No corrections yet.',
  'crewCorrection.history.title': 'Correction history ({count})',
  'crewCorrection.history.action.update': 'values corrected',
  'crewCorrection.history.action.move': 'moved',
  'crewCorrection.history.action.delete': 'deleted',
  'crewCorrection.badge.crew': 'Crew',
  'crewCorrection.badge.crewTitle': 'Captured in Crew Mode',
  'crewCorrection.badge.corrected': 'Corrected',
  'crewCorrection.badge.correctedTitle': 'Corrected {count} time(s) — open "Correct…" to see the history',
  'crewCorrection.badge.loggedBy': 'by {name}',
  'crewCorrection.badge.late': 'Entered late',
  'crewCorrection.badge.noMortality': 'No mortality',
  'crewCorrection.badge.notChecked': 'Not checked',
  'crewCorrection.badge.notCheckedTitle': 'The tank could not be checked; this is not a zero-deaths record',
  'crewMode.day.label': 'Day',
  'crewMode.day.today': 'Today',
  'crewMode.day.yesterday': 'Yesterday',
  'crewMode.day.twoDaysAgo': '2 days ago',
  'crewMode.home.today.field.notChecked': 'Not checked',
  'crewMode.lateEntry': 'entered late',
  'crewMode.location.youAreIn': 'You are at',
  'crewMode.location.change': 'Change',
  'crewMode.location.close': 'Close',
  'crewMode.location.pickerTitle': 'Where are you?',
  'crewMode.location.noneLot': 'Choose lot and unit',
  'crewMode.location.noneUnit': 'Choose unit',
  'crewMode.location.emptyLot': 'No unit has an active lot. Ask the office to activate or stock one.',
  'crewMode.location.emptyUnit': 'This facility has no units for Crew Mode.',
  'crewMode.location.next': 'Next: {label} →',
  'crewMode.mortality.noneToday': 'No mortality today',
  'crewMode.mortality.noneThatDay': 'No mortality that day',
  'crewMode.mortality.addCause': '+ Another cause',
  'crewMode.mortality.removeCause': 'Remove',
  'crewMode.mortality.note': 'Note',
  'crewMode.mortality.notePlaceholder': 'Optional note',
  'crewMode.mortality.summary.none': 'No mortality',
  'crewMode.mortality.summary.notChecked': 'Not checked ({reason})',
  'crewMode.mortality.notChecked.open': 'Could not check',
  'crewMode.mortality.notChecked.prompt': 'Why could it not be checked?',
  'crewMode.mortality.notChecked.weather': 'Weather',
  'crewMode.mortality.notChecked.equipment': 'Equipment',
  'crewMode.mortality.notChecked.noStaff': 'No staff',
  'crewMode.mortality.notChecked.other': 'Other',
  'crewMode.undo': 'Undo',
  'crewMode.weightSample.round.remove': 'Remove',
  'crewMode.weightSample.round.replace': 'Fix',
  'crewMode.weightSample.round.fishNumber': 'Fish {n}',
  'crewMode.weightSample.round.removedReason': 'Removed by the operator during the round (Crew Mode)',
  'crewMode.mortality.confirm': 'Confirm',
  'crewMode.mortality.confirmAnyway': 'Confirm anyway',
  'crewMode.mortality.lastEntry': '{count} dead',
  'crewMode.mortality.sync.local': 'Saved on device',
  'crewMode.mortality.sync.pending': 'Syncing…',
  'crewMode.mortality.sync.synced': 'Synced',
  'crewMode.mortality.sync.error': 'Sync failed',
  'crewMode.mortality.retry': 'Retry',
  'crewMode.mortality.notEnabledForFarm': 'Crew Mode mortality capture is not enabled for this farm yet. Ask your administrator.',

  'crewMode.weightSample.pond': 'Pond',
  'crewMode.weightSample.lot.choose': 'Which lot?',
  'crewMode.weightSample.lot.none': 'No active lot in this pond. Check with your supervisor.',
  'crewMode.weightSample.lot.current': 'Lot: {lotKey}',
  'crewMode.weightSample.weight': 'Sampled weight',
  'crewMode.weightSample.weightUnit': 'g',
  'crewMode.weightSample.sampleCount': 'How many individuals? (optional)',
  'crewMode.weightSample.error.invalidWeight': 'Enter the sampled weight in grams.',
  'crewMode.weightSample.error.outOfRangeWeight': 'Weight must be more than 0 and at most {max} g.',
  'crewMode.weightSample.error.invalidSampleCount': 'Enter a whole number of individuals.',
  'crewMode.weightSample.error.outOfRangeSampleCount': 'Sample count must be at least 1.',
  'crewMode.weightSample.warning.duplicate': 'A weight sample for this lot was saved recently. Tap Confirm again to save anyway.',
  'crewMode.weightSample.confirm': 'Confirm',
  'crewMode.weightSample.confirmAnyway': 'Confirm anyway',
  'crewMode.weightSample.lastEntry': '{weight} g',
  'crewMode.weightSample.lastEntryCount': '{count} sampled',
  'crewMode.weightSample.sync.local': 'Saved on device',
  'crewMode.weightSample.sync.pending': 'Syncing…',
  'crewMode.weightSample.sync.synced': 'Synced',
  'crewMode.weightSample.sync.error': 'Sync failed',
  'crewMode.weightSample.retry': 'Retry',
  'crewMode.weightSample.notEnabledForFarm': 'Crew Mode sampling capture is not enabled for this farm yet. Ask your administrator.',
  'crewMode.weightSample.mode.label': 'Capture mode',
  'crewMode.weightSample.mode.individual': 'One fish at a time',
  'crewMode.weightSample.mode.mean': 'Average of several',
  'crewMode.weightSample.length': 'Length (optional)',
  'crewMode.weightSample.lengthUnit': 'mm',
  'crewMode.weightSample.error.invalidLength': 'Enter the length in millimeters.',
  'crewMode.weightSample.error.outOfRangeLength': 'Length must be more than 0 and at most {max} mm.',
  'crewMode.weightSample.add': 'Add',
  'crewMode.weightSample.addAnyway': 'Add anyway',
  'crewMode.weightSample.undoLast': 'Undo last',
  'crewMode.weightSample.finishRound': 'Finish round',
  'crewMode.weightSample.session.count': '{count} recorded this round',
  'crewMode.weightSample.plan.counter': '{count} of {total}',
  'crewMode.weightSample.plan.complete': 'Sampling complete — tap Finish round',
  'crewMode.weightSample.session.syncSummary': '{synced} of {total} synced',
  'crewMode.weightSample.session.syncSummaryWithIssues': '{synced} of {total} synced, {issues} pending or failed',
  'crewMode.weightSample.session.retryFailed': 'Retry failed',

  // CrewModeFarmBar -- shared farm context indicator/selector across all five Crew Mode screens
  'crewMode.farmBar.label': 'Farm',
  'crewMode.farmBar.changeLabel': 'Change farm',

  'crewMode.home.prompt': 'What are you logging?',
  'crewMode.home.noStreams': 'Nothing is enabled for this farm yet. Ask your administrator.',
  'crewMode.home.stream.water': 'Water',
  'crewMode.home.stream.feed': 'Feed',
  'crewMode.home.stream.mortality': 'Mortality',
  'crewMode.home.stream.weightSample': 'Sampling',
  'crewMode.home.today.heading': 'Your entries today',
  'crewMode.home.today.waitingToSync': '{count} waiting to sync',
  'crewMode.home.today.sync.local': 'Saved on device',
  'crewMode.home.today.sync.pending': 'Syncing…',
  'crewMode.home.today.sync.synced': 'Synced',
  'crewMode.home.today.sync.error': 'Sync failed',
  'crewMode.home.today.field.feedType': 'Feed',
  'crewMode.home.today.field.feedKg': 'kg',
  'crewMode.home.today.field.mortalityCount': 'Dead',
  'crewMode.home.today.field.weightGrams': 'g',
  'crewMode.home.today.field.sampleCount': 'sampled',

  // Optional header shortcuts
  'shortcut.createAccount': 'Create account',
  'shortcut.iconDemo': 'Icon Demo',
  'shortcut.kpiDemo': 'KPI Demo',
  'shortcut.formDemo': 'Form Demo',
  'shortcut.buttonDemo': 'Button Demo',

  // Common aria
  'aria.settings': 'Settings',
  'aria.teamName': 'Team name',
  'aria.createNewTask': 'Create a new task',

  // Auth
  'auth.titleHuman': 'Sign in with your email or username',
  'auth.helpHuman': 'Use the credentials assigned to your tenant member account. Tenant key is only needed when the same person belongs to multiple teams.',
  'auth.humanModeNotice': 'Use email or username plus password for normal member access. Compatibility token access now lives on a separate path for bootstrap, support, or explicitly administrative workflows.',
  'auth.titleToken': 'Sign in with your team access token',
  'auth.helpToken': 'Paste a compatibility token only when a team owner or admin explicitly issued one for bootstrap, support, or admin access. This signs you into the correct team and locks offline storage to that team.',
  'auth.tokenModeNotice': 'Token access is secondary and purpose-specific. Human members should sign in with email or username plus password unless an owner or admin directs them to use a compatibility token.',
  'auth.apiToken': 'API token',
  'auth.tokenPlaceholder': 'sun_...',
  'auth.tokenAccessTitle': 'Need a compatibility access token?',
  'auth.tokenAccessHelp': 'Open token access only for bootstrap, support, integration, or explicitly administrative workflows. Normal member sign-in stays on email or username plus password.',
  'auth.openTokenAccess': 'Open token access',
  'auth.backToHumanSignIn': 'Back to email sign-in',
  'auth.identifier': 'Email or username',
  'auth.identifierPlaceholder': 'you@example.com or field-operator',
  'auth.password': 'Password',
  'auth.passwordPlaceholder': 'At least 8 characters',
  'auth.tenantKeyOptional': 'Tenant key (optional)',
  'auth.tenantKeyPlaceholder': 'Only needed for multi-tenant memberships',
  'auth.signIn': 'Sign in',
  'auth.signInWithToken': 'Sign in with token',
  'auth.signingIn': 'Signing in…',
  'auth.clear': 'Clear',
  'auth.enterCredentialsFirst': 'Enter your email or username and password first.',
  'auth.pasteTokenFirst': 'Paste a token first.',
  'auth.signInFailed': 'Sign-in failed',
  'auth.tooManyAttempts': 'Too many sign-in attempts. Wait 15 minutes and try again.',
  'auth.memberRevoked': 'This account was revoked. Talk to your administrator.',
  'auth.signedInAsPrefix': 'Signed in as',
  'auth.onTenant': 'on tenant',
  'auth.resetActivationTitle': 'Set your account credentials',
  'auth.resetPasswordTitle': 'Reset your password',
  'auth.resetHelp': 'Use this one-time link from your team owner to set or recover your human login credentials.',
  'auth.resetLoading': 'Loading password link…',
  'auth.resetInvalid': 'This password link is invalid, expired, or already used.',
  'auth.resetTokenMissing': 'This password link is missing its token.',
  'auth.resetEmail': 'Email',
  'auth.resetEmailPlaceholder': 'you@example.com',
  'auth.resetUsername': 'Username',
  'auth.resetUsernamePlaceholder': 'field-operator',
  'auth.resetConfirmPassword': 'Confirm password',
  'auth.resetConfirmPasswordPlaceholder': 'Repeat your password',
  'auth.resetPasswordMismatch': 'Passwords must match.',
  'auth.resetSubmitting': 'Saving…',
  'auth.resetActivateAction': 'Save credentials and activate account',
  'auth.resetPasswordAction': 'Save new password',
  'auth.resetSuccess': 'Password updated. Sign in with your new credentials.',
  'ownerConsole.error.passwordResetFailed': 'Failed to create password reset link.',
  'auth.resetExpiresAt': 'Expires',

  // Tasks
  'tasks.title': 'Tasks',
  'tasks.subtitle': 'Coordinate operational tasks with clarity, accountability, and timely reminders.',
  'tasks.notice.cachedTasks': 'Showing cached tasks. Latest updates may be missing until the server is reachable.',
  'tasks.confirm.deleteTask': 'Delete this task?',
  'tasks.confirm.deleteCta': 'Confirm delete',
  'tasks.error.unableToLoad': 'Unable to load tasks',
  'tasks.error.resolveBeforeSaving': 'Please resolve the highlighted errors before saving.',
  'tasks.error.unableToCreate': 'Unable to create task right now.',
  'tasks.error.failedToCreate': 'Failed to create task',
  'tasks.error.failedToUpdate': 'Failed to update task',
  'tasks.error.unableToUpdateStatus': 'Unable to update task status',
  'tasks.error.failedToDelete': 'Failed to delete task',
  'tasks.error.unableToExport': 'Unable to export tasks right now.',
  'tasks.error.unableToImport': 'Unable to import tasks.',
  'tasks.error.readonlyCreate': 'Read-only access: viewer tokens cannot create tasks.',
  'tasks.error.activeLotRequired': 'Select an active lot or clear the lot filter before creating a task.',
  'tasks.error.taskMissing': 'Task no longer exists. Refresh and try again.',
  'tasks.error.readonlyImport': 'Read-only access: this token cannot import tasks.',

  'tasks.validation.titleTooShort': 'Title must be at least 3 characters long.',
  'tasks.validation.descriptionEmpty': 'Description cannot be empty text.',
  'tasks.validation.dueInvalid': 'Due date must be a valid ISO timestamp.',
  'tasks.validation.dueMustBeFutureForActive': 'Active tasks need a due date in the future.',
  'tasks.validation.parentMissing': 'Selected parent task no longer exists.',
  'tasks.validation.parentSelf': 'A task cannot be its own parent.',
  'tasks.validation.parentCycle': 'Parent chain would create a cycle.',
  'tasks.validation.cannotCompleteWithActiveLinked.one': 'Cannot complete while {count} linked task is still active.',
  'tasks.validation.cannotCompleteWithActiveLinked.many': 'Cannot complete while {count} linked tasks are still active.',
  'tasks.validation.objectiveConsiderDueDate': 'Linked tasks are scheduled; consider setting a due date on this objective.',
  'tasks.status.exportBrowserOnly': 'Export is only available in the browser.',
  'tasks.status.noTasksToExport': 'No tasks to export yet.',
  'tasks.status.exportedPrefix': 'Exported',
    'tasks.status.createdLocal': 'Task saved on this device. Server sync is still pending.',
    'tasks.status.createdOffline': 'Task saved on this device. It will sync when back online.',
    'tasks.status.createdSynced': 'Task created and synced.',
    'tasks.status.updatedLocal': 'Task changes saved on this device. Server sync is still pending.',
    'tasks.status.updatedOffline': 'Task changes saved on this device. It will sync when back online.',
    'tasks.status.updatedSynced': 'Task updated and synced.',
    'tasks.status.deletedLocal': 'Task deleted on this device. Server sync is still pending.',
    'tasks.status.deletedOffline': 'Task deleted on this device. It will sync when back online.',
    'tasks.status.deletedSynced': 'Task deleted and synced.',
  'tasks.status.importedPrefix': 'Imported',
  'tasks.status.importedTaskSingular': 'task imported',
  'tasks.status.importedTaskPlural': 'tasks imported',
  'tasks.status.skippedPrefix': ', skipped',
  'tasks.status.duplicateSingular': 'duplicate',
  'tasks.status.duplicatePlural': 'duplicates',
  'tasks.sync.pending': 'Task changes are still queued for sync.',
  'tasks.sync.offline': 'Task changes are saved locally and will sync when connection is restored.',
  'tasks.sync.blocked': '{count} task change(s) need attention before sync can continue.',
  'tasks.sync.blockedWithReason': '{count} task change(s) need attention: {reason}',
  'tasks.sync.retryAction': 'Retry sync',
  'tasks.sync.retrying': 'Retrying sync…',
  'tasks.sync.refreshAction': 'Refresh tasks',
  'tasks.sync.refreshing': 'Refreshing tasks…',
  'tasks.sync.retrySynced': 'Task sync is now up to date.',
  'tasks.sync.retryPending': 'Task sync is still pending. We will keep retrying.',
  'tasks.sync.retryOffline': 'Still offline. Task changes remain safely queued.',
  'tasks.sync.retryResolvedMissing': 'Sync completed with {count} queued change(s) skipped because the task no longer exists on the server.',
  'tasks.sync.retryFailed': 'Unable to retry task sync right now.',
  'tasks.sync.refreshComplete': 'Task list refreshed.',
  'tasks.sync.refreshFailed': 'Unable to refresh tasks right now.',
  'tasks.sync.lastResult': 'Last sync result: {result}',
  'tasks.sync.lastAttempted': 'Last attempted: {time}',
  'tasks.context.facility': 'Facility',
  'tasks.context.productionUnit': 'Production unit',
  'tasks.context.lot': 'Lot',
  'tasks.filter.allFacilities': 'All facilities',
  'tasks.filter.allProductionUnits': 'All production units',
  'tasks.filter.allUnitsInFacility': 'All units in selected facility',
  'tasks.filter.allLots': 'All lots',
  'tasks.filter.loadingLots': 'Loading lots…',
  'tasks.filter.noLotsForUnit': 'No lots for selected unit',
  'tasks.filter.allLotsInUnit': 'All lots in selected unit',
  'tasks.speciesPresetPrefix': 'Species preset:',
  'tasks.selectionHelperSuffix': 'Selections tag new tasks and filter those with matching metadata.',
  'tasks.section.taskList': 'Task list',
  'tasks.view.list': 'List',
  'tasks.view.agenda': 'Calendar',
  'tasks.view.month': 'Month',
  'tasks.searchPlaceholder': 'Search tasks, tags, people',
  'tasks.clearFilters': 'Clear filters',
  'tasks.export': 'Export',
  'tasks.import': 'Import',
  'tasks.importing': 'Importing…',
  'tasks.loading': 'Loading tasks...',
  'tasks.empty.noTasks': 'No tasks yet. Create a new task or apply a suggestion.',
  'tasks.empty.noMatches': 'No tasks match the current selections. Adjust the filters to see more.',
  'tasks.empty.searchNoMatch': 'No tasks match this search.',
  'tasks.empty.adjustFilters': 'No tasks to show. Adjust filters to reveal tasks.',
  'tasks.empty.adjustFiltersOrGrouping': 'No tasks to show. Adjust filters or grouping to reveal tasks.',
  'tasks.month.prev': 'Prev',
  'tasks.month.today': 'Today',
  'tasks.month.next': 'Next',
  'tasks.weekday.mon': 'Mon',
  'tasks.weekday.tue': 'Tue',
  'tasks.weekday.wed': 'Wed',
  'tasks.weekday.thu': 'Thu',
  'tasks.weekday.fri': 'Fri',
  'tasks.weekday.sat': 'Sat',
  'tasks.weekday.sun': 'Sun',
  'tasks.table.what': 'What',
  'tasks.table.who': 'Who',
  'tasks.table.when': 'When',
  'tasks.table.done': 'Done',
  'tasks.completion.completed': 'Completed',
  'tasks.completion.markDone': 'Mark done',
  'tasks.due.none': 'No due date',
  'tasks.due.invalid': 'Invalid due date',
  'tasks.due.overdueByPrefix': 'Overdue by',
  'tasks.due.dueInPrefix': 'Due in',
  'tasks.due.dueOnPrefix': 'Due',
  'tasks.due.minute': 'minute',
  'tasks.due.minutes': 'minutes',
  'tasks.due.hour': 'hour',
  'tasks.due.hours': 'hours',
  'tasks.due.day': 'day',
  'tasks.due.days': 'days',

  'tasks.statusInsight.completed.label': 'Completed',
  'tasks.statusInsight.completed.description.withAge': 'Finished {duration} ago.',
  'tasks.statusInsight.completed.description.noAge': 'Marked as complete.',

  'tasks.statusInsight.blocked.unassigned.label': 'Unassigned',
  'tasks.statusInsight.blocked.unassigned.description': 'Assign a responsible teammate to unblock progress.',
  'tasks.statusInsight.blocked.invalidDue.label': 'Invalid due date',
  'tasks.statusInsight.blocked.invalidDue.description': 'Due date could not be parsed. Update the schedule.',

  'tasks.statusInsight.overdue.label': 'Overdue',
  'tasks.statusInsight.overdue.description': 'Past due by {duration}.',
  'tasks.statusInsight.dueSoon.label': 'Due soon',
  'tasks.statusInsight.dueSoon.description': 'Due in {duration}.',
  'tasks.statusInsight.onTrack.label': 'On track',
  'tasks.statusInsight.onTrack.description': 'Assigned owner and no near-term deadline.',
  'tasks.suggestions.title': 'Suggestions',
  'tasks.suggestions.subtitle': 'Dashboard signals suggest the following tasks. Click to pre-fill a new task.',
  'tasks.create.title': 'Create new task',
  'tasks.create.loggingFor': 'Logging for',
  'tasks.create.changeContext': 'Change context',
  'tasks.create.viewerNoticeSignedInAs': 'Signed in as',
  'tasks.create.viewerNoticeReadOnlyPrefix': 'Tasks are read-only; creating requires a',
  'tasks.create.viewerNoticeOr': 'or',
  'tasks.create.viewerNoticeTokenSuffix': 'token.',
  'tasks.form.whatLabel': 'What needs to happen?',
  'tasks.form.whatPlaceholder': 'Describe the action, context, and expected result',
  'tasks.form.additionalContextLabel': 'Additional context (optional)',
  'tasks.form.tagsLabel': 'Tags',
  'tasks.form.tagsPlaceholder': 'alert, water, urgent',
  'tasks.form.tagsHint': 'Use "alert" to show in System Alert.',
  'tasks.edit.tagsPlaceholder': 'Tags (comma separated)',
  'tasks.form.responsibleLabel': 'Responsible',
  'tasks.form.responsiblePlaceholder': 'Select teammate',
  'tasks.form.collaboratorsLabel': 'Collaborators',
  'tasks.form.objectiveLabel': 'Group under objective',
  'tasks.form.objectivePlaceholder': 'Standalone task',
  'tasks.form.objectiveHelp': 'Link related work to a shared objective so everyone sees the dependency chain.',
  'tasks.form.whenLabel': 'When',
  'tasks.form.reminderCadenceLabel': 'Reminder cadence',
  'tasks.form.enableReminders': 'Enable reminders',
  'tasks.form.saving': 'Saving...',
  'tasks.form.addTask': 'Add task',
  'tasks.reminder.off': 'Reminders off',
  'tasks.reminder.on': 'Reminders on',
  'tasks.reminder.cadencePrefix': 'Cadence:',
  'tasks.cadence.every30min': 'Every 30 minutes',
  'tasks.cadence.every2h': 'Every 2 hours',
  'tasks.cadence.every12h': 'Every 12 hours',
  'tasks.cadence.every24h': 'Every 24 hours',
  'tasks.cadence.every3d': 'Every 3 days',

  'tasks.group.expandAria': 'Expand group',
  'tasks.group.collapseAria': 'Collapse group',
  'tasks.clarity.clear': 'Clear for teammates',
  'tasks.groupInsight.label': 'Group insight:',
  'tasks.assignee.unassigned': 'Unassigned',
  'tasks.collaborators.prefix': 'Collaborators:',
  'tasks.objective.prefix': 'Objective:',
  'tasks.groupSummary.linkedTaskSingularSuffix': 'linked task',
  'tasks.groupSummary.linkedTaskPluralSuffix': 'linked tasks',
  'tasks.groupSummary.overdueSingularSuffix': 'overdue',
  'tasks.groupSummary.overduePluralSuffix': 'overdue',
  'tasks.groupSummary.activeSingularSuffix': 'active',
  'tasks.groupSummary.activePluralSuffix': 'active',
  'tasks.groupSummary.doneSingularSuffix': 'done',
  'tasks.groupSummary.donePluralSuffix': 'done',
  'tasks.groupSummary.unassignedSingularSuffix': 'unassigned',
  'tasks.groupSummary.unassignedPluralSuffix': 'unassigned',

  // Database (Production Data Table)
  'datatable.title': 'Production Data Table',
  'datatable.lead.editable': 'Edit cells anytime — changes save offline on this device and sync when online.',
  'datatable.lead.readonlyPrefix': 'Read-only mode',
  'datatable.lead.readonlySuffix': 'Editing and syncing production records requires an editor token.',

  'datatable.context.facility': 'Facility',
  'datatable.context.productionUnit': 'Production Unit',
  'datatable.filter.allFacilities': 'All facilities',
  'datatable.filter.allProductionUnitsShort': 'All PUs',

  'datatable.action.exportCsv': 'Export CSV',
  'datatable.action.importWorkspace': 'Import Workspace',
  'datatable.action.syncNow': 'Sync now',
  'datatable.action.fuseLegacyDuplicates': 'Fuse duplicates ({count})',
  'datatable.action.fuseLegacyDuplicatesTitle': 'Merge legacy rows with the same lot and date into one row, preserving samplings and mortality events.',
  'datatable.table.actions': 'Actions',
  'datatable.hint.selectContextForImport': 'Select a Facility and Production Unit to enable CSV import.',

  'datatable.syncNow.offline': 'You are offline. Changes are saved locally and will sync when online.',
  'datatable.syncNow.started': 'Sync started. If the connection is unstable, it will continue when online.',
  'datatable.syncNow.failed': 'Sync failed to start. Please try again when the connection is stable.',
  'datatable.sync.title': 'Sync status',
  'datatable.sync.retryAction': 'Retry sync',
  'datatable.sync.refreshAction': 'Refresh records',
  'datatable.sync.retrySynced': 'Database sync is now caught up.',
  'datatable.sync.retryOffline': 'Database changes are local only while this device is offline.',
  'datatable.sync.retryPending': 'Database changes are still pending sync.',
  'datatable.sync.blocked': '{count} production change(s) are blocked.',
  'datatable.sync.blockedWithReason': '{count} production change(s) are blocked: {reason}',
  'datatable.sync.retryResolvedMissing': 'Retry skipped {count} missing production change(s) and kept syncing the rest.',
  'datatable.sync.retryFailed': 'Database sync retry failed.',
  'datatable.sync.refreshFailed': 'Unable to refresh Database sync status right now.',
  'datatable.sync.attentionBlocked': '{count} production change(s) are blocked and need attention.',
  'datatable.sync.attentionBlockedWithReason': '{count} production change(s) are blocked: {reason}',
  'datatable.sync.attentionOffline': 'This device is offline. Database changes will sync when connection returns.',
  'datatable.sync.attentionPending': 'Database changes are queued and still pending sync.',
  'datatable.sync.lastResult': 'Last sync result: {result}',
  'datatable.sync.lastAttempted': 'Last attempted: {time}',

  'datatable.notification.closeTitle': 'Close notification',
  'datatable.lotNotice.label': 'Lot notice:',

  'datatable.dateNotice.title': 'Date Format Notice:',
  'datatable.dateNotice.message': 'Use the date/time picker. Uploaded CSV dates like DD/MM/YYYY HH:mm are normalized automatically.',

  'datatable.empty.noDataTitle': 'No production data found.',
  'datatable.emptyRows.hidden': '{count} past days with no record are hidden (their records were deleted, or the lot was).',
  'datatable.emptyRows.shown': 'Showing {count} past days with no record.',
  'datatable.movedLot.tag': 'moved to another unit',
  'datatable.movedLot.tagTo': 'moved to {unit}',
  'datatable.movedLot.title': 'This lot is no longer in this unit: it was moved to another one. Its records here are kept and stay in the lot\'s history, but cannot be edited.',
  'datatable.movedLot.readOnly': 'This row belongs to a lot that was moved to another unit and cannot be edited here.',
  'datatable.movedLot.hidden': '{count} rows of lots moved to another unit are hidden (they stay in the lot\'s history).',
  'datatable.movedLot.hiddenOne': '1 row of a lot moved to another unit is hidden (it stays in the lot\'s history).',
  'datatable.movedLot.shown': 'Showing {count} rows of lots moved to another unit (read-only).',
  'datatable.movedLot.shownOne': 'Showing 1 row of a lot moved to another unit (read-only).',
  'datatable.deletedLot.tag': 'deleted lot',
  'datatable.context.lot': 'Lot',
  'datatable.filter.allLots': 'All lots',
  'datatable.lotFilter.moved': '(moved)',
  'datatable.lotFilter.from': 'from {day}',
  'datatable.deletedLot.title': 'This lot was deleted. Its records are kept but cannot be edited. To bring it back: Lots → Deleted lots → Restore.',
  'datatable.deletedLot.readOnly': 'This row belongs to a deleted lot and cannot be edited. Restore the lot in Lots → Deleted lots.',
  'datatable.deletedLot.hidden': '{count} rows of deleted lots are hidden (they are kept; Lots → Deleted lots → Restore brings them back).',
  'datatable.deletedLot.hiddenOne': '1 row of a deleted lot is hidden (it is kept; Lots → Deleted lots → Restore brings it back).',
  'datatable.deletedLot.shown': 'Showing {count} rows of deleted lots (read-only).',
  'datatable.deletedLot.shownOne': 'Showing 1 row of a deleted lot (read-only).',
  'datatable.emptyRows.show': 'Show',
  'datatable.emptyRows.hide': 'Hide',
  'datatable.emptyRows.hiddenOne': '1 past day with no record is hidden (its records were deleted, or the lot was).',
  'datatable.emptyRows.shownOne': 'Showing 1 past day with no record.',
  'datatable.empty.noDataSubtitle': 'Add a new record to get started.',

  'datatable.date.placeholder': 'YYYY-MM-DDThh:mm',
  'datatable.date.invalidHint': 'Invalid date — use the date/time picker',

  'datatable.readonly.cannotEditRecords': 'Read-only access: this token cannot edit production records.',
  'datatable.readonly.cannotDeleteRecords': 'Read-only access: this token cannot delete production records.',
  'datatable.readonly.cannotImportRecords': 'Read-only access: this token cannot import production records.',

  'datatable.error.activeLotRequired': 'Select an active lot before assigning a production row.',
  'datatable.error.lotEditOutsideRecovery': 'Edit the lot on the original capture screen, not on the rollup.',

  'datatable.import.noFileSelected': 'No file selected.',
  'datatable.import.selectCsvFile': 'Please select a CSV file.',
  'datatable.import.fileEmpty': 'File is empty or could not be read.',
  'datatable.import.noRowsFound': 'No rows found in CSV',
  'datatable.import.success': 'CSV imported successfully!',
  'datatable.import.clearedForLimitsPrefix': 'Some values exceeded limits',
  'datatable.import.numericExceededNotice': 'Some imported values exceeded numeric limits. Please review those cells in the table.',

  'datatable.lots.autoCreatedPrefix': 'Lots auto-created:',
  'datatable.lots.autoCreatedSuffix': '— added to Lots for the selected Facility + Production Unit.',
  'datatable.lots.foundNotValidatedPrefix': 'Lots found in file but not validated (no Facility + Production Unit selected):',
  'datatable.lots.ambiguousPrefix': 'Lots with ambiguous roster matches left for manual mapping:',
  'datatable.lots.leftUnmappedPrefix': 'Lots not in roster and left unmapped:',

  'datatable.numericNotice.storedPrefix': 'Some stored values were invalid or outside allowed ranges',
  'datatable.numericNotice.storedSuffix': 'Please review those cells in the table.',

  'datatable.storageWriteFailed': 'Could not save locally on this device (storage may be full). Export CSV as a backup, then clear old site data/caches (especially any production table backups) and try again.',

  'datatable.numericValidation.mustBeNumber': '{label} must be a number.',
  'datatable.numericValidation.mustBeWholeNumber': '{label} must be a whole number.',
  'datatable.numericValidation.min': '{label} must be at least {min}.',
  'datatable.numericValidation.max': '{label} must be {max} or below.',

  'datatable.feedKg.autoLabel': 'Auto from Feed logs',
  'datatable.feedKg.autoTitle': 'Auto-filled from Feed logs (edit to override).',
  'datatable.feedKg.manualLabel': 'Manual override',
  'datatable.feedKg.manualTitle': 'Manually entered value (will not be overwritten by autofill).',

    'datatable.weight.lastKnownLabel': 'last',
    'datatable.weight.lastKnownTitle': 'Last average weight recorded for this lot (from an earlier sampling). Shown for reference; not stored on this row.',
  'datatable.fishStock.derivedLabel': 'derived',
  'datatable.fishStock.derivedTitle': 'End-of-day stock derived from previous known stock minus daily mortality. The stored fish stock value remains blank.',
  'datatable.fishStock.derivedUnknownLabel': 'no prior stock',
  'datatable.fishStock.derivedUnknownTitle': 'Fish stock cannot be derived because no earlier row has a known stock value for this lot. Record an initial stock to enable derivation.',

  'datatable.sameDayRows.badge': '{count} rows this day',
  'datatable.sameDayRows.badgeTitle': 'This day has {count} sampling rows for this lot. Click to see every row of the day.',
  'datatable.sameDayRows.popover.title': 'Rows on the same day — {lot} · {date}',
  'datatable.sameDayRows.popover.total': '{count} rows recorded on this day',
  'datatable.sameDayRows.popover.empty': 'No other rows are linked to this day.',
  'datatable.sameDayRows.popover.column.time': 'Time',
  'datatable.sameDayRows.popover.column.weight': 'Weight (g)',
  'datatable.sameDayRows.popover.column.mortality': 'Mortality',
  'datatable.sameDayRows.popover.column.stock': 'Fish stock',
  'datatable.sameDayRows.popover.close': 'Close',

  'datatable.mortalityRate.cell': '{pct}%',
  'datatable.mortalityRate.cellTitle': 'Daily mortality rate: {count} deaths ÷ {stock} fish at the start of the day. Derived over synced data; not stored.',
  'datatable.mortalityRate.readOnly': 'Daily mortality % is a calculated indicator (mortality ÷ start-of-day stock) — it cannot be edited here.',
  'datatable.mortalityRate.severity.green': 'On track (matrix expects {expected}%). Click to create a task.',
  'datatable.mortalityRate.severity.yellow': 'Above expected (matrix expects {expected}%). Click to create a task.',
  'datatable.mortalityRate.severity.red': 'Well above expected (matrix expects {expected}%, 1.5x or worse). Click to create a task.',
    'datatable.mortalityRate.stage.green': '{day}: within the reference (green up to {green} %/day, red from {red} %; Edit facility → Hatchery). Click to create a task.',
    'datatable.mortalityRate.stage.yellow': '{day}: above the expected (green up to {green} %/day, red from {red} %; Edit facility → Hatchery). Click to create a task.',
    'datatable.mortalityRate.stage.red': '{day}: well above the expected (red from {red} %/day; Edit facility → Hatchery). Click to create a task.',
    'datatable.mortalityRate.stage.eggs': 'Eggs',
    'datatable.mortalityRate.stage.yolkSac': 'Yolk-sac fry',
    'datatable.mortalityRate.stage.critical': 'Critical day ({moment})',
    'datatable.mortalityRate.critical.arrival': 'egg arrival and re-incubation',
    'datatable.mortalityRate.critical.hatch': 'hatching',
    'datatable.mortalityRate.critical.firstFeeding': 'first feeding',
    'datatable.mortalityRate.critical.firstTransfer': 'first transfer',
    'datatable.mortalityRate.missing.noWeight': 'No weight: record a sampling of the lot to compare with the mortality matrix. Click to create a task.',
    'datatable.mortalityRate.missing.noTemperature': 'No temperature: no temperature reading for the lot in the last 7 days to compare with the matrix. Click to create a task.',
  'datatable.mortalityRate.taskTitle': 'Review mortality — lot {lot} ({date})',
  'datatable.mortality.derivedFromEventsLabel': 'from {count} events',
  'datatable.mortality.derivedFromEventsTitle': 'Daily mortality is the canonical sum of {count} mortality events recorded for this lot and date. The stored row value is preserved but is not displayed; edit individual events from the Biomass screen instead.',
  'datatable.mortality.readOnlyEditAttempt': 'Daily mortality is read-only here because mortality events exist for this lot and date. Edit individual events from the Biomass screen.',
  'datatable.weight.readOnlyEditAttempt': 'Weight is read-only here because weight sample events exist for this lot and date. Log new samples from the Biomass Sampling screen.',
  'datatable.harvest.readOnlyEditAttempt': 'Harvest is read-only here because harvest events exist for this lot and date. Log or edit harvest from the Biomass screen.',
  'datatable.date.readOnlyEditAttempt': "This row's date is read-only here — changing it directly would disconnect it from its own weight, mortality, and harvest events. To move an entry to a different date, correct it from the Biomass screen instead.",
  'datatable.mortality.events.badgeAction': 'Click to see the {count} individual mortality events that sum to this daily total.',
  'datatable.mortality.events.popover.title': 'Mortality events — {lot} · {date}',
  'datatable.mortality.events.popover.total': '{count} events · daily total: {sum}',
  'datatable.mortality.events.popover.column.time': 'Time',
  'datatable.mortality.events.popover.column.count': 'Count',
  'datatable.mortality.events.popover.column.notes': 'Notes',
  'datatable.mortality.events.popover.close': 'Close',
  'datatable.mortality.events.popover.empty': 'No mortality events are linked to this row.',
  'datatable.mortality.events.popover.column.actions': 'Actions',
  'datatable.mortality.events.popover.delete': 'Delete',
  'datatable.mortality.events.popover.deleteTitle': 'Delete this mortality event (does not delete the sampling row).',
  'datatable.mortality.events.popover.deleteConfirm': 'Delete this mortality event of {count} on {time}? This cannot be undone.',
  'datatable.mortality.events.popover.deleteCta': 'Confirm delete',
  'datatable.mortality.events.popover.deleting': 'Deleting…',

  'datatable.sampling.averagedFromEventsLabel': 'averaged from {count} samplings',
  'datatable.sampling.averagedFromEventsTitle': 'This average weight is the arithmetic mean of {count} samplings recorded for this lot and date. Click to review individual weights.',
  'datatable.sampling.events.badgeAction': 'Click to see the {count} individual samplings that compose this average weight.',
  'datatable.sampling.events.popover.title': 'Same-day samplings — {lot} · {date}',
  'datatable.sampling.events.popover.total': '{count} samplings · mean weight: {mean} g',
  'datatable.sampling.events.popover.column.time': 'Time',
  'datatable.sampling.events.popover.column.weight': 'Weight (g)',
  'datatable.sampling.events.popover.column.length': 'Length',
  'datatable.sampling.events.popover.close': 'Close',
  'datatable.sampling.events.popover.empty': 'No individual samplings recorded for this row.',

  'datatable.legacyDuplicates.modal.title': 'Fuse legacy duplicate rows',
  'datatable.legacyDuplicates.modal.subtitle': '{groupCount} groups · {affected} rows → {kept} row per group · {deleted} rows will be deleted.',
  'datatable.legacyDuplicates.modal.intro': 'This action merges rows with the same lot and date into a single row. Weight becomes the arithmetic mean of the samplings, fish stock keeps the most recent value, and linked mortality events are rebound to the kept row. The action cannot be automatically undone.',
  'datatable.legacyDuplicates.modal.itemFormat': '{lot} · {date} · {from} rows → 1 ({rebinds} events rebound)',
  'datatable.legacyDuplicates.modal.truncatedHint': 'Showing {shown} of {total} groups.',
  'datatable.legacyDuplicates.modal.cancel': 'Cancel',
  'datatable.legacyDuplicates.modal.confirm': 'Confirm merge',
  'datatable.legacyDuplicates.modal.running': 'Merging…',
  'datatable.legacyDuplicates.toast.success': 'Merge complete: {kept} row per group, {deleted} rows deleted, {rebinds} events rebound.',
  'datatable.legacyDuplicates.toast.failure': 'Failed to merge duplicates. Detail: {reason}',

  'datatable.column.weight': 'Weight (g)',
  'datatable.column.dailyMortality': 'Daily Mortality',
  'datatable.column.dailyMortalityRate': 'Daily Mortality %',
  'datatable.column.fishStock': 'Fish Stock',
  'datatable.column.plCount': 'PL Count',
  'datatable.column.mort': 'Treatment',
  'datatable.column.do': 'Length',
  'datatable.column.upVol': 'Notes',
  'datatable.column.avgLengthMm': 'Avg Length (mm)',
  'datatable.column.feedKg': 'Feed (kg)',
  'datatable.column.fcr': 'FCR',
  'datatable.column.feedProteinPct': 'Feed Protein %',
  'datatable.column.harvestKg': 'Harvest (kg)',
  'datatable.column.harvestCount': 'Harvest (count)',
  'datatable.column.temperatureC': 'Temperature (°C)',
  'datatable.column.salinityPpt': 'Salinity (ppt)',

  'datatable.option.none': '(none)',
  'datatable.lot.noneYet': 'No lots yet',
  'datatable.lot.createInLotsHelp': 'Create a Lot in the Lots screen to enable selection here.',
  'datatable.lot.goToLots': 'Go to Lots',
  'datatable.lot.clearedNotInList': 'Cleared (not in lots list)',
  'datatable.lot.ambiguousPrefix': 'Ambiguous match:',
  'datatable.lot.unmappedPrefix': 'Unmapped:',
  'datatable.lot.mapToExisting': 'Map to existing',
  'datatable.lot.ignoreUnmapped': 'Ignore label',
  'datatable.mortalityGuide.recommendation': 'Matrix guide: expected mortality {expectedMortality}% at {temperature}°C. Projected survival {projectedSurvival}%, projected stock {projectedStock}, projected biomass {projectedBiomass} kg.',
  'datatable.mortalityGuide.noTemperature': 'Matrix guide is ready, but this lot has no matched water temperature for the same week yet.',
  'datatable.mortalityGuide.missingInputs': 'Add date, lot, weight, and stock to show the mortality-matrix recommendation here.',

  'datatable.stage.active': 'Active',
  'datatable.stage.inactive': 'Inactive',
  'datatable.health.good': 'Good',
  'datatable.health.issue': 'Issue',
  'datatable.health.emergency': 'Emergency',
  'datatable.treatment.vaccine': 'Vaccine',
  'datatable.treatment.antibiotic': 'Antibiotic',
  'datatable.treatment.disinfectant': 'Disinfectant',
  'datatable.treatment.salt': 'Salt',
  'datatable.treatment.other': 'Other',

  // Costs (v2.2 T3-C-07 — dedicated /costs screen for the LotCostEvent Tier A stream)
  'costs.title': 'Costs',
  'costs.subtitle': 'Log manual cost events (Genetics / Health / Labor) per lot and review auto-derived feed costs.',
  'costs.readonly.banner': 'Read-only mode. Recording or editing cost events requires an editor token.',
  'costs.context.facility': 'Facility',
  'costs.context.productionUnit': 'Production Unit',
  'costs.context.lot': 'Lot',
  'costs.placeholder.selectFacility': 'Select a facility...',
  'costs.placeholder.selectFacilityFirst': 'Select facility first...',
  'costs.placeholder.selectProductionUnit': 'Select production unit...',
  'costs.placeholder.selectLot': 'Select a lot...',
  'costs.placeholder.noLotsInScope': 'No lots in this scope',

  // Water
  'water.title': '🌊 Water Quality Parameters',
  'water.readonly.bannerPrefix': 'Read-only mode (',
  'water.readonly.bannerMiddle': ' token).',
  'water.readonly.bannerSuffix': 'Editing water logs and creating lots requires an editor token.',

  'water.context.facility': 'Facility',
  'water.context.productionUnit': 'Production Unit',

  'water.placeholder.selectFacility': 'Select a facility...',
  'water.placeholder.selectFacilityFirst': 'Select facility first...',
  'water.placeholder.selectProductionUnit': 'Select production unit...',

  'water.action.exportCsv': 'Export CSV',
  'water.action.importWorkspace': 'Import Workspace',
  'water.action.importCsv': 'Import CSV',
  'water.action.addMeasurement': '+ Add Measurement',
  'water.action.refreshLogs': 'Refresh water logs',
  'water.hint.selectContextForImport': 'Select a Facility and Production Unit to enable Water import.',

  'water.notification.closeTitle': 'Close notification',
  'water.lotNotice.label': 'Lot notice:',

  'water.dateNotice.title': 'Date Format Notice:',
  'water.dateNotice.message': 'Use the date/time picker. Uploaded CSV dates like DD/MM/YYYY HH:mm are normalized automatically.',

  'water.date.placeholder': 'YYYY-MM-DDThh:mm',
  'water.date.invalidHint': 'Invalid date — use the date/time picker',

  'water.import.readonlyCannotImport': 'Read-only access: this token cannot import water logs.',
  'water.import.noFileSelected': 'No file selected.',
  'water.import.selectCsvFile': 'Please select a CSV file.',
  'water.import.fileEmpty': 'File is empty or could not be read.',
  'water.import.success': 'CSV imported successfully!',

  'water.status.savedOffline': 'Water log saved locally while offline.',
  'water.status.savedPending': 'Water log saved locally. Server sync is pending.',
  'water.status.savedSynced': 'Water log synced.',
  'water.status.deletedOffline': 'Water log deleted locally while offline.',
  'water.status.deletedPending': 'Water log deleted locally. Server sync is pending.',
  'water.status.deletedSynced': 'Water log deletion synced.',
  'water.status.refreshing': 'Refreshing water logs...',
  'water.status.refreshComplete': 'Water logs refreshed from the server.',
  'water.status.refreshFailed': 'Could not refresh water logs right now.',
  'water.sync.retrySynced': 'Water sync is now caught up.',
  'water.sync.retryOffline': 'Water changes are local only while this device is offline.',
  'water.sync.retryPending': 'Water changes are still pending sync.',
  'water.sync.blocked': '{count} water change(s) need attention before they can sync.',
  'water.sync.blockedWithReason': '{count} water change(s) need attention: {reason}',
  'water.sync.retryResolvedMissing': 'Retry skipped {count} missing water change(s) and kept syncing the rest.',
  'water.sync.retryAction': 'Retry sync',
  'water.sync.retrying': 'Retrying sync...',
  'water.sync.refreshAction': 'Refresh water logs',
  'water.sync.refreshing': 'Refreshing water logs...',
  'water.sync.lastResult': 'Last sync result: {result}',
  'water.sync.lastAttempted': 'Last attempted: {time}',
  'water.sync.baselineCaughtUp': 'Water sync is up to date. No queued changes.',


  'water.readonly.cannotAddMeasurements': 'Read-only access: this token cannot add water measurements.',
  'water.readonly.cannotDeleteMeasurements': 'Read-only access: this token cannot delete water measurements.',

  'water.option.none': '(none)',
  'water.lot.loadingLots': 'Loading lots…',
  'water.lot.noneYet': 'No lots yet',
  'water.lot.selectContext': 'Select a Facility + Production Unit',
  'water.lot.createInLotsHelp': 'Create a Lot in the Lots screen to enable selection here.',
  'water.lot.selectContextHelp': 'Select a Facility and Production Unit to load the lots list.',
  'water.lot.savedLabelPrefix': 'Saved label:',
  'water.lot.ambiguousPrefix': 'Ambiguous match:',
  'water.lot.unmappedPrefix': 'Unmapped:',
  'water.lot.clearedNotInList': 'Cleared (not in lots list)',
  'water.lot.transferredTag': 'transferred',
  'water.lot.transferredTitle': 'This lot has since been transferred to another production unit. Its readings here stay visible but read-only — manage the lot from its current unit.',
    'water.lot.deletedTag': 'deleted lot',
    'water.lot.deletedTitle': 'This lot was deleted; the reading is kept as a record of the pond.',
  'water.lot.ignoreUnmapped': 'Ignore label',
  'water.lot.goToLots': 'Go to Lots',

  'water.lots.autoCreatedPrefix': 'Lots auto-created:',
  'water.lots.autoCreatedSuffix': '— added to Lots for the selected Facility + Production Unit.',
  'water.lots.foundNotValidatedPrefix': 'Lots found in file but not validated (no Facility + Production Unit selected):',
  'water.lots.ambiguousPrefix': 'Lots with ambiguous roster matches left for manual mapping:',
  'water.lots.leftUnmappedPrefix': 'Lots not in roster and left unmapped:',

  'water.table.date': 'Date',
  'water.table.lot': 'Lot',
  'water.table.temperature': 'Temperature (°C)',
  'water.table.ph': 'pH',
  'water.table.doEstimated': 'DO (mg/L) estimated',
  'water.table.doSaturationEstimated': 'DO Saturation (%) vs sea level',
  'water.table.doManual': 'DO (mg/L) manually',
  'water.table.doSaturationManual': 'DO Saturation (%) vs sea level (manual)',
  'water.table.salinity': 'Salinity (‰)',
  'water.table.notes': 'Notes',
  'water.table.actions': 'Actions',
  'water.lockedRowHint': 'Saved reading: change or delete it with "Correct…" (a reason is required and the previous values are kept).',
  'water.delete.draftReason': 'New row deleted while it was being typed',

  'water.placeholder.celsius': '°C',
  'water.placeholder.ph': 'pH',
  'water.placeholder.salinity': '‰',
  'water.placeholder.manualReading': 'Manual reading',
  'water.placeholder.manualSaturation': 'Manual % saturation',
  'water.placeholder.observations': 'Observations...',

  'water.do.autoFromPrefix': 'Auto from',
  'water.do.vsSeaLevelStandard': 'vs sea level standard',
  'water.do.manualSaturationDerived': 'Auto from manual DO and temperature: {value}%',

  'water.empty.title': '🌊 No water quality measurements recorded yet.',
  'water.empty.subtitlePrefix': 'Click "',
  'water.empty.subtitleAction': 'Add Measurement',
  'water.empty.subtitleSuffix': '" to start monitoring your aquaculture system.',

  'water.info.do.heading': '🌡️🏔️ ⇄ 💧 Temperature & Altitude-Corrected Dissolved Oxygen',
  'water.info.do.baseFormula': 'Base Formula:',
  'water.info.do.altitudeCorrection': 'Altitude Correction:',
  'water.info.do.currentAltitudePrefix': 'Current Altitude:',
  'water.info.do.pressureRatioPrefix': 'pressure ratio:',
  'water.info.do.examplesPrefix': 'Examples at',
  'water.info.do.examplesSeaLevel': '10°C → 11.3 mg/L | 20°C → 9.1 mg/L',
  'water.info.do.tip': '💡 Dissolved oxygen is automatically estimated from temperature and facility altitude (set in Edit Facility). Higher altitudes = lower DO due to reduced atmospheric pressure. Use the "Manual DO" column for measured values.',

  'water.info.monitoring.title': '💡 Water Quality Monitoring Info',
  'water.info.monitoring.temperature.title': '🌡️ Temperature (°C)',
  'water.info.monitoring.temperature.optimalRangeLabel': 'Optimal range:',
  'water.info.monitoring.temperature.optimalRangeValue': '9-17°C for SFR calculations',
  'water.info.monitoring.temperature.note': 'Temperature data is automatically used in the SFR Matrix system.',
  'water.info.monitoring.ph.title': '🧪 pH Level',
  'water.info.monitoring.ph.optimalRangeLabel': 'Optimal range:',
  'water.info.monitoring.ph.optimalRangeValue': '6.5-8.5',
  'water.info.monitoring.ph.note': 'Monitors acidity/alkalinity for optimal fish health.',
  'water.info.monitoring.do.title': '💨 Dissolved Oxygen',
  'water.info.monitoring.do.minimumLabel': 'Minimum:',
  'water.info.monitoring.do.minimumValue': '5.0 mg/L',
  'water.info.monitoring.do.note': 'Critical for fish respiration and system health.',
  'water.info.monitoring.salinity.title': '🧂 Salinity',
  'water.info.monitoring.salinity.variesBySpecies': 'Varies by species',
  'water.info.monitoring.salinity.note': 'Monitor salt concentration for marine systems.',

  // Feed
  'feed.title': '🐟 Feed Planning & KPIs',

  'feed.context.facility': 'Facility',
  'feed.context.productionUnit': 'Production Unit',

  'feed.placeholder.selectFacility': 'Select a facility...',
  'feed.placeholder.selectFacilityFirst': 'Select facility first...',
  'feed.placeholder.selectProductionUnit': 'Select production unit...',

  'feed.stockMetric.fishStock': 'Fish Stock',
  'feed.stockMetric.plCount': 'PL Count',

  'feed.summary.calculationPeriods': '{count} calculation periods',

  'feed.section.actualFed.title': 'Actual fed (real feed events)',
  'feed.section.actualFed.lead': 'Record each feeding event. Saved offline-first and synced when online.',
  'feed.readonly.banner': 'Read-only mode ({role} token). Creating, editing, and deleting feed events requires an editor token.',

  'feed.form.recordedAt': 'Recorded at',
  'feed.form.lot': 'Lot',
  'feed.form.feedKg': 'Feed (kg)',
  'feed.form.feedKg.placeholder': 'e.g. 12.5',
  'feed.form.feedType': 'Feed type (optional)',
  'feed.form.feedType.placeholder': 'e.g. 3mm pellet',
  'feed.form.feedType.suggestionsHelp': 'Suggestions based on feed types logged in this facility in the last {days} days.',
  'feed.form.notes': 'Notes (optional)',
  'feed.form.feedingResponse': 'Feeding response (optional)',
  'feed.form.feedingResponseHelp': 'How the fish took the feed. Crew Mode does not ask it.',
  'feed.feedingResponse.notRecorded': 'Not recorded',
  'feed.feedingResponse.good': 'Good — ate eagerly',
  'feed.feedingResponse.normal': 'Normal',
  'feed.feedingResponse.poor': 'Poor — feed left over',
  'feed.feedingResponse.none': 'Did not eat',
  'feed.table.response': 'Response: {response}',
  'feed.table.mixPart': 'Mix: {mix}',
  'feed.table.weighed': 'Weighed {weighed} kg − leftover {leftover} kg',
  'feed.table.weighedNoLeftover': 'Weighed {weighed} kg · no leftover recorded',
  'feed.table.feedLot': 'Feed lot {lot}',

  'feed.lots.loadingLots': 'Loading lots…',
  'feed.lots.noneLoaded': 'No lots loaded',

  'feed.errors.lotsPrefix': 'Lots:',
  'feed.errors.eventsPrefix': 'Feed events:',

  'feed.error.readonlyCannotCreateOrEdit': 'Read-only access: this token cannot create or edit feed events.',
  'feed.error.readonlyCannotEdit': 'Read-only access: this token cannot edit feed events.',
  'feed.error.readonlyCannotDelete': 'Read-only access: this token cannot delete feed events.',
  'feed.confirm.deleteEvent': 'Delete this feed event?',
  'feed.confirm.deleteEventCta': 'Confirm delete',
  'feed.error.activeLotRequired': 'Select an active lot before saving a feed event.',
  'feed.error.feedKgRequired': 'Feed (kg) is required.',
  'feed.error.feedKgNonNegative': 'Feed (kg) must be a valid non-negative number.',
  'feed.error.unableToLoadEvents': 'Unable to load feed events.',
  'feed.error.unableToSaveEvent': 'Unable to save feed event.',
  'feed.error.unableToDeleteEvent': 'Unable to delete feed event.',
    'feed.status.savedLocal': 'Feed event saved on this device. Server sync is still pending.',
    'feed.status.savedOffline': 'Feed event saved on this device. It will sync when back online.',
    'feed.status.savedSynced': 'Feed event saved and synced.',
    'feed.status.deletedLocal': 'Feed event deleted on this device. Server sync is still pending.',
    'feed.status.deletedOffline': 'Feed event deleted on this device. It will sync when back online.',
    'feed.status.deletedSynced': 'Feed event deleted and synced.',
    'feed.status.refreshing': 'Refreshing feed events...',
    'feed.status.refreshed': 'Feed events refreshed from the server.',
    'feed.status.refreshFailed': 'Could not refresh feed events right now.',
    'feed.sync.retrySynced': 'Feed sync is now caught up.',
    'feed.sync.retryOffline': 'Feed changes are local only while this device is offline.',
    'feed.sync.retryPending': 'Feed changes are still pending sync.',
    'feed.sync.blocked': '{count} feed change(s) need attention before they can sync.',
    'feed.sync.blockedWithReason': '{count} feed change(s) need attention: {reason}',
    'feed.sync.retryResolvedMissing': 'Retry skipped {count} missing feed change(s) and kept syncing the rest.',
    'feed.sync.retryAction': 'Retry sync',
    'feed.sync.retrying': 'Retrying sync...',
    'feed.sync.refreshAction': 'Refresh feed events',
    'feed.sync.refreshing': 'Refreshing feed events...',
    'feed.sync.retryFailed': 'Feed sync retry failed.',
    'feed.sync.lastResult': 'Last sync result: {result}',
    'feed.sync.lastAttempted': 'Last attempted: {time}',

  'feed.button.addEvent': 'Add event',
  'feed.button.updateEvent': 'Update event',
  'feed.button.cancelEdit': 'Cancel edit',
  'feed.action.importWorkspace': 'Import Workspace',
    'feed.button.refreshEvents': 'Refresh feed events',
    'feed.button.refreshEventsTooltip': 'Refresh feed events from the server to see changes made on other devices.',
  'feed.button.showLog': 'Show log',
  'feed.button.hideLog': 'Hide log',
  'feed.button.loadOlderWeeks': 'Load older weeks',
    'feed.instructions.refreshHint': 'Use Refresh feed events if this device is not yet showing changes from another device.',

  'feed.section.recentEvents': 'Recent events',
  'feed.status.loading': 'Loading…',
  'feed.empty.noFeedEvents': 'No feed events yet for this unit.',

  'feed.table.recorded': 'Recorded',
  'feed.table.lot': 'Lot',
  'feed.table.feedKg': 'Feed (kg)',
  'feed.table.type': 'Type',
  'feed.table.notes': 'Notes',
  'feed.table.actions': 'Actions',
  'feed.table.costBadgeTooltip': 'Auto-derived cost from feed × unit cost set in Warehouse.',

  'feed.note.recommendedVsActual': 'Recommended feed KPIs remain intact. Actual feed values are derived from these events and shown alongside for comparison.',

  'feed.viewMode.daily': 'Daily View',
  'feed.viewMode.weekly': 'Weekly View',

  'feed.aria.panels': 'Panels',
  'feed.panel.log': 'Log',
  'feed.panel.analyze': 'Analyze',

  'feed.panel.kpis': 'KPIs',
  'feed.panel.breakdown': 'Breakdown Table',
  'feed.panel.charts': '📊 Charts & Graphs',

  'feed.empty.noData.title': '⚠️ No Data Available',
  'feed.empty.noData.intro': 'Please ensure you have:',
  'feed.empty.noData.item.database': 'Database records with Weight and {stockMetric} data',
  'feed.empty.noData.item.water': 'Water quality records with Temperature data',
  'feed.empty.noData.item.lots': 'Matching Lot numbers between Database and Water screens',

  'feed.kpi.weekLabel': 'Week:',
  'feed.temperatureAlert.title': 'Temperature Alert',
  'feed.kpi.biomass': 'Biomass:',
  'feed.kpi.sfr': 'SFR:',
  'feed.kpi.dailyFeed': 'Daily Feed:',
  'feed.kpi.weeklyFeed': 'Weekly Feed:',
  'feed.kpi.actualFeed': 'Actual Feed:',
  'feed.kpi.eventsSuffix': 'events',
  'feed.kpi.adherence': 'Adherence:',
  'feed.kpi.sgr': 'SGR:',
  // v2.2 T3-B-06d: this KPI is the SFR-model-predicted FCR (recommended
  // feed ÷ observed weekly biomass gain), distinct from the
  // cost-per-kg report's actual FCR (feed kg ÷ harvest kg). Label +
  // tooltip disambiguate the two surfaces.
  'feed.kpi.fcrPlanned': 'FCR (planned):',
  'feed.kpi.fcrPlannedTooltip': 'Predicted by the SFR model — recommended feed ÷ observed weekly biomass gain. Different from the cost-per-kg report’s FCR, which uses actual feed and actual harvest.',

  'feed.missingData.title': 'Missing Data:',
  'feed.missingData.weight': 'Weight data in Database',
  'feed.missingData.stockFish': 'Fish Stock data in Database',
  'feed.missingData.stockPl': 'PL Count data in Database',
  'feed.missingData.temperature': 'Temperature data in Water screen',

  'feed.quality.complete': 'Complete',
  'feed.quality.partial': 'Partial',
  'feed.quality.incomplete': 'Incomplete',

  'feed.summaryStats.title': '📊 Summary Statistics',
  'feed.summaryStats.totalBiomass': 'Total Biomass:',
  'feed.summaryStats.totalDailyFeed': 'Total Daily Feed:',
  'feed.summaryStats.totalWeeklyFeed': 'Total Weekly Feed:',
  'feed.summaryStats.totalActualFeed': 'Total Actual Feed:',
  'feed.summaryStats.overallAdherence': 'Overall Adherence:',
  'feed.summaryStats.avgSfr': 'Avg SFR:',
  'feed.summaryStats.avgFcr': 'Avg FCR:',
  'feed.summaryStats.periodsSuffix': 'periods',
  'feed.summaryStats.dataQuality': 'Data Quality:',

  'feed.period.day': 'Day',
  'feed.period.week': 'Week',
  'feed.period.daily': 'Daily',
  'feed.period.weekly': 'Weekly',

  'feed.breakdown.title': '🐟 Breakdown by {period}',
  'feed.breakdown.column.period': 'Period',
  'feed.breakdown.column.lot': 'Lot',
  'feed.breakdown.column.biomassKg': 'Biomass (kg)',
  'feed.breakdown.column.sfrPct': 'SFR (%)',
  'feed.breakdown.column.feedKg': '{period} Feed (kg)',
  'feed.breakdown.column.actualKg': 'Actual (kg)',
  'feed.breakdown.column.adherence': 'Adherence',
  'feed.breakdown.column.events': 'Events',
  'feed.breakdown.column.fcr': 'FCR (planned)',
  'feed.breakdown.column.status': 'Status',
  'feed.value.na': 'N/A',

  'feed.charts.heading': '📊 Visual Analytics',
  'feed.charts.lead': 'Charts are derived from the same KPI periods shown in cards and tables.',
  'feed.charts.tipLandscape': 'Tip: rotate to landscape for easier reading. You can also scroll charts horizontally.',
  'feed.charts.noData': 'No data available for charts',
  'feed.charts.recommendedVsActualTitle': 'Recommended vs Actual {period} Feed (kg) by Period',
  'feed.charts.axis.feedKg': 'Feed (kg)',
  'feed.charts.sfrTitle': 'Avg SFR (%) by Period (biomass-weighted; complete lots only)',
  'feed.charts.axis.sfrPct': 'SFR (%)',
  'feed.charts.noFcr': 'No FCR chart available yet (needs at least 2 weeks of complete data).',
  'feed.charts.fcrTitle': 'Avg FCR (planned) by Period (complete lots only)',
  'feed.charts.axis.fcr': 'FCR',
  'feed.charts.relationshipNeedsData': 'Relationship chart needs weeks with complete SFR + SGR + FCR data.',
  'feed.charts.relationshipTitle': 'Relationship: SGR (left) and FCR (right) vs SFR (x)',
  'feed.charts.axis.sgr': 'SGR (%/day)',
  'feed.charts.legend.sgr': 'SGR',
  'feed.charts.legend.fcr': 'FCR',

  'feed.guide.title': '📊 Feed Planning Metrics Guide',
  'feed.guide.sfr.title': '🌡️ Specific Feeding Rate (SFR)',
  'feed.guide.fcr.title': '📈 Feed Conversion Rate (FCR)',
  'feed.guide.biomass.title': '⚖️ Biomass Calculation',
  'feed.guide.quality.title': '🚨 Data Quality',
  'feed.guide.label.definition': 'Definition:',
  'feed.guide.label.calculation': 'Calculation:',
  'feed.guide.label.temperatureDependency': 'Temperature dependency:',
  'feed.guide.sfr.definition': 'Percentage of body weight to feed daily',
  'feed.guide.sfr.calculation': 'Based on fish weight and water temperature',
  'feed.guide.sfr.temperatureDependency': 'Optimal 9-17°C for trout/salmon',
  'feed.guide.fcr.definition': 'Feed consumed ÷ weight gained',
  'feed.guide.fcr.calculation': 'Weekly feed ÷ biomass increase',
  'feed.guide.fcr.good': 'Good FCR:',
  'feed.guide.fcr.goodValue': '1.0-1.5 (lower is better)',
  'feed.guide.fcr.requires': 'Requires:',
  'feed.guide.fcr.requiresValue': 'At least 2 weeks of data',
  'feed.guide.sgr.title': '🌱 Specific growth rate (SGR)',
  'feed.guide.sgr.definition': 'How much the mean weight grows per day, in %',
  'feed.guide.sgr.calculation': 'Mean weight of two samples and the real days between them. SGR = 100 × (e^g − 1), with g = (ln W2 − ln W1) ÷ days',
  'feed.guide.sgr.example': 'Example:',
  'feed.guide.sgr.exampleValue': '2.5 g to 3.0 g in 7 days = 2.64 % per day (2.5 g growing 2.64 % each day reaches 3.0 g)',
  'feed.guide.sgr.classic': 'Tables and papers:',
  'feed.guide.sgr.classicValue': 'many use 100 × g, which reads slightly lower (2.60 in the example). Reportes has it as "SGR classic" for comparison.',
  'feed.guide.biomass.formula': 'Formula:',
  'feed.guide.biomass.formulaValue': '{stockMetric} × Average Weight ÷ 1000',
  'feed.guide.biomass.units': 'Units:',
  'feed.guide.biomass.unitsValue': 'Count × grams = kilograms',
  'feed.guide.biomass.purpose': 'Purpose:',
  'feed.guide.biomass.purposeValue': 'Total weight in production unit',
  'feed.guide.quality.completeLabel': 'Complete (100%):',
  'feed.guide.quality.completeValue': 'Weight, stock, and temperature data',
  'feed.guide.quality.incompleteLabel': 'Incomplete:',
  'feed.guide.quality.incompleteValue': 'Missing critical data for calculations',
  'feed.guide.quality.impactLabel': 'Impact:',
  'feed.guide.quality.impactValue': 'Incomplete data affects FCR calculation accuracy',

  'feed.fcrInterp.title': '💡 FCR Interpretation:',
  'feed.fcrInterp.excellent': 'Excellent efficiency',
  'feed.fcrInterp.good': 'Good performance',
  'feed.fcrInterp.fair': 'Fair, needs improvement',
  'feed.fcrInterp.poor': 'Poor, review practices',

  'feed.controls.fcrDecimals': 'FCR decimals: {count}',
  'feed.controls.biomassDecimals': 'Biomass decimals: {count}',

  'feed.temperatureWarning.criticalCold': '🚨 CRITICAL: Temperature below {min}°C - suspend feeding for {context}. Values are clamped to {clamp}°C for interpolation.',
  'feed.temperatureWarning.tooCold': '❄️ WARNING: Temperature is near the lower threshold for {context}. Monitor dissolved oxygen closely before feeding.',
  'feed.temperatureWarning.tooWarm': '🔥 WARNING: Temperature is approaching the upper tolerance for {context}. Reduce feeding rates and monitor water quality.',
  'feed.temperatureWarning.criticalWarm': '🚨 CRITICAL: Temperature above {max}°C - suspend feeding for {context}. Values are clamped to {clamp}°C for interpolation.',

  // Biomass
  'biomass.title': '📊 Biomass Analytics',

  'biomass.context.facility': 'Facility',
  'biomass.context.productionUnit': 'Production Unit',
  'biomass.context.selectFacility': 'Select a facility...',
  'biomass.context.selectFacilityFirst': 'Select facility first...',
  'biomass.context.selectProductionUnit': 'Select production unit...',
  'biomass.summary.lotRecords': '{count} lot records',

  'biomass.aria.panels': 'Biomass panels',
  'biomass.panel.log': 'Log',
  'biomass.panel.analyze': 'Analyze',

  'biomass.section.logOperations': '🧾 Log operations',
  'biomass.details.sampling.title': '🧾 Sampling and mortality',
  'biomass.details.count.title': '🔢 Count / Estimate',
  'biomass.details.harvest.title': '🧺 Harvest',

  'biomass.readonly.sampling': 'Signed in as viewer. Biomass logs are read-only.',
  'biomass.readonly.count': 'Signed in as viewer. Count/estimate is read-only.',
  'biomass.readonly.harvest': 'Signed in as viewer. Harvest is read-only.',

  'biomass.stockLabel.fish': 'Fish stock',
  'biomass.stockLabel.shrimp': 'PL count',

  'biomass.field.recordedAt': 'Recorded at',
  'biomass.field.lot': 'Lot',
  'biomass.field.stage': 'Stage',
  'biomass.field.avgWeightG': 'Average weight (g)',
  'biomass.field.dailyMortality': 'Daily mortality',
  'biomass.field.notes': 'Notes',
  'biomass.field.method': 'Method',
  'biomass.field.harvestKg': 'Harvest (kg)',
  'biomass.field.harvestCount': 'Harvest (count)',

  'biomass.lot.placeholder.selectUnitFirst': 'Select a production unit first',
  'biomass.lot.placeholder.loadingLots': 'Loading lots…',
  'biomass.lot.placeholder.noLots': 'No lots for this unit (create in Lots)',
  'biomass.lot.placeholder.chooseLot': 'Choose lot',

  'biomass.button.addSamplingRow': 'Add sampling row',
  'biomass.button.addCountRow': 'Add count row',
  'biomass.button.addHarvestRow': 'Add harvest row',
  'biomass.button.refreshLogs': 'Refresh biomass logs',
  'biomass.button.useAsTemplate': 'Use as template',
  'biomass.action.importOperationalCsv': 'Import CSV (operational database)',

  'biomass.status.savedOffline': 'Biomass log saved locally while offline.',
  'biomass.status.savedPending': 'Biomass log saved locally. Server sync is pending.',
  'biomass.status.savedSynced': 'Biomass log synced.',
  'biomass.status.deletedOffline': 'Biomass log deleted locally while offline.',
  'biomass.status.deletedPending': 'Biomass log deleted locally. Server sync is pending.',
  'biomass.status.deletedSynced': 'Biomass log deletion synced.',
  'biomass.status.refreshing': 'Refreshing biomass logs...',
  'biomass.status.refreshComplete': 'Biomass logs refreshed from the server.',
  'biomass.status.refreshFailed': 'Could not refresh biomass logs right now.',
  'biomass.sync.retrySynced': 'Biomass sync is now caught up.',
  'biomass.sync.retryOffline': 'Biomass changes are local only while this device is offline.',
  'biomass.sync.retryPending': 'Biomass changes are still pending sync.',
  'biomass.sync.blocked': '{count} biomass change(s) need attention before they can sync.',
  'biomass.sync.blockedWithReason': '{count} biomass change(s) need attention: {reason}',
  'biomass.sync.retryResolvedMissing': 'Retry skipped {count} missing biomass change(s) and kept syncing the rest.',
  'biomass.sync.retryAction': 'Retry sync',
  'biomass.sync.retrying': 'Retrying sync...',
  'biomass.sync.refreshAction': 'Refresh biomass logs',
  'biomass.sync.attentionBlocked': '{count} biomass change(s) are blocked and need attention.',
  'biomass.sync.attentionBlockedWithReason': '{count} biomass change(s) are blocked: {reason}',
  'biomass.sync.attentionOffline': 'This device is offline. Biomass changes will sync when connection returns.',
  'biomass.sync.attentionPending': 'Biomass changes are queued and still pending sync.',
  'biomass.sync.lastResult': 'Last sync result: {result}',
  'biomass.sync.lastAttempted': 'Last attempted: {time}',

  'biomass.recent.samplingTitle': 'Recent sampling rows',
  'biomass.recent.countTitle': 'Recent count rows',
  'biomass.recent.harvestTitle': 'Recent harvest rows',
  'biomass.recent.noneSampling': 'No sampling rows yet for this unit.',
  'biomass.recent.noneCount': 'No count rows yet for this unit.',
  'biomass.recent.noneHarvest': 'No harvest rows yet for this unit.',
  'biomass.recent.loadMore': 'Load more ({count} older)',
  'biomass.row.unmappedLot': 'Unmapped lot',
    'biomass.row.deletedLot': 'Deleted lot (the record is kept)',
  'biomass.row.avgWeightPrefix': 'Avg weight:',
  'biomass.row.methodPrefix': 'Method:',
  'biomass.row.harvestPrefix': 'Harvest:',
  'biomass.unit.count': 'count',

  'biomass.method.estimate': 'Estimate',
  'biomass.method.exact': 'Exact',

  'biomass.error.chooseLot': 'Please choose a lot.',
  'biomass.error.saveFailed': 'Failed to save. Please try again.',
  'biomass.error.deleteFailed': 'Failed to delete. Please try again.',
  'biomass.error.enterRequired': 'Please enter {field}.',
  'biomass.error.enterWeightOrMortality': 'Please enter average weight or daily mortality.',
  'biomass.error.harvestRequired': 'Enter harvest kg, harvest count, or both.',
  'biomass.error.mustBeNumber': 'Please enter a number for {field}.',
  'biomass.error.mustBePositive': 'Please enter a positive number for {field}.',
  'biomass.error.mustBeWholeNumber': 'Please enter a whole number for {field}.',

  // PR-α3b U5: harvest-driven Lot.stage auto-transition (warning banner +
  // button-flip confirm). See docs/lot-lifecycle-vs-concerns-audit.md §1.1 X8/X9.
  'biomass.harvest.transition.warningBanner': 'Cumulative harvest reached {ratio}% of live biomass. Consider closing the lot.',
  'biomass.harvest.transition.confirmCta': 'Confirm lot closure',
  'biomass.harvest.transition.confirmed': 'Lot closed',

  'biomass.view.current': 'Current Status',
  'biomass.view.historical': 'Historical Trends',
  'biomass.view.charts': '📊 Charts & Graphs',

  'biomass.empty.noData.title': 'No Data Available',
  'biomass.empty.noData.subtitle': 'Please ensure database entries exist for {farm} - {unit}',

  'biomass.cards.totalActiveBiomass.title': '🐟 Total Active Biomass',
  'biomass.cards.totalActiveBiomass.needsSample': '{count} lot(s) without a weight sample: record a sample to calculate their biomass.',
  'biomass.cards.totalActiveBiomass.caption': 'Active lots: {count}',
  'biomass.cards.totalActiveBiomass.activeLabel': 'Active',
  'biomass.cards.totalActiveBiomass.historicalLabel': 'Historical',
  'biomass.cards.totalActiveBiomass.breakdownTitle': 'Per-lot breakdown',
  'biomass.cards.totalStock.title': '🔢 Total {stockLabel}',
  'biomass.cards.totalStock.caption': 'Active count',
  'biomass.cards.avgWeight.title': '⚖️ Average Weight',
  'biomass.cards.avgWeight.caption': 'Weighted average',
  'biomass.cards.survival.title': '💚 Derived Survival',
  'biomass.cards.survival.caption': '{count} active lots with derived survival',
  'biomass.cards.observedMortality.title': '🟥 Observed Mortality',
  'biomass.cards.observedMortality.caption': '{count} active lots with daily mortality recorded',
  'biomass.cards.expectedMortality.title': '🟥 Expected Mortality',
  'biomass.cards.expectedMortality.caption': '{count} active lots matched to water temperature',
  'biomass.cards.projectedBiomass.title': '🧮 Projected Biomass',
  'biomass.cards.projectedBiomass.caption': '{count} active lots projected from the mortality matrix',
  'biomass.projection.emptyHint': 'Add water temperature records and configure the Mortality Matrix to show projections here.',
  'biomass.projection.assumptions.title': 'Projection assumptions',
  'biomass.projection.assumptions.observed': 'Observed mortality is measured from recorded daily mortality and previous known stock.',
  'biomass.projection.assumptions.expected': 'Expected mortality is derived from the mortality matrix using weight and water temperature.',
  'biomass.projection.assumptions.stock': 'Projected stock applies expected mortality day by day across each date gap.',
  'biomass.projection.assumptions.biomass': 'Projected biomass uses the latest valid weight as a fixed-weight assumption.',
  'biomass.projection.scenarios.title': 'Projection scenario split',
  'biomass.projection.scenarios.currentTrendLabel': 'Current-trend projection (observed mortality)',
  'biomass.projection.scenarios.currentTrendCaption': '{count} active lots with observed mortality rate available',
  'biomass.projection.scenarios.expectedLabel': 'Expected-condition projection (matrix mortality)',
  'biomass.projection.scenarios.expectedCaption': '{count} active lots with matrix-based projection',
  'biomass.performance.title': '7-day mortality performance snapshot',
  'biomass.performance.asOf': 'As of: {date}',
  'biomass.performance.activeLots': 'Active lots tracked: {count}',
  'biomass.performance.currentObserved': 'Current observed mortality: {value}',
  'biomass.performance.previousObserved': 'Previous observed mortality: {value}',
  'biomass.performance.expected': 'Current expected mortality: {value}',
  'biomass.performance.gap': 'Observed vs expected gap: {value}',
  'biomass.performance.trend': 'Trend: {value}',
  'biomass.performance.trend.improving': 'Improving',
  'biomass.performance.trend.worsening': 'Worsening',
  'biomass.performance.trend.stable': 'Stable',
  'biomass.performance.trend.insufficient-history': 'Insufficient history',
  'biomass.performance.samples': 'Samples (current/previous): {current}/{previous}',
  'biomass.performance.sourceContract': 'Observed mortality uses linked Mortality Log rows when present; otherwise it falls back to the daily summary row.',
  'biomass.performance.trustSummary': 'Snapshot trust: {value}',
  'biomass.performance.trustSummary.strong': 'Strong',
  'biomass.performance.trustSummary.caution': 'Caution',
  'biomass.performance.trustSummary.weak': 'Weak',
  'biomass.performance.trustReason.current-summary-fallback': 'Current window is summary fallback only.',
  'biomass.performance.trustReason.previous-no-observed': 'Previous window has no observed samples.',
  'biomass.performance.trustReason.previous-stale': 'Previous Mortality Log evidence is stale.',
  'biomass.performance.trustReason.current-no-observed': 'Current window has no observed samples.',
  'biomass.performance.trustReason.both-log-backed-recent': 'Both windows are log-backed and recent.',
  'biomass.performance.trustReason.mixed-evidence': 'Observed evidence mixes Mortality Log and fallback signals.',
  'biomass.performance.sourceMode': 'Observed source mode (current/previous): {currentMode}, {previousMode}',
  'biomass.performance.sourceMode.mortality-log-only': 'Mortality Log only',
  'biomass.performance.sourceMode.mixed': 'Mixed (Mortality Log + summary fallback)',
  'biomass.performance.sourceMode.summary-fallback': 'Summary fallback only',
  'biomass.performance.sourceMode.no-observed': 'No observed samples',
  'biomass.performance.sourceCoverage': 'Mortality Log-backed samples (current/previous): {currentLog}/{currentTotal}, {previousLog}/{previousTotal}',
  'biomass.performance.sourceCoverageDates': 'Latest Mortality Log sample dates (current/previous): {currentDate}, {previousDate}',
  'biomass.performance.sourceRecencyRisk': 'Mortality Log recency risk (current/previous): {currentRisk}, {previousRisk}',
  'biomass.performance.sourceRecencyRisk.fresh': 'Fresh',
  'biomass.performance.sourceRecencyRisk.aging': 'Aging',
  'biomass.performance.sourceRecencyRisk.stale': 'Stale',
  'biomass.performance.sourceRecencyRisk.unknown': 'Unknown',
  'biomass.performance.details.show': 'Show observed-source details',
  'biomass.performance.details.hide': 'Hide observed-source details',
  'biomass.performance.empty': 'Not enough active mortality data yet to compute the 7-day snapshot.',

  'biomass.lotDetails.title': '📋 Lot Details',
  'biomass.lotDetails.metric.biomass': 'Biomass:',
  'biomass.lotDetails.metric.stock': 'Stock:',
  'biomass.lotDetails.metric.avgWeight': 'Avg Weight:',
  'biomass.lotDetails.metric.mortality': 'Observed mortality:',
  'biomass.lotDetails.metric.survival': 'Derived survival:',
  'biomass.lotDetails.metric.density': 'Density:',
  // FCR-in-flight PRD (2026-09-11): same number, same label as the Reports
  // "FCR in flight" column (src/utils/feedConversionRatio.ts) — a second
  // surface, not a fourth FCR metric. See feed.kpi.fcrPlanned for the
  // unrelated (model-predicted) Feed screen FCR this must stay distinct from.
  'biomass.lotDetails.metric.fcrInFlight': 'eFCR in flight:',
  'biomass.lotDetails.metric.fcrInFlightHelp': 'Feed to date ÷ (current biomass − stocked biomass). "--" when the lot\'s first count or first sample is missing.',
  'biomass.lotDetails.metric.expectedMortality': 'Expected mortality:',
  'biomass.lotDetails.metric.projectedStock': 'Projected stock:',
  'biomass.lotDetails.metric.projectedBiomass': 'Projected biomass:',
  'biomass.lotDetails.metric.temperature': 'Temperature:',

  'biomass.charts.title': '📊 Visual Analytics Dashboard',
  'biomass.charts.lead': 'Interactive charts and graphs showing growth trends, stock levels, and performance metrics over time.',
  'biomass.charts.tipLandscape': 'Tip: rotate to landscape for easier reading. You can also scroll charts horizontally.',
  'biomass.charts.avgWeightGrowth.title': '📈 Average Weight Growth Over Time',
  'biomass.charts.stockLevels.title': '📊 {stockLabel} Levels Over Time',
  'biomass.charts.stock.noData': 'No stock data available for chart',
  'biomass.charts.stock.chartTitle': '{stockLabel} by Lot and Date',
  'biomass.charts.stock.series.actual': 'Actual',
  'biomass.charts.stock.series.projected': 'Projected',
  'biomass.charts.stock.barTooltip': '{lot}\nDate: {date}\n{stockLabel}: {stock}',
  'biomass.charts.stock.projectedBarTooltip': '{lot}\nDate: {date}\nProjected {stockLabel}: {stock}',
  'biomass.charts.stock.projectedMissing': 'No projection',
  'biomass.charts.stock.axisTitle': 'Count',

  'biomass.historical.title': '📈 Historical Biomass Data',
  'biomass.historical.column.period': 'Period',
  'biomass.historical.column.lot': 'Lot',
  'biomass.historical.column.biomassKg': 'Biomass (kg)',
  'biomass.historical.column.stock': 'Stock',
  'biomass.historical.column.avgWeightG': 'Avg Weight (g)',
  'biomass.historical.column.temperatureC': 'Temp (C)',
  'biomass.historical.column.survivalPct': 'Derived survival (%)',
  'biomass.historical.column.mortalityPct': 'Observed mortality (%)',
  'biomass.historical.column.expectedMortalityPct': 'Expected mortality (%)',
  'biomass.historical.column.projectedStock': 'Projected stock',
  'biomass.historical.column.projectedBiomassKg': 'Projected biomass (kg)',
  'biomass.historical.column.fcrInFlight': 'eFCR in flight',
  'biomass.historical.column.stage': 'Stage',

  'biomass.guide.title': '📊 Biomass Metrics Guide',
  'biomass.guide.totalBiomass.title': '🐟 Total Biomass',
  'biomass.guide.survival.title': '💚 Derived Survival',
  'biomass.guide.density.title': '📏 Stocking Density',
  'biomass.guide.growth.title': '⚖️ Growth Tracking',

  'biomass.guide.label.formula': 'Formula:',
  'biomass.guide.label.purpose': 'Purpose:',
  'biomass.guide.label.units': 'Units:',
  'biomass.guide.label.example': 'Example:',
  'biomass.guide.label.goodRate': 'Good Rate:',
  'biomass.guide.label.note': 'Note:',
  'biomass.guide.label.optimal': 'Optimal:',
  'biomass.guide.label.averageWeight': 'Average Weight:',
  'biomass.guide.label.monitoring': 'Monitoring:',
  'biomass.guide.label.target': 'Target:',

  'biomass.guide.totalBiomass.formulaValue': '{stockLabel} × Average Weight ÷ 1000',
  'biomass.guide.totalBiomass.purposeValue': 'Total live weight in production unit',
  'biomass.guide.totalBiomass.unitsValue': 'Kilograms (kg)',

  'biomass.guide.survival.formulaValue': '((Previous stock - Daily mortality) ÷ Previous stock) × 100',
  'biomass.guide.survival.exampleValue': '(80 - 15) ÷ 80 = 81.25%',
  'biomass.guide.survival.goodRateValue': 'Closer to 100% day by day',
  'biomass.guide.survival.noteValue': 'Shown only when a row has daily mortality and there is a previous stock record',

  'biomass.guide.density.formulaValue': 'Total Biomass ÷ Volume',
  'biomass.guide.density.unitsValue': 'kg/m³',
  'biomass.guide.density.optimalValue': 'Varies by species and life stage',

  'biomass.guide.growth.averageWeightValue': 'Key growth indicator',
  'biomass.guide.growth.monitoringValue': 'Track weekly increases',
  'biomass.guide.growth.targetValue': 'Consistent growth curves',

  // Dashboard (New)
  'dashboardNew.title': 'Dashboard (new)',
  'dashboardNew.subtitle': 'Pivot tables and charts built from saved reports.',

  'dashboardNew.addWidget.title': 'Add pivot widget',
  'dashboardNew.addWidget.triggerSubtitle': 'Build a pivot table or chart from one of your saved reports.',
  'dashboardNew.addWidget.empty.noReports': 'No saved reports yet. Create one in Reports first.',

  'dashboardNew.form.title.label': 'Title',
  'dashboardNew.form.title.placeholder': 'e.g. Total feed by lot',
  'dashboardNew.form.report.label': 'Report',
  'dashboardNew.form.rows.label': 'Rows (group by)',
  'dashboardNew.form.rows.dateGrouping.label': 'Rows date grouping',
  'dashboardNew.form.columns.label': 'Columns (optional)',
  'dashboardNew.form.columns.dateGrouping.label': 'Columns date grouping',
  'dashboardNew.form.aggregation.label': 'Aggregation',
  'dashboardNew.form.valueField.label': 'Value field',
  'dashboardNew.form.valueField.placeholder.count': '(not used for count)',
  'dashboardNew.form.valueField.placeholder.select': 'Select a field…',
  'dashboardNew.form.showChart': 'Show chart',
  'dashboardNew.form.chartType.label': 'Chart type',
  'dashboardNew.chartType.bar': 'Bar',
  'dashboardNew.chartType.line': 'Line',

  'dashboardNew.option.none': 'None',
  'dashboardNew.option.noneDailyRaw': 'None (daily/raw)',
  'dashboardNew.option.week': 'Week',
  'dashboardNew.option.month': 'Month',
  'dashboardNew.option.quarter': 'Quarter',
  'dashboardNew.option.year': 'Year',

  'dashboardNew.aggregation.count': 'Count',
  'dashboardNew.aggregation.sum': 'Sum',
  'dashboardNew.aggregation.avg': 'Average',
  'dashboardNew.aggregation.min': 'Minimum',
  'dashboardNew.aggregation.max': 'Maximum',
  'dashboardNew.aggregation.last': 'Latest value',
  'dashboardNew.aggregation.sumOfLast': 'Sum of latest per entity',
  'dashboardNew.aggregation.ratio': 'Ratio',
  'dashboardNew.aggregation.weightedAvg': 'Weighted average',
  'dashboardNew.option.day': 'Day',
  'dashboardNew.aggregation.help.count': 'Counts the rows in each group.',
  'dashboardNew.aggregation.help.sum': 'Adds up the value field. Use for flows (feed, mortality), not for levels like stock.',
  'dashboardNew.aggregation.help.avg': 'Plain average of the rows: every row counts the same, regardless of lot size.',
  'dashboardNew.aggregation.help.min': 'The lowest value in the group.',
  'dashboardNew.aggregation.help.max': 'The highest value in the group.',
  'dashboardNew.aggregation.help.last': 'The value on the most recent row in the group (e.g. current weight).',
  'dashboardNew.aggregation.help.sumOfLast': 'For levels like stock or biomass: takes each lot’s latest value in the group and adds them up. Gives the correct total.',
  'dashboardNew.aggregation.help.ratio': 'Sum of value ÷ sum of denominator (e.g. deaths ÷ stock × 100 = mortality %). Weights each lot by its size.',
  'dashboardNew.aggregation.help.weightedAvg': 'Average weighted by another field (e.g. average weight weighted by stock).',
  'dashboardNew.form.entityField.label': 'Sum the latest value of each',
  'dashboardNew.form.entityField.hint': 'Usually "Lot".',
  'dashboardNew.form.orderField.label': 'Most recent by',
  'dashboardNew.form.orderField.auto': 'Automatic (row date)',
  'dashboardNew.form.orderField.hint': 'Which date decides the most recent row.',
  'dashboardNew.form.denominatorField.label': 'Divide by (denominator)',
  'dashboardNew.form.ratioMultiplier.label': 'Express as',
  'dashboardNew.form.ratioMultiplier.none': 'Ratio (× 1)',
  'dashboardNew.form.ratioMultiplier.percent': 'Percent (× 100)',
  'dashboardNew.form.ratioMultiplier.perMille': 'Per mille (× 1000)',
  'dashboardNew.form.weightField.label': 'Weight by',
  'dashboardNew.form.weightField.hint': 'Usually "Fish stock (derived)".',
  'dashboardNew.form.chartSeries.label': 'Chart series',
  'dashboardNew.form.chartSeries.total': 'One series (row total)',
  'dashboardNew.form.chartSeries.byColumn': 'One series per column (e.g. one line per lot)',
  'dashboardNew.chart.titleBySeries': 'Chart by {field}',
  'dashboardNew.chart.truncatedSeries': 'Showing {shownSeries} of {totalSeries} series and {shown} of {total} groups. The table below has everything.',

  'dashboardNew.button.addWidget': 'Add widget',
  'dashboardNew.button.updateWidget': 'Update widget',
  'dashboardNew.button.refresh': 'Refresh',
  'dashboardNew.button.pinToHome': 'Pin to Home',
  'dashboardNew.button.unpinFromHome': 'Unpin from Home',

  'dashboardNew.help.datasetAllowlist': 'Data comes from the dataset allowlist ({count} datasets) and runs offline-first.',

  'dashboardNew.widgets.title': 'Widgets',
  'dashboardNew.widgets.empty': 'No widgets yet. Add a pivot widget above.',
  'dashboardNew.widgets.dragHandleHint': 'Drag to move, or drag the corner to resize',
  'dashboardNew.widgets.missingReport': 'Missing report',
  'dashboardNew.widgets.rowsPrefix': 'Rows',
  'dashboardNew.widgets.colsPrefix': 'Cols',
  'dashboardNew.widgets.missingReportWarning': 'This widget references a report that no longer exists.',

  'dashboardNew.status.loadingReportRows': 'Loading report rows…',
  'dashboardNew.status.showingUpTo': 'Showing up to {rowCount} row groups and {colCount} column groups.',

  'dashboardNew.error.unknownDataset': 'Unknown dataset: {datasetKey}',
  'dashboardNew.error.runFailed': 'Run failed: {message}',
  'dashboardNew.error.pivotFailed': 'Pivot failed: {message}',

  'dashboardNew.chart.title': 'Chart (top groups)',
  'dashboardNew.chart.truncated': 'Showing {shown} of {total} groups in the chart. The table below has the full list.',
  'dashboardNew.chart.ariaLabel': 'Pivot chart',

  'dashboardNew.pivot.total': 'Total',
  'dashboardNew.pivot.grandTotal': 'Grand total',

  // Home (Dashboard)
  'home.title': 'Home',
  'home.subtitle': 'Welcome to your management app, friend.',
  'home.error.dataRefreshFailed': 'Data refresh failed',

  'home.reportPdf.error.unableToGenerate': 'Unable to generate report PDF',
  'home.reportPdf.error.generateReportFailed': 'Generate report failed: {message}',

  'home.debug.na': 'n/a',
  'home.debug.week.current': 'Current week',
  'home.debug.week.previous': 'Previous week',
  'home.debug.label.date': 'Date',
  'home.debug.gap.inline': ', gap {days}d',
  'home.debug.gap.parens': ' (gap {days}d)',

  'home.debug.fishStock.unavailable': 'Debug information not available',
  'home.debug.fishStock.title': '{emoji} {metricLabel} Debug Summary',
  'home.debug.fishStock.section.current': '📊 Current Period ({label}):',
  'home.debug.fishStock.section.compare': '📈 Comparison Period ({label}):',
  'home.debug.fishStock.section.trend': '🔄 Trend Calculation:',
  'home.debug.fishStock.section.activeEntries': '📋 Active Entries This Week:',
  'home.debug.fishStock.section.historicalEntries': '📋 Matched Historical Entries:',
  'home.debug.fishStock.line.totalActiveStock': '• Total Active Stock: {value} {unit}',
  'home.debug.fishStock.line.activeLots': '• Active Lots: {count}',
  'home.debug.fishStock.line.matchedHistoricalStock': '• Matched Historical Stock: {value} {unit}',
  'home.debug.fishStock.line.matchedLots': '• Matched Lots: {count}',
  'home.debug.fishStock.line.change': '• Change: {value} {unit}',
  'home.debug.fishStock.line.percentage': '• Percentage: {value}%',
  'home.debug.fishStock.line.formula': '• Formula: (({current} - {lastMonth}) / {denominator}) × 100',
  'home.debug.fishStock.entry.current': '{index}. {facilityLabel}: {facility} | {lotLabel}: {lot} | {metricLabel}: {metric}',
  'home.debug.fishStock.entry.historical': '{index}. {facilityLabel}: {facility} | {lotLabel}: {lot} | {metricLabel}: {metric} | {dateLabel}: {date}',
  'home.debug.fishStock.noneActive': 'No active entries',
  'home.debug.fishStock.noneMatches': 'No matches found',

  'home.debug.feed.title': '🍽️ Active Daily Feed Consumption',
  'home.debug.feed.noActiveRows': 'No active rows found for the current week.',
  'home.debug.feed.summaryUnavailable': 'Debug summary unavailable.',
  'home.debug.comparison': '📆 Comparison: {current} vs {previous}',
  'home.debug.feed.totals': '🔢 Totals: {currentKg} kg vs {previousKg} kg',
  'home.debug.feed.delta': 'Δ Change: {deltaKg} kg ({percent})',
  'home.debug.matchedLots': '✅ Matched lots: {matched}/{total}',
  'home.debug.feed.missingPrevSuffix': ' (Missing {count} previous-week lots)',
  'home.debug.feed.noPreviousWeekData': 'no previous-week data',
  'home.debug.feed.row.matched': '• {farm} · {lotLabel} {lot}: {currentKg} kg → {previousKg} kg (Δ {deltaKg} kg, {percent})',
  'home.debug.feed.row.unmatched': '• {farm} · {lotLabel} {lot}: {currentKg} kg ({noPreviousWeekData})',
  'home.debug.moreLots': '• … and {count} more lots',

  'home.debug.actualFeed.title': '🍽️ Actual Daily Feed (Events)',
  'home.debug.actualFeed.selectContext': 'Select a facility and production unit to see Actual feed totals.',
  'home.debug.actualFeed.noEvents': 'No FeedLog events loaded for the current context.',
  'home.debug.actualFeed.weeklyTotals': '🔢 Weekly totals: {currentKg} kg ({currentEvents} events) vs {previousKg} kg ({previousEvents} events)',
  'home.debug.actualFeed.dailyAvg': '📅 Daily avg: {kgPerDay} kg/day',
  'home.debug.actualFeed.adherence': '🎯 Adherence vs Recommended: {adherence}%',
  'home.debug.actualFeed.adherenceNa': '🎯 Adherence vs Recommended: {na} (no recommended baseline)',

  'home.debug.growth.title': '📈 Growth Rate (Daily Gain)',
  'home.debug.growth.noActiveLots': 'No active lots matched for the current period.',
  'home.debug.growth.insufficientHistory': 'Insufficient historical data to compute growth trend.',
  'home.debug.growth.currentAverage': '📊 Current average: {value}',
  'home.debug.growth.specificGrowthRate': '• Specific growth rate: {value}',
  'home.debug.growth.baselineAverage': '📉 Baseline average: {value}',
  'home.debug.growth.baselineSgr': '• Baseline SGR: {value}',
  'home.debug.growth.deltaChange': 'Δ Change: {value}',
  'home.debug.growth.relativeVsBaseline': '   ({value} vs baseline)',
  'home.debug.growth.baselineUnavailable': '📉 Baseline average: {na} (insufficient historical matches)',
  'home.debug.growth.deltaNeutral': 'Δ Change: 0.00 g/day (trend neutral)',
  'home.debug.gapWarningTitle': '⚠️ Lots with >7 day gap ({count}):',
  'home.debug.skippedTitle': '🚫 Skipped (>21 day gap) ({count}):',
  'home.debug.unmatchedLotsTitle': '📝 Unmatched lots ({count}):',
  'home.debug.matchedDetailTitle': '🔍 Matched lots detail:',
  'home.debug.noMatchedLotsContributed': '🔍 No matched lots contributed to this metric.',
  'home.debug.detail.current': '  - Current: {value}',
  'home.debug.detail.previous': '  - Previous: {value}',
  'home.debug.detail.baseline': '  - Baseline: {value}',
  'home.debug.detail.dailyGain': '  - Daily gain: {value}',
  'home.debug.detail.sgr': '  - SGR: {value}',
  'home.debug.detail.previousGain': '  - Previous gain: {value}',
  'home.debug.detail.previousSgr': '  - Previous SGR: {value}',
  'home.debug.detail.note': '  - Note: {note}',

  'home.debug.mortality.title': '🪦 Weekly Mortality Rate',
  'home.debug.mortality.noActiveLots': 'No active lots with {stockMetricLabel} recorded this week.',
  'home.debug.mortality.insufficientHistory': 'Insufficient historical data available for the current week.',
  'home.debug.mortality.currentRate': '📊 Current rate: {rate}',
  'home.debug.mortality.mortalityLine': '• Mortality: {mortality} {unit} from {baseline} baseline',
  'home.debug.mortality.previousRate': '📉 Previous rate: {rate}',
  'home.debug.mortality.deltaPts': 'Δ Change: {pts} pts',
  'home.debug.mortality.previousRateNa': '📉 Previous rate: {na} (insufficient baseline)',
  'home.debug.mortality.deltaNeutral': 'Δ Change: 0.00 pts (trend neutral)',
  'home.debug.mortality.detail.mortality': '  - Mortality: {value}',
  'home.debug.mortality.detail.historical': '  - Historical: {value}',

  'home.water.tooltip.rollup': '7-day average (latest reading per lot). Matched {matched}/{total} lots.',
  'home.water.tooltip.trend': 'Trend vs previous 7 days: {trendText}.',
  'home.water.tooltip.noPrevBaseline': 'No previous 7-day baseline.',
  'home.water.tooltip.none.temp': 'No water temperature readings found for the current context in the last 7 days.',
  'home.water.tooltip.none.ph': 'No pH readings found for the current context in the last 7 days.',
  'home.water.tooltip.none.do': 'No dissolved oxygen readings found for the current context in the last 7 days.',
  'home.water.tooltip.none.salinity': 'No salinity readings found for the current context in the last 7 days.',

  'home.context.facility': 'Facility',
  'home.context.productionUnit': 'Production Unit',
  'home.context.lot': 'Lot',

  'home.selector.facility.label': 'Facility',
  'home.selector.facility.placeholder.none': 'No facilities available',
  'home.selector.facility.placeholder.select': 'Select a facility...',
  'home.selector.facility.unnamed': 'Unnamed facility',

  'home.selector.productionUnit.label': 'Production Unit',
  'home.selector.productionUnit.all': 'All units',
  'home.selector.productionUnit.placeholder.selectFacilityFirst': 'Select facility first...',
  'home.selector.productionUnit.placeholder.noneForFacility': 'No units for selected facility',
  'home.selector.productionUnit.placeholder.select': 'Select production unit...',
  'home.selector.productionUnit.unnamed': 'Unnamed unit',

  'home.selector.lot.label': 'Lot',
  'home.selector.lot.placeholder.selectUnitFirst': 'Select production unit first...',
  'home.selector.lot.placeholder.noneForUnit': 'No lots for selected unit',
  'home.selector.lot.placeholder.select': 'Select lot...',

  'home.stockMetric.shrimp': 'PL Count',
  'home.stockMetric.fish': 'Fish Stock',
  'home.stockUnit.shrimp': 'PL',
  'home.stockUnit.fish': 'fish',
  'home.stockCountLabel.shrimp': 'PL Count',
  'home.stockCountLabel.fish': 'Fish Count',

  'home.alert.title': '⚠️ System Alert',
  'home.alert.viewTasks': 'View tasks',
  'home.alert.error.unableToLoad': 'Unable to load alert tasks right now.',
  'home.alert.empty.noAlerts': 'No alert tasks. Add the “alert” tag to a task to surface it here.',
  'home.alert.badge.alertTagged': 'Alert-tagged',
  'home.alert.badge.overdue': 'Overdue',
  'home.alert.badge.dueSoon': 'Due soon',
  'home.alert.dueAt': 'Due {date}',
  'home.alert.noDueDate': 'No due date',

  'home.kpi.sectionTitle': '📈 Key Performance Indicators',
  'home.kpi.totalActiveStock.title': 'Total Active {stockMetricLabel}',
  'home.kpi.avgWaterTemp.title': 'Average Water Temperature',
  'home.kpi.dailyFeed.title': 'Active Daily Feed Consumption',
  'home.kpi.actualDailyFeed.title': 'Actual Daily Feed (Events)',
  'home.kpi.weeklyMortality.title': 'Weekly Mortality Rate',
  'home.kpi.weeklySurvival.title': 'Weekly Survival Rate',
  'home.kpi.mortalitySnapshot.title': '7-day Mortality Snapshot',
  'home.kpi.mortalitySnapshot.empty': 'Not enough active mortality history yet to compute the 7-day snapshot.',
  'home.kpi.mortalitySnapshot.tooltip.asOf': 'As of: {date}',
  'home.kpi.mortalitySnapshot.tooltip.activeLots': 'Active lots tracked: {count}',
  'home.kpi.mortalitySnapshot.tooltip.currentObserved': 'Current observed mortality: {value}',
  'home.kpi.mortalitySnapshot.tooltip.previousObserved': 'Previous observed mortality: {value}',
  'home.kpi.mortalitySnapshot.tooltip.samples': 'Samples (current/previous): {current}/{previous}',
  'home.kpi.mortalitySnapshot.tooltip.sourceContract': 'Observed mortality uses linked Mortality Log rows when present; otherwise it falls back to the daily summary row.',
  'home.kpi.mortalitySnapshot.tooltip.trustSummary': 'Snapshot trust: {value}',
  'home.kpi.mortalitySnapshot.tooltip.trustSummary.strong': 'Strong',
  'home.kpi.mortalitySnapshot.tooltip.trustSummary.caution': 'Caution',
  'home.kpi.mortalitySnapshot.tooltip.trustSummary.weak': 'Weak',
  'home.kpi.mortalitySnapshot.tooltip.trustReason.current-summary-fallback': 'Current window is summary fallback only.',
  'home.kpi.mortalitySnapshot.tooltip.trustReason.previous-no-observed': 'Previous window has no observed samples.',
  'home.kpi.mortalitySnapshot.tooltip.trustReason.previous-stale': 'Previous Mortality Log evidence is stale.',
  'home.kpi.mortalitySnapshot.tooltip.trustReason.current-no-observed': 'Current window has no observed samples.',
  'home.kpi.mortalitySnapshot.tooltip.trustReason.both-log-backed-recent': 'Both windows are log-backed and recent.',
  'home.kpi.mortalitySnapshot.tooltip.trustReason.mixed-evidence': 'Observed evidence mixes Mortality Log and fallback signals.',
  'home.kpi.mortalitySnapshot.tooltip.sourceMode': 'Observed source mode (current/previous): {currentMode}, {previousMode}',
  'home.kpi.mortalitySnapshot.tooltip.sourceMode.mortality-log-only': 'Mortality Log only',
  'home.kpi.mortalitySnapshot.tooltip.sourceMode.mixed': 'Mixed (Mortality Log + summary fallback)',
  'home.kpi.mortalitySnapshot.tooltip.sourceMode.summary-fallback': 'Summary fallback only',
  'home.kpi.mortalitySnapshot.tooltip.sourceMode.no-observed': 'No observed samples',
  'home.kpi.mortalitySnapshot.tooltip.sourceCoverage': 'Mortality Log-backed samples (current/previous): {currentLog}/{currentTotal}, {previousLog}/{previousTotal}',
  'home.kpi.mortalitySnapshot.tooltip.sourceCoverageDates': 'Latest Mortality Log sample dates (current/previous): {currentDate}, {previousDate}',
  'home.kpi.mortalitySnapshot.tooltip.sourceRecencyRisk': 'Mortality Log recency risk (current/previous): {currentRisk}, {previousRisk}',
  'home.kpi.mortalitySnapshot.tooltip.sourceRecencyRisk.fresh': 'Fresh',
  'home.kpi.mortalitySnapshot.tooltip.sourceRecencyRisk.aging': 'Aging',
  'home.kpi.mortalitySnapshot.tooltip.sourceRecencyRisk.stale': 'Stale',
  'home.kpi.mortalitySnapshot.tooltip.sourceRecencyRisk.unknown': 'Unknown',
  'home.kpi.mortalitySnapshot.tooltip.trend': 'Trend: {value}',
  'home.kpi.mortalitySnapshot.tooltip.trend.improving': 'Improving',
  'home.kpi.mortalitySnapshot.tooltip.trend.worsening': 'Worsening',
  'home.kpi.mortalitySnapshot.tooltip.trend.stable': 'Stable',
  'home.kpi.mortalitySnapshot.tooltip.trend.insufficient-history': 'Insufficient history',
  'home.kpi.weeklySurvival.tooltip': 'Computed as 100% - Weekly Mortality Rate.',
  'home.kpi.growthRate.title': 'Growth Rate',
  'home.kpi.activeTasks.title': 'Active Tasks',

  'home.kpi.trend.vsLastMonth': 'vs last month',
  'home.kpi.trend.vsPrevious7Days': 'vs previous 7 days',
  'home.kpi.trend.vsLastWeek': 'vs last week',
  'home.kpi.trend.vsPriorBaseline': 'vs prior baseline',

  'home.kpi.actualDailyFeed.detail.events': '{events} events',
  'home.kpi.actualDailyFeed.detail.eventsAdherence': '{events} events · {adherence}% adherence',

  'home.kpi.activeTasks.tooltip.loading': 'Loading tasks...',
  'home.kpi.activeTasks.tooltip.failed': 'Failed to load tasks: {error}',
  'home.kpi.activeTasks.tooltip.summary': 'Active tasks for current context (facility/unit/lot). Includes tasks with no context tags. Updated {date}.',


  'home.pinnedWidgets.sectionTitle': '📌 Featured Reports & Charts',
  'home.pinnedWidgets.viewFullPanel': 'View full Panel',
  'home.pinnedWidgets.empty': 'No widgets pinned yet. Use "Add widget" or pin one from Panel.',
  'home.pinnedWidgets.addWidget': 'Add widget',
  'home.pinnedWidgets.addWidget.empty': 'You\'ve already added every available Panel widget. Create a new one in Panel to add it here.',
  'home.pinnedWidgets.addWidget.add': 'Add',
  'home.pinnedWidgets.remove': 'Remove',

  'home.water.sectionTitle': '💧 Water Quality Monitoring',
  'home.water.ph.title': 'pH Level',
  'home.water.do.title': 'Dissolved Oxygen',
  'home.water.salinity.title': 'Salinity',
  'home.water.temp.title': 'Temperature',

  'home.quickActions.sectionTitle': '⚡ Quick Actions',
  'home.quickActions.scheduleFeeding': 'Schedule Feeding',
  'home.quickActions.recordWaterTest': 'Record Water Test',
  'home.quickActions.setTask': 'Set Task',
  'home.quickActions.backupData': 'Backup Data',
  'home.quickActions.generateReport': 'Generate Report',

  'home.activity.sectionTitle': '🕒 Recent Activity',
  'home.activity.error.unableToLoad': 'Unable to load recent activity.',
  'home.activity.empty.noActivity': 'No activity yet. Updates and sync-queued actions will appear here.',

  // Reports
  'reports.title': 'Reports',
  'reports.subtitle': 'Build and save dynamic tables (private or team).',

  'reports.create.title': 'Create report',
  'reports.field.name': 'Name',
  'reports.field.name.placeholder': 'e.g. Weekly mortality Potgapampa',
  'reports.field.dataset': 'Report type',
  'reports.field.dataset.placeholder': 'Choose what you want to see',
  'reports.field.visibility': 'Visibility',
  'reports.visibility.private': 'Private (only me)',
  'reports.visibility.team': 'Team (shared)',

  'reports.columns.title': 'Columns',
  'reports.columns.noneAvailable': 'No columns available for this dataset.',

  'reports.fx.sourceTitle': 'Source money',
  'reports.fx.sourceBody': 'Source money stays authoritative. FX conversion is additive only and never rewrites the original money columns.',
  'reports.fx.convertedTitle': 'Converted values',
  'reports.fx.subtitle': 'Turn FX on only when you need a converted view. If it is off, the report runs on source money only.',
  'reports.fx.enable': 'Add FX-converted values',
  'reports.fx.targetCurrency': 'Target currency',
  'reports.fx.asOf': 'As of',
  'reports.fx.off': 'Conversion off: the report keeps source money only.',
  'reports.fx.notAvailable': 'This dataset does not support FX conversion.',
  'reports.fx.convertedMoney': 'Converted values are additive only and show explicit provenance in the report rows.',
  'reports.fx.diagnostics': 'Missing rate stays null with reason, and mixed-currency lots remain diagnosable before conversion.',
  'reports.fx.summaryOn': 'FX on · {currency} as of {asOf}',
  'reports.fx.summaryOff': 'FX off',
  'reports.fx.previewTitle': 'FX preview',

  'reports.scope.title': 'Choose scope (saved with report)',
  'reports.scope.subtitle': 'This scope will be applied when the report runs (including in dashboards).',
  'reports.scope.from': 'From',
  'reports.scope.to': 'To',
  // v2.2 T3-B-06f: rolling `toDate = 'today'` sentinel. Closes v2.2
  // finding #6. Resolved at fetch time in the farm's local timezone.
  'reports.scope.toDateMode.aria': 'How to resolve the To date',
  'reports.scope.toDateMode.fixed': 'Fixed date',
  'reports.scope.toDateMode.today': 'Today (rolling)',
  'reports.scope.toDateMode.todayHint': 'Resolves to today’s date on each Run — no need to edit.',
  'reports.scope.facilities': 'Facilities',
  'reports.scope.productionUnits': 'Production units',
  'reports.scope.lots': 'Lots',
  'reports.choice.clear': 'Clear',
  'reports.choice.noneAvailable': 'No options available.',

  'reports.create.trigger.subtitle': 'Filter, choose columns, and save a new report.',
  'reports.button.newReport': 'New report',
  'reports.edit.title': 'Edit report',
  'reports.button.duplicate': 'Duplicate',
  'reports.duplicate.namePrefix': 'Copy of {name}',
  'reports.button.sendToPanel': 'Send to Panel',
  'reports.button.previewDraft': 'Preview',

  'reports.button.save': 'Save report',
  'reports.saved.title': 'Saved reports',
  'reports.saved.empty': 'No reports yet. Create one above.',
  'reports.button.run': 'Run',
  // v2.2 T3-B-08: Reports v1 Edit button. Loading a saved report into
  // the create form + flipping Save into upsert-in-place mode. Closes
  // v2.2 finding #5.
  'reports.button.edit': 'Edit',
  'reports.button.cancelEdit': 'Cancel edit',
  'reports.form.editingHint': 'Editing “{name}” — Save will overwrite this report.',
  'reports.button.publishInternal': 'Publish internally',
  'reports.button.exportCsv': 'Export CSV',

  // Renamed from "Preview" — this section shows the actual executed,
  // scope-applied report result, not a preview of it. "Preview" is now
  // reserved for the drawer's own pre-save draft check.
  'reports.results.title': 'Results',
  'reports.preview.selectPrompt': 'Select a report to preview.',
  'reports.preview.scopeApplied': 'Scope applied · Rows: {total} total · {scoped} in scope',
  'reports.deletedLot.leftOut': 'Records of {count} deleted lots are not included (they are kept; Lotes → Lotes eliminados → Restaurar brings them back).',
  'reports.deletedLot.leftOutOne': 'Records of 1 deleted lot are not included (they are kept; Lotes → Lotes eliminados → Restaurar brings them back).',
  'reports.preview.loading': 'Loading…',
  'reports.preview.runFailed': 'Run failed: {error}',
  'reports.preview.clickRun': 'Click “Run” to load rows.',
  'reports.publish.subtitle': 'Publish the current scope-applied preview as one immutable Collaboration snapshot using this report visibility.',
  'reports.publish.noneYet': 'No internal snapshot published yet for this report.',
  'reports.publish.current': 'Current internal snapshot · Version {version}',
  'reports.publish.publishedAt': 'Published at {date}',
  'reports.publish.success': 'Published internally as version {version}.',
  'reports.publish.error.noPreview': 'Run the report and keep at least one row in scope before publishing internally.',
  'reports.publish.error.failed': 'Failed to publish internally: {error}',

  'reports.error.unknownDataset': 'Unknown dataset: {datasetKey}',
  'reports.cell.unserializable': '[unserializable]',
  // Appended to a lot label when the row's farm no longer exists in the
  // operator's current farm roster — the underlying history is real, but
  // it's not a lot they can find on the Lots screen today.
  'reports.cell.removedFarmSuffix': '(removed farm)',
  'reports.cell.derivedTooltip': 'Estimated (calculated) value, not a direct reading.',
  // 2026-09-12 pack: split the single ambiguous seeded report into two
  // unambiguously-named ones (harvest vs in-flight FCR). The old key
  // stays as the exact-match marker `seedDefaultReportsIfEmpty` uses to
  // migrate an existing tenant's already-seeded report without
  // clobbering a name the operator chose themselves.
  'reports.seed.costPerKg.name': 'Cost per kg — overview',
  'reports.seed.costPerKgHarvest.name': 'Cost per kg — at harvest',
  'reports.seed.costPerKgInFlight.name': 'Cost per kg — in flight',
  'dashboardNew.seed.fcrHarvestByLot.title': 'eFCR at harvest, by lot',
  'dashboardNew.seed.fcrInFlightByLot.title': 'eFCR in flight, by lot',

  // Reports/Panel pack item (c): weekly SGR / FCR (planned) / SFR by lot.
  'reports.seed.lotGrowthPerformance.name': 'Lot growth — SGR, FCR, SFR (weekly)',
  'dashboardNew.seed.lotGrowthSgr.title': 'Weekly SGR, by lot',
  'dashboardNew.seed.lotGrowthFcr.title': 'Weekly FCR (planned), by lot',
  'dashboardNew.seed.lotGrowthSfr.title': 'Weekly SFR, by lot',

  // v2.2 findings.md #45: core monitoring widgets (mortality, stock,
  // average weight) over the production Datatable report.
  'reports.seed.coreMonitoring.name': 'Core monitoring — mortality, stock, weight (daily)',
  'dashboardNew.seed.coreMortalityRate.title': 'Average daily mortality (%), by lot',
  'dashboardNew.seed.coreStock.title': 'Total stock (weekly)',
  'dashboardNew.seed.coreAvgWeight.title': 'Weekly average weight (g), by lot',

  // Lots
  'lots.title': 'Manage lots',
  'lots.subtitle':
    'Create and maintain production lots for each facility and unit. These identifiers power tasks, water logs, and production records.',
  'lots.instructions.refreshHint': 'Use Refresh Lots if changes from another device do not appear yet.',
  'lots.readonly.banner': 'Read-only mode ({role} token). Editing lots requires an editor token.',
  'lots.workspace.label': 'Workspace',
  'lots.workspace.operations': 'Operations',
  'lots.workspace.operations.subtitle': 'Keep creation, transfers, traceability, and lot maintenance on the operational surface.',
  'lots.workspace.story': 'Story workspace',
  'lots.workspace.story.subtitle': 'Review previews, drafts, promotion, and receiver delivery checks without crowding the operational tools.',
  'lots.workspace.story.activeSelection': 'Current lot story: {lot}',
  'lots.workspace.story.empty': 'Open a lot story from the library, or return to Operations and launch one from a lot row.',

  'lots.filter.facilityLabel': 'Facility',
  'lots.filter.productionUnitLabel': 'Production unit',
  'lots.filter.processingSiteLabel': 'Processing site',
  'lots.filter.loadingContext': 'Loading saved facility and sub-location context...',
  'lots.filter.allFacilities': 'All facilities',
  'lots.filter.allUnits': 'All units',
  'lots.filter.allUnitsInSelectedFacility': 'All units in selected facility',
  'lots.filter.allProcessingSites': 'All processing sites',
  'lots.filter.allProcessingSitesInSelectedFacility': 'All processing sites in selected facility',

  'lots.processingSiteSummary.title': 'Processing site snapshot',
  'lots.processingSiteSummary.subtitle': 'Current lots visible in {site}.',
  'lots.processingSiteSummary.totalLots': 'Current lots',
  'lots.processingSiteSummary.activeLots': 'Active lots',
  'lots.processingSiteSummary.totalBiomass': 'Total biomass (kg)',
  'lots.processingSiteSummary.species': 'Species in view',
  'lots.processingSiteSummary.noSpecies': 'No species recorded',
  'lots.processingSiteSummary.latestActivity': 'Latest visible activity',
  'lots.processingSiteSummary.latestActivityValue': '{lot} updated {timestamp}',
  'lots.processingSiteSummary.noActivity': 'No visible activity timestamp yet',
  'lots.processingSiteSummary.recentLots': 'Recent lots in view',
  'lots.processingSiteSummary.noRecentLots': 'No recent visible lots yet',
  'lots.processingSiteSummary.recentLotMeta': 'Status: {stage} · Updated {timestamp}',
  'lots.processingSiteSummary.recentLotSource': 'Source lot: {sourceLot}',
  'lots.processingSiteSummary.recentLotProvenance': '{eventType} from {origin}',
  'lots.processingSiteSummary.recentLotProvenanceWithTimestamp': '{eventType} from {origin} on {timestamp}',
  'lots.processingSiteSummary.recentLotPreviousProvenanceWithTimestamp': 'Previous recorded origin: {eventType} from {origin} on {timestamp}',
  'lots.processingSiteSummary.siteActivity': 'Recent site activity',
  'lots.processingSiteSummary.noSiteActivity': 'No recent move or transfer activity into this site yet',
  'lots.processingSiteSummary.siteActivityTransfers': 'Transfers: {count}',
  'lots.processingSiteSummary.siteActivityMoves': 'Moves: {count}',
  'lots.processingSiteSummary.siteActivityFilterAll': 'All',
  'lots.processingSiteSummary.siteActivityFilterTransfers': 'Transfers only',
  'lots.processingSiteSummary.siteActivityFilterMoves': 'Moves only',
  'lots.processingSiteSummary.originSummaryTitle': 'Top recent origins in this filter',
  'lots.processingSiteSummary.originSummaryEntry': '{origin} · {total} entries ({transfers} transfers, {moves} moves) · latest {timestamp}',
  'lots.processingSiteSummary.originRecencyBadgeLast24h': 'Last 24h',
  'lots.processingSiteSummary.originRecencyBadgeLast7d': 'Last 7d',
  'lots.processingSiteSummary.originRecencyBadgeStale': 'Stale',
  'lots.processingSiteSummary.originRecencyBadgeUnknown': 'Unknown',
  'lots.processingSiteSummary.originRecencyBreakdown': 'Origin freshness mix: Last 24h {last24h} · Last 7d {last7d} · Stale {stale} · Unknown {unknown}',
  'lots.processingSiteSummary.recencyWindowTitle': 'Recent activity window',
  'lots.processingSiteSummary.recencyWindowValue': 'Last 24h: {last24h} · Last 7d: {last7d}',
  'lots.processingSiteSummary.recencyWindowStaleWarning': 'No arrivals recorded in the last 7 days for this filter.',
  'lots.processingSiteSummary.unknownOrigin': 'Unknown origin',
  'lots.processingSiteSummary.latestArrival': 'Latest arrival in current filter',
  'lots.processingSiteSummary.latestArrivalEntry': '{lot}: {eventType} from {origin} on {timestamp}',
  'lots.processingSiteSummary.noSiteActivityForFilter': 'No recent entries match this activity filter',
  'lots.processingSiteSummary.siteActivityEntry': '{lot}: {eventType} from {origin} on {timestamp}',

  'app.localWriteFailed': 'This device could not save a change locally (storage full or blocked). Keep this page open while online so it syncs, and tell your administrator.',
  'lots.stockUnits.title': 'Stock into several units',
  'lots.stockUnits.subtitle': 'One batch arrives and goes into several tanks: one lot per unit, same name, linked to the batch.',
  'lots.stockUnits.intro': 'The batch is recorded once at facility level and split into one lot per unit. Every unit keeps the batch name; reports can group them with "Lot (origin)".',
  'lots.stockUnits.name': 'Batch / lot name',
  'lots.stockUnits.namePlaceholder': 'e.g. Lot 1',
  'lots.stockUnits.nameHint': 'The name only has to be unique within each unit.',
  'lots.stockUnits.date': 'Stocking date',
  'lots.stockUnits.species': 'Species',
  'lots.stockUnits.totalFish': 'Fish that arrived',
  'lots.stockUnits.averageWeight': 'Average arrival weight (g)',
  'lots.stockUnits.seedSupplier': 'Seed supplier (hatchery / origin)',
  'lots.stockUnits.strain': 'Strain / genetic line',
  'lots.stockUnits.units': 'Units and fish per unit',
  'lots.stockUnits.noUnits': 'This facility has no production units yet.',
  'lots.stockUnits.fishPerUnit': 'Fish',
  'lots.stockUnits.fishPerUnitFor': 'Fish for {unit}',
  'lots.stockUnits.nameTakenHere': 'already has a lot with this name',
  'lots.stockUnits.spreadEvenly': 'Split evenly',
  'lots.stockUnits.assignedOfTotal': '{assigned} of {total} fish assigned',
  'lots.stockUnits.assigned': '{assigned} fish in {units} units',
  'lots.stockUnits.notes': 'Notes',
  'lots.stockUnits.submit': 'Stock',
  'lots.stockUnits.success': '"{name}" stocked into {count} units.',
  'lots.stockUnits.error.name': 'Enter the batch name.',
  'lots.stockUnits.error.date': 'Enter the stocking date.',
  'lots.stockUnits.error.units': 'Choose at least two units (for one unit, create the lot normally).',
  'lots.stockUnits.error.nameTaken': 'A chosen unit already has a lot with this name. Pick another name or unit.',
  'lots.stockUnits.error.countsAllOrNone': 'Give the fish count for every chosen unit, or leave them all empty.',
  'lots.stockUnits.error.countsNeeded': 'You entered the fish that arrived: split them across the units ("Split evenly" helps).',
  'lots.stockUnits.error.countsMismatch': 'The units add up to {assigned} fish but {total} arrived.',
  'lots.stockUnits.error.weight': 'The average weight must be a number above 0.',
  'lots.stockUnits.error.offline': 'You are offline. Stocking into several units needs a connection.',
  'lots.stockUnits.error.generic': 'Could not stock the batch. Nothing was saved — try again.',
  'lots.lineage.title': 'Split / Merge traceability (MVP)',
  'lots.lineage.subtitle': 'Records lineage by writing metadata onto the involved lots (offline-first, sync via the Lot outbox).',
  'lots.lineage.hint.selectContext': 'Select a facility to enable split/merge.',
  'lots.lineage.readonly.banner': 'Read-only mode ({role} token). Split/merge requires an editor token.',
  'lots.lineage.processingPlantNotice': 'Split and merge remain farm-only. Use transfer, history, and story review from processing-plant contexts for now.',

  'lots.lineage.split.title': 'Split',
  'lots.lineage.split.sourceLotLabel': 'Source lot',
  'lots.lineage.split.selectLot': 'Select lot',
  'lots.lineage.quickPickerLabel': 'Quick source picker',
  'lots.lineage.quickPickerPlaceholder': 'Search lot key or production unit',
  'lots.lineage.quickPickerSuggested': 'Suggested source lots',
  'lots.lineage.quickPickerMatches': 'Matching source lots',
  'lots.lineage.quickPickerEmpty': 'No source lots match this search yet.',
  'lots.lineage.split.recordedAtLabel': 'Recorded at',
  'lots.lineage.split.newLotKeyALabel': 'New lot key A',
  'lots.lineage.split.newLotKeyBLabel': 'New lot key B',
  'lots.lineage.split.notesLabel': 'Notes',
  'lots.lineage.split.placeholderKeyA': 'Example: LOT-123A',
  'lots.lineage.split.placeholderKeyB': 'Example: LOT-123B',
  'lots.lineage.split.recordButton': 'Record split',

  'lots.lineage.merge.title': 'Merge',
  'lots.lineage.merge.sourceLotsLabel': 'Source lots (select 2+)',
  'lots.lineage.merge.recordedAtLabel': 'Recorded at',
  'lots.lineage.merge.newMergedKeyLabel': 'New merged lot key',
  'lots.lineage.merge.placeholderKey': 'Example: LOT-123M',
  'lots.lineage.merge.markInactiveLabel': 'Mark source lots inactive',
  'lots.lineage.merge.notesLabel': 'Notes',
  'lots.lineage.merge.guidance.selectOneMore': 'Select one more source lot from {unit} to continue.',
  'lots.lineage.merge.guidance.ready': 'Selected source lots are all in {unit}. Merge can continue.',
  'lots.lineage.merge.guidance.crossUnit': 'Current selection spans multiple production units: {units}. Merge currently requires one production unit.',
  'lots.lineage.merge.selectedSummary': 'Selected source lots ({count})',
  'lots.lineage.merge.groupSummary': '{unit} ({count})',
  'lots.lineage.merge.clearSelection': 'Clear selection',
  'lots.lineage.merge.removeSource': 'Remove',
  'lots.lineage.merge.moveSourceEarlier': 'Earlier',
  'lots.lineage.merge.moveSourceLater': 'Later',
  'lots.lineage.merge.review.compactSummary': 'Previewing {shown} of {total} selected source lots. {hidden} more remain in the compact review.',
  'lots.lineage.merge.review.expandedSummary': 'Reviewing all {total} selected source lots.',
  'lots.lineage.merge.review.showAll': 'Review all sources',
  'lots.lineage.merge.review.showLess': 'Show compact review',
  'lots.lineage.merge.recordButton': 'Record merge',

  'lots.lineage.error.selectContextFirst': 'Select a facility first.',
  'lots.lineage.error.chooseSourceLot': 'Choose a source lot to split.',
  'lots.lineage.error.enterTwoKeys': 'Enter at least two new lot keys for the split.',
  'lots.lineage.error.mergeSelectAtLeastTwo': 'Select at least two source lots to merge.',
  'lots.lineage.error.mergeSameUnit': 'Merge currently requires source lots from the same production unit.',
  'lots.lineage.error.mergeEnterTargetKey': 'Enter a new lot key for the merged target lot.',
  'lots.lineage.error.splitFailed': 'Split failed.',
  'lots.lineage.error.mergeFailed': 'Merge failed.',

  'lots.lineage.parentsPrefix': 'Parents:',
  'lots.lineage.childrenPrefix': 'Children:',
  'lots.lineage.summary': 'P:{parents} C:{children}',
  'lots.history.button.show': 'History',
  'lots.history.button.hide': 'Hide history',
  'lots.history.title': 'History for {lot}',
  'lots.history.subtitle': 'Loaded on demand from the LotEvent ledger and cached locally after it loads. Pending local mutations may not appear here until they sync.',
  'lots.history.current.title': 'Current lot snapshot',
  'unit.inactive.suffix': '(Inactive)',
  'unit.inactive.captureBlocked': 'This unit is Inactiva: its records stay visible, but new entries are blocked. Reactivate it in Editar instalación to record again.',
  'lots.projection.title': 'Projection',
  'lots.projection.lowConfidence': 'Low confidence — read with care',
  'lots.projection.loading': 'Calculating projection…',
  'lots.projection.unavailable': 'No projection for this lot.',
  'lots.projection.status.noSampling': 'No sampling yet: record a sampling (Muestreo) to project growth.',
  'lots.projection.status.noGrowthRate': 'Needs two samplings at least a week apart, or a facility TGC (Editar instalación).',
  'lots.projection.status.noTemperature': 'No water temperature for this unit: record temperature in Agua.',
  'lots.projection.status.noStock': 'Stock unknown: record a fish count for this lot.',
  'lots.projection.target.label': 'Harvest target',
  'lots.projection.target.fromLot': '{grams} g (this lot)',
  'lots.projection.target.fromFacility': '{grams} g (facility default)',
  'lots.projection.target.none': 'not set',
  'lots.projection.target.edit': 'Change',
  'lots.projection.target.help': 'Empty = use the facility default.',
  'lots.projection.target.invalid': 'Enter a weight in grams greater than 0.',
  'lots.projection.target.saveFailed': 'Could not save the target. Try again.',
  'lots.projection.harvest': 'Projected harvest',
  'lots.projection.harvestValue': '{date} (in {days} days)',
  'lots.projection.weightToday': 'Estimated weight today',
  'lots.projection.weightIn30': 'Weight in 30 days',
  'lots.projection.biomass': 'Biomass now → at harvest',
  'lots.projection.stockAtHarvest': 'Fish at harvest',
  'lots.projection.feedToHarvest': 'Feed to harvest',
  'lots.projection.input.tgcObserved': 'Growth: TGC {tgc} from the last samplings (last: {date}, {weight} g).',
  'lots.projection.input.tgcOffice': 'Growth: facility TGC {tgc} (last sampling: {date}, {weight} g).',
  'lots.projection.input.temperature': 'Temperature: {temperature} °C (mean of recent readings, held constant).',
  'lots.projection.input.mortality': 'Mortality: {rate} %/day over recorded days ({recorded} of {total} days recorded).',
  'lots.projection.input.lowCompleteness': 'under {pct} % of days recorded',
  'lots.projection.caution.lowMortalityCompleteness': 'Few days with a mortality record: the rate may not reflect the lot (blank days are not counted as zero).',
  'lots.projection.caution.noMortalityRecords': 'No mortality records in the last 30 days: no mortality is applied.',
  'lots.projection.caution.officeTgc': "Growth uses the facility TGC, not this lot's samplings.",
  'lots.projection.caution.oldSampling': 'The last sampling is more than 30 days old.',
  'lots.projection.caution.noTarget': 'No harvest target: set one to project the harvest date and feed.',
  'lots.projection.caution.targetReached': 'The lot has already reached the harvest target.',
  'lots.projection.caution.horizonReached': 'The target is not reached within 2 years at the current growth.',
  'lots.projection.caution.noSfr': 'No SFR matrix applies: feed to harvest is not calculated.',
  'lots.projection.caution.unusualFcr': 'Projected FCR outside the usual range (0.8–2.5): growth and the SFR matrix disagree. Check the samplings and the SFR matrix.',
  'editFarm.projection.title': 'Lot projections',
  'editFarm.projection.target': 'Default harvest target (g)',
  'editFarm.projection.tgc': 'TGC when a lot has one sampling',
  'editFarm.projection.help': 'Used by Lotes → lot → Proyección and the "Proyección de lotes" report. A lot can set its own target. Typical trout TGC: 1.5–3.',
  'editFarm.projection.invalid': 'Enter positive numbers (TGC up to 6).',
  'lots.hatchery.title': 'Hatchery',
  'lots.hatchery.stage.eggs': 'Eggs',
  'lots.hatchery.stage.yolkSac': 'Yolk-sac alevins',
  'lots.hatchery.stage.firstFeeding': 'First feeding',
  'lots.hatchery.record.eggs': 'Record egg arrival',
  'lots.hatchery.record.yolkSac': 'Record hatch',
  'lots.hatchery.record.firstFeeding': 'Record first feeding',
  'lots.hatchery.stageDate': 'Real date:',
  'lots.hatchery.dueNotice': 'TUA target reached — record the real date when you see it.',
  'lots.hatchery.stageLine.eggs': 'Eggs since {date}',
  'lots.hatchery.stageLine.yolkSac': 'hatched {date}',
  'lots.hatchery.stageLine.firstFeeding': 'first feeding {date}',
  'lots.hatchery.noProjectionYet': 'Eggs and yolk-sac alevins do not eat or grow yet: the growth and feed projection starts at first feeding.',
  'lots.hatchery.loading': 'Loading hatchery data…',
  'lots.hatchery.unavailable': 'No hatchery data for this lot.',
  'lots.hatchery.tuaToDate': 'TUA to date',
  'lots.hatchery.tuaDetail': '{arrival} at arrival + {accumulated} in {days} days',
  'lots.hatchery.milestone.hatch': 'Hatch',
  'lots.hatchery.milestone.firstFeeding': 'First feeding',
  'lots.hatchery.milestone.recorded': '{date} (at {tua} TUA)',
  'lots.hatchery.milestone.expected': '≈ {date} (in {days} days, target {tua} TUA)',
  'lots.hatchery.milestone.due': 'Due now (target {tua} TUA)',
  'lots.hatchery.milestone.target': 'Target {tua} TUA (no temperature yet)',
  'lots.hatchery.milestone.noTarget': 'No TUA target',
  'lots.hatchery.alive': 'Alive now',
  'lots.hatchery.ofReceived': 'of {received} received',
  'lots.hatchery.survival': 'Survival',
  'lots.hatchery.survivalToHatch': '{pct} % to hatch',
  'lots.hatchery.survivalToFirstFeeding': '{pct} % to first feeding',
  'lots.hatchery.survivalToDate': '{pct} % to date',
  'lots.hatchery.deaths': 'Dead eggs {eggs} · dead alevins {alevins}',
  'lots.hatchery.density': 'Density in this unit',
  'lots.hatchery.perLitre': '{value} per litre',
  'lots.hatchery.perLitreDetail': '{alive} alive ÷ {litres} L ({cubic} m³ unit)',
  'lots.hatchery.noVolume': 'Unit volume not set (Editar instalación)',
  'lots.hatchery.flow': 'Water flow',
  'lots.hatchery.flowValue': '{flow} L/min · {perThousand} L/min per 1,000',
  'lots.hatchery.flowDate': 'Reading of {date}',
  'lots.hatchery.noFlow': 'No flow reading: tick "Caudal" in the unit\'s water parameters (Editar instalación) and record it in Agua.',
  'lots.hatchery.eggSize': 'Egg size',
  'lots.hatchery.eggSizeValue': '{diameter} mm · ≈ {mg} mg per egg',
  'lots.hatchery.fryWeight': 'Fry at first feeding ≈ {grams} g (approx., until the first sampling)',
  'lots.hatchery.input.temperature': 'Temperature: {temperature} °C recent mean · own readings on {recorded} of {total} days since arrival.',
  'lots.hatchery.input.temperatureBorrowed': 'Temperature from {units} (same arrival or measuring point) on {borrowed} of {total} days: this unit has no reading on those days.',
  'lots.hatchery.input.mortality': 'Mortality recorded on {recorded} days ({pct} % of the days of this stage).',
  'lots.hatchery.input.origin': 'Origin: {supplier} · supplier egg lot {lot} · certification {certification}',
  'lots.hatchery.input.health': 'Health certificate {certificate} ({date}) · quarantine: {quarantine}',
  'lots.hatchery.input.disinfection': 'Arrival disinfection: {product} {ppm} ppm, {minutes} min ({date})',
  'lots.hatchery.input.noDisinfection': 'Arrival disinfection: not recorded',
  'lots.hatchery.inheritedOrigin': 'Egg origin and stage come from the lot this one was split from.',
  'lots.hatchery.yes': 'yes',
  'lots.hatchery.no': 'no',
  'lots.hatchery.edit': 'Edit egg arrival',
  'lots.hatchery.applyToSiblings': 'Also save on the other {count} lots of the same origin',
  'lots.hatchery.start.question': 'Did this lot arrive as eggs?',
  'lots.hatchery.start.button': 'Record egg arrival',
  'lots.hatchery.arrival.title': 'Egg arrival',
  'lots.hatchery.arrival.origin': 'Origin and traceability',
  'lots.hatchery.arrival.health': 'Health status',
  'lots.hatchery.arrival.disinfection': 'Disinfection at arrival',
  'lots.hatchery.arrival.help': 'Keep the certificates on paper; here you record their number and date. Empty TUA targets use the facility defaults (Editar instalación).',
  'lots.hatchery.field.arrivalDate': 'Arrival date',
  'lots.hatchery.field.eggsReceived': 'Eggs received',
  'lots.hatchery.field.tuaAtArrival': 'TUA at arrival (°C·day)',
  'lots.hatchery.field.hatchTargetTua': 'Hatch at (total TUA)',
  'lots.hatchery.field.firstFeedingTargetTua': 'First feeding at (total TUA)',
  'lots.hatchery.field.eggDiameterMm': 'Egg diameter (mm)',
  'lots.hatchery.field.eggsPerGram': 'Eggs per gram',
  'lots.hatchery.field.eggsPerLitre': 'Eggs per litre',
  'lots.hatchery.field.eggVolumeLitres': 'Total egg volume (L)',
  'lots.hatchery.arrival.countHelp': 'No exact count? Enter eggs per litre and the total litres: eggs received = eggs/L × L (estimate).',
  'lots.hatchery.countFromVolume': '(estimated by volume)',
  'lots.hatchery.field.supplier': 'Supplier',
  'lots.hatchery.field.supplierLot': 'Supplier egg lot',
  'lots.hatchery.field.supplierCertification': 'Supplier certification (certificate no.)',
  'lots.hatchery.field.strain': 'Strain / genetic line',
  'lots.hatchery.field.healthCertificateNo': 'Health certificate no.',
  'lots.hatchery.field.healthCertificateDate': 'Certificate date',
  'lots.hatchery.field.quarantine': 'Held in quarantine',
  'lots.hatchery.field.disinfectionProduct': 'Product',
  'lots.hatchery.field.disinfectionPpm': 'Dose (ppm)',
  'lots.hatchery.field.disinfectionMinutes': 'Time (min)',
  'lots.hatchery.field.disinfectionDate': 'Date',
  'lots.hatchery.trait.allFemale': 'All-female',
  'lots.hatchery.trait.triploid': 'Triploid',
  'lots.hatchery.caution.noArrivalDate': 'No arrival date: TUA cannot be accumulated.',
  'lots.hatchery.caution.noEggCount': 'No egg count: record the eggs received to see survival and density.',
  'lots.hatchery.caution.noTemperature': 'No water temperature in this unit or in another unit of the same arrival: record it daily in Agua to accumulate TUA.',
  'lots.hatchery.caution.fewTemperatureDays': 'Temperature recorded on less than 70 % of the days: missing days use the previous reading.',
  'lots.hatchery.caution.noMortalityRecords': 'No mortality records for this stage (a blank day is not a zero).',
  'lots.hatchery.caution.lowMortalityCompleteness': 'Few days with a mortality record: survival may read high.',
  'lots.hatchery.caution.noTargets': 'No TUA targets: set them on the lot or in Editar instalación to project hatch and first feeding.',
  'lots.hatchery.error.stageBeforeArrival': 'The date cannot be before the arrival.',
  'lots.hatchery.error.futureDate': 'The date cannot be in the future.',
  'lots.hatchery.error.invalidNumber': 'Check the numbers: they must be positive (egg count a whole number).',
  'lots.hatchery.error.diameter': 'Egg diameter looks wrong (mm, usually 3–7).',
  'lots.hatchery.error.targetsOrder': 'First feeding TUA must be higher than hatch TUA.',
  'lots.hatchery.error.arrivalDate': 'Enter the arrival date.',
  'lots.hatchery.error.saveFailed': 'Could not save. Try again.',
  'lots.projection.status.hatcheryStage': 'Eggs or yolk-sac alevins: no growth or feed projection until first feeding.',
  'lots.projection.caution.estimatedStartWeight': 'Growth starts from the fry weight estimated from the egg size (approx.) — record a sampling to replace it.',
  'editFarm.hatchery.title': 'Hatchery (egg lots)',
  'editFarm.hatchery.hatchTargetTua': 'Hatch at (total TUA)',
  'editFarm.hatchery.firstFeedingTargetTua': 'First feeding at (total TUA)',
  'editFarm.hatchery.fryWeightFactor': 'Fry weight ÷ egg weight',
  'editFarm.hatchery.help': 'Used by egg lots that have no targets of their own. Total TUA, counted from fertilization as on the supplier sheet (each lot adds its TUA at arrival). Use your egg supplier\'s values. The factor turns egg weight into the estimated fry weight at first feeding (empty = 1).',
  'editFarm.hatchery.mortality.title': 'Daily mortality reference (%)',
  'editFarm.hatchery.mortality.green': 'green up to',
  'editFarm.hatchery.mortality.red': 'red from',
  'editFarm.hatchery.mortality.eggs': 'Eggs',
  'editFarm.hatchery.mortality.yolkSac': 'Yolk-sac fry',
  'editFarm.hatchery.mortality.critical': 'Critical days',
  'editFarm.hatchery.mortality.help': 'Colours Daily mortality % in the Database for egg and yolk-sac lots (between the two values = yellow). Critical days: egg arrival and re-incubation (2 days), hatching (4 days from the hatch date), first feeding (7 days) and the first transfer after hatching (2 days). Empty = the suggested value shown in grey.',
  'editFarm.hatchery.mortality.invalid': 'In each row, "red from" must be higher than "green up to" (100 % at most).',
  'editFarm.hatchery.invalid': 'Enter positive numbers (factor up to 5).',
  'crewMode.mortality.cause.deadEgg': 'Dead egg (white)',
  'crewMode.mortality.cause.notHatched': 'Did not hatch',
  'crewMode.mortality.cause.deformed': 'Deformed',
  'crewMode.mortality.cause.fungus': 'Fungus',
  'crewMode.mortality.countHatchery': 'How many dead eggs or alevins?',
  'crewMode.water.parameter.flow': 'Flow',
  'water.table.flow': 'Flow (L/min)',
  'reports.dataset.lotHatchery': 'Hatchery (eggs and alevins: TUA, survival, density, flow)',
  'reports.column.lifeStage': 'Life stage',
  'reports.column.arrivalDate': 'Arrival date',
  'reports.column.eggsReceived': 'Eggs received',
  'reports.column.tuaAtArrival': 'TUA at arrival',
  'reports.column.tuaToDate': 'TUA to date',
  'reports.column.temperatureDaysRecorded': 'Days with temperature',
  'reports.column.hatchTargetTua': 'Hatch target (TUA)',
  'reports.column.hatchDate': 'Hatch date',
  'reports.column.expectedHatchDate': 'Expected hatch',
  'reports.column.firstFeedingTargetTua': 'First feeding target (TUA)',
  'reports.column.firstFeedingDate': 'First feeding date',
  'reports.column.expectedFirstFeedingDate': 'Expected first feeding',
  'reports.column.eggDeaths': 'Dead eggs',
  'reports.column.alevinDeaths': 'Dead alevins',
  'reports.column.aliveNow': 'Alive now',
  'reports.column.survivalToHatchPct': 'Survival to hatch (%)',
  'reports.column.survivalToFirstFeedingPct': 'Survival to first feeding (%)',
  'reports.column.survivalToDatePct': 'Survival to date (%)',
  'reports.column.perLitre': 'Per litre',
  'reports.column.flowLpm': 'Flow (L/min)',
  'reports.column.flowPerThousandLpm': 'Flow per 1,000 (L/min)',
  'reports.column.eggDiameterMm': 'Egg diameter (mm)',
  'reports.column.eggsPerGram': 'Eggs per gram',
  'reports.column.eggWeightMg': 'Egg weight (mg, approx.)',
  'reports.column.estimatedFryWeightG': 'Fry weight at first feeding (g, approx.)',
  'reports.column.seedSupplier': 'Supplier',
  'reports.column.supplierLot': 'Supplier egg lot',
  'reports.column.supplierCertification': 'Supplier certification',
  'reports.column.strain': 'Strain',
  'reports.column.stockTraits': 'Stock traits',
  'reports.column.healthCertificateNo': 'Health certificate',
  'reports.column.healthCertificateDate': 'Certificate date',
  'reports.column.quarantine': 'Quarantine',
  'reports.column.disinfectionProduct': 'Disinfection product',
  'reports.column.disinfectionPpm': 'Disinfection (ppm)',
  'reports.column.disinfectionMinutes': 'Disinfection (min)',
  'reports.column.disinfectionDate': 'Disinfection date',
  'lots.history.current.location': 'Current location',
  'lots.history.current.latestOrigin': 'Latest move or transfer origin',
  'lots.history.current.latestOriginValue': '{eventType} from {origin} on {timestamp}',
  'lots.history.current.timeline': 'Movement timeline (latest first)',
  'lots.history.current.timelineStep': 'Step {step}: {entry}',
  'lots.history.current.recentChain': 'Recent move and transfer chain',
  'lots.history.current.recentChainValue': '{eventType} from {origin} on {timestamp}',
  'lots.history.loading': 'Loading history...',
  'lots.history.empty': 'No ledger events found for this lot yet.',
  'lots.history.error.loadFailed': 'Unable to load lot history.',
  'lots.history.eventType.create': 'Create',
  'lots.history.eventType.update': 'Update',
  'lots.history.eventType.split': 'Split',
  'lots.history.eventType.merge': 'Merge',
  'lots.history.eventType.move': 'Move',
  'lots.history.eventType.transfer': 'Transfer',
  'lots.history.sourceLots': 'Source lots:',
  'lots.history.targetLots': 'Target lots:',
  'lots.history.location': 'Location:',
  'lots.history.changes': 'Changes:',
  'lots.history.notes': 'Notes:',
  'lots.history.field.lotKey': 'Lot key',
  'lots.history.field.stage': 'Lot status',
  'lots.history.field.facility': 'Facility',
  'lots.history.field.productionUnit': 'Production unit',
  'lots.history.field.species': 'Species',
  'lots.history.field.biomassKg': 'Biomass (kg)',
  'lots.story.button.show': 'Story preview',
  'lots.story.button.hide': 'Hide story',
  'lots.story.title': 'Lot story preview for {lot}',
  'lots.story.subtitle': 'Server-generated PRIVATE publishing preview. Save the current preview as a draft artifact, then publish that draft privately when it is ready.',
  'lots.story.library.title': 'Lot story library',
  'lots.story.library.subtitle': 'Review the current draft or published artifact for each lot family inside this tenant.',
  'lots.story.library.refresh': 'Refresh library',
  'lots.story.library.refreshing': 'Refreshing library...',
  'lots.story.library.loading': 'Loading lot story library...',
  'lots.story.library.empty': 'No tenant-internal lot story artifacts have been created yet.',
  'lots.story.library.openPreview': 'Open live preview',
  'lots.story.library.item.version': 'Version {version}',
  'lots.story.library.switcher.title': 'Artifact switcher',
  'lots.story.library.switcher.subtitle': 'Keep the current tenant-internal artifact in view and switch records only when you need another source story.',
  'lots.story.library.switcher.open': 'Switch artifact',
  'lots.story.library.switcher.close': 'Close switcher',
  'lots.story.library.switcher.field': 'Choose another artifact',
  'lots.story.library.switcher.help': 'Selecting another artifact switches the detail panel immediately without reopening the full library rail.',
  'lots.story.library.detail.empty': 'Select a lot story artifact to review its current version details.',
  'lots.story.library.detail.version': 'Current version {version}',
  'lots.story.library.detail.currentVersionStatus': 'Current version status',
  'lots.story.library.detail.visibility': 'Visibility',
  'lots.story.library.detail.publishedAt': 'Published at',
  'lots.story.library.detail.readiness': 'Publish readiness',
  'lots.story.library.detail.artifactContextTitle': 'Artifact context',
  'lots.story.library.detail.artifactContextSubtitle': 'Private publish checks stay here when you need to inspect source-side context.',
  'lots.story.library.detail.editorialNotesTitle': 'Editorial notes',
  'lots.story.library.detail.editorialNotesSubtitle': 'Open the saved summary and internal notes only when you need the original publishing context.',
  'lots.story.library.detail.groupSetupTitle': '1. Choose target group',
  'lots.story.library.detail.groupSetupSubtitle': 'Select the target group and save it before the owner-side review workflow starts.',
  'lots.story.library.detail.groupPromotionTitle': '2. Review and promote',
  'lots.story.library.detail.groupPromotionSubtitle': 'Record owner review, approval, release gate, and final promotion for the saved group.',
  'lots.story.library.detail.groupDeliveryTitle': '3. Receiver delivery check',
  'lots.story.library.detail.groupDeliverySubtitle': 'After promotion, verify the receiver-side delivery snapshot for the saved group.',
  'lots.story.library.detail.attribution': 'Actor attribution',
  'lots.story.library.detail.attributionSubtitle': 'Open when you need the stored actor and tenant evidence for draft, publish, or revoke events.',
  'lots.story.library.detail.draftedBy': 'Draft saved by',
  'lots.story.library.detail.publishedBy': 'Published by',
  'lots.story.library.detail.revokedBy': 'Revoked by',
  'lots.story.library.detail.presentationIdentity': 'Identity: {value}',
  'lots.story.library.detail.tenantKey': 'Tenant: {value}',
  'lots.story.library.detail.tenantMemberId': 'Member ID: {value}',
  'lots.story.library.detail.userId': 'User ID: {value}',
  'lots.story.library.detail.tenantMembershipId': 'Membership ID: {value}',
  'lots.story.library.detail.actorId': 'Actor: {value}',
  'lots.story.library.detail.tokenId': 'Token: {value}',
  'lots.story.library.detail.recordedAt': 'Recorded: {value}',
  'lots.story.library.detail.historySubtitle': 'Open older saved versions only when you need to compare or restore context.',
  'lots.story.library.history.title': 'Version history',
  'lots.story.library.history.subtitle': 'Review how this artifact moved through draft, publish, revoke, and replacement states.',
  'lots.story.library.history.current': 'Current version',
  'lots.story.library.history.currentDraft': 'Current draft',
  'lots.story.library.history.currentPublished': 'Current published',
  'lots.story.library.history.createdAt': 'Created at',
  'lots.story.library.history.openSnapshot': 'Open saved version',
  'lots.story.library.history.snapshotOpen': 'Saved version open',
  'lots.story.library.compare.title': 'Version diff',
  'lots.story.library.compare.subtitle': 'Comparing version {left} with version {right}.',
  'lots.story.library.compare.currentColumn': 'Baseline v{version}',
  'lots.story.library.compare.comparedColumn': 'Compared v{version}',
  'lots.story.library.compare.withCurrent': 'Compare to baseline',
  'lots.story.library.compare.comparing': 'Compared version',
  'lots.story.library.compare.setBase': 'Set baseline',
  'lots.story.library.compare.baseSelected': 'Baseline selected',
  'lots.story.library.compare.clear': 'Clear compare',
  'lots.story.library.compare.empty': 'No differences are visible across the fields currently tracked in this review.',
  'lots.story.library.compare.snapshotTitle': 'Saved version inspection',
  'lots.story.library.compare.snapshotSubtitle': 'Review the saved story projections for both selected versions side by side.',
  'lots.story.library.compare.snapshotBase': 'Baseline saved version v{version}',
  'lots.story.library.compare.snapshotTarget': 'Compared saved version v{version}',
  'lots.story.library.status.draft': 'Draft',
  'lots.story.library.status.published': 'Published',
  'lots.story.library.status.revoked': 'Revoked',
  'lots.story.library.status.archived': 'Archived',
  'lots.story.library.versionStatus.draft': 'Draft',
  'lots.story.library.versionStatus.published': 'Published',
  'lots.story.library.versionStatus.superseded': 'Superseded',
  'lots.story.library.versionStatus.revoked': 'Revoked',
  'lots.story.library.visibility.private': 'Private',
  'lots.story.library.visibility.group': 'Group',
  'lots.story.library.error.loadFailed': 'Unable to load the lot story library.',
  'lots.story.library.error.detailFailed': 'Unable to load the selected lot story artifact.',
  'lots.story.draft.title': 'Draft artifact',
  'lots.story.draft.subtitle': 'Save this preview as a tenant-internal draft artifact with editorial metadata.',
  'lots.story.draft.readonly': 'Your current role can preview lot stories but cannot save draft artifacts.',
  'lots.story.draft.field.title': 'Draft title',
  'lots.story.draft.field.summary': 'Summary',
  'lots.story.draft.field.notes': 'Publisher notes',
  'lots.story.draft.placeholder.title': 'Example: Tilapia lot story for internal review',
  'lots.story.draft.placeholder.summary': 'Short internal summary for reviewers',
  'lots.story.draft.placeholder.notes': 'Notes, caveats, and interpretation for this draft',
  'lots.story.draft.defaultTitle': '{lot} lot story',
  'lots.story.draft.saveButton': 'Save draft',
  'lots.story.draft.saving': 'Saving draft...',
  'lots.story.draft.saved': 'Draft saved',
  'lots.story.draft.savedVersion': 'Draft version {version} is now the current draft.',
  'lots.story.draft.savedFamily': 'Lot family: {lot}',
  'lots.story.draft.savedArtifact': 'Internal artifact ID: {artifactId}',
  'lots.story.draft.error.noPreview': 'Load a story preview before saving a draft.',
  'lots.story.draft.error.titleRequired': 'Enter a draft title before saving.',
  'lots.story.draft.error.saveFailed': 'Unable to save the lot story draft.',
  'lots.story.preflight.title': 'Private write context',
  'lots.story.preflight.subtitle': 'PRIVATE publish still stays inside this tenant. This preflight resolves the current profile, tenant role, and group context before draft or publish actions run.',
  'lots.story.preflight.loading': 'Checking private write context...',
  'lots.story.preflight.identity': 'Presentation identity',
  'lots.story.preflight.tenantRole': 'Resolved tenant role',
  'lots.story.preflight.activeGroups': 'Active groups',
  'lots.story.preflight.partnerOrganizations': 'Partner organizations',
  'lots.story.preflight.none': 'None',
  'lots.story.preflight.status.allowed': 'Private write allowed',
  'lots.story.preflight.status.denied': 'Private write not allowed for this tenant membership.',
  'lots.story.preflight.error.loadFailed': 'Unable to load the private write context.',
  'lots.story.preflight.error.denied': 'Private draft and publish actions require a contributor, editor, or admin tenant membership in the current tenant.',
  'lots.story.groupPreflight.title': 'GROUP read preflight',
  'lots.story.groupPreflight.subtitle': 'This does not share anything yet. It resolves current artifact visibility plus eligible active group context so the current detail view can explain whether GROUP read would be possible later.',
  'lots.story.groupPreflight.loading': 'Checking GROUP read preflight...',
  'lots.story.groupPreflight.targetGroup': 'Target group',
  'lots.story.groupPreflight.identity': 'Presentation identity',
  'lots.story.groupPreflight.tenantRole': 'Resolved tenant role',
  'lots.story.groupPreflight.visibility': 'Current artifact visibility',
  'lots.story.groupPreflight.eligibleGroups': 'Eligible active groups',
  'lots.story.groupPreflight.partnerOrganizations': 'Partner organizations',
  'lots.story.groupPreflight.blockingReasons': 'Blocking reasons',
  'lots.story.groupPreflight.noEligibleGroups': 'No eligible active groups',
  'lots.story.groupPreflight.none': 'None',
  'lots.story.groupPreflight.status.allowed': 'GROUP read would be allowed for the current actor context.',
  'lots.story.groupPreflight.status.denied': 'GROUP read not allowed yet for the current artifact and actor context.',
  'lots.story.groupPreflight.reason.artifactNotPublished': 'Current artifact version is not published yet.',
  'lots.story.groupPreflight.reason.artifactNotGroupVisible': 'Current artifact visibility is still private.',
  'lots.story.groupPreflight.reason.noActiveGroupContext': 'No active eligible group context is available for the current actor.',
  'lots.story.groupPreflight.reason.noTargetGroupSelected': 'Select one target group before GROUP read can be evaluated honestly.',
  'lots.story.groupPreflight.reason.targetGroupNotEligible': 'The selected group is not eligible for the current actor context.',
  'lots.story.groupPreflight.reason.targetGroupNotSaved': 'Current GROUP visibility only applies to the saved target group.',
  'lots.story.groupPreflight.error.loadFailed': 'Unable to load the GROUP read preflight.',
  'lots.story.groupDelivery.title': 'GROUP delivery snapshot',
  'lots.story.groupDelivery.subtitle': 'This loads the current published snapshot through the receiver delivery boundary for the saved target group only.',
  'lots.story.groupDelivery.loading': 'Loading GROUP delivery snapshot...',
  'lots.story.groupDelivery.selectedGroup': 'Current selected group',
  'lots.story.groupDelivery.savedGroup': 'Saved delivery group',
  'lots.story.groupDelivery.savedMatch': 'Matches saved delivery group',
  'lots.story.groupDelivery.identity': 'Presentation identity',
  'lots.story.groupDelivery.tenantRole': 'Resolved tenant role',
  'lots.story.groupDelivery.grantedGroup': 'Granted delivery group',
  'lots.story.groupDelivery.grantedGroupRole': 'Granted group role',
  'lots.story.groupDelivery.partnerOrganizations': 'Partner organizations',
  'lots.story.groupDelivery.blockingReasons': 'Delivery blocking reasons',
  'lots.story.groupDelivery.snapshotTitle': 'Delivered title',
  'lots.story.groupDelivery.summary': 'Delivered summary',
  'lots.story.groupDelivery.notes': 'Delivered notes',
  'lots.story.groupDelivery.publishedAt': 'Delivered published at',
  'lots.story.groupDelivery.publishedBy': 'Delivered published by',
  'lots.story.groupDelivery.status.allowed': 'Receiver-side GROUP delivery snapshot is loaded for the saved target group.',
  'lots.story.groupDelivery.status.denied': 'GROUP delivery snapshot not available for the current selected group.',
  'lots.story.groupDelivery.reason.noTargetGroupSelected': 'Select the saved target group before loading the receiver delivery snapshot.',
  'lots.story.groupDelivery.reason.targetGroupNotSaved': 'Switch back to the saved target group to load the receiver delivery snapshot honestly.',
  'lots.story.groupDelivery.reason.groupReadNotAllowed': 'Current GROUP read preflight is not allowed for the saved target group.',
  'lots.story.groupDelivery.error.loadFailed': 'Unable to load the GROUP delivery snapshot.',
  'lots.story.groupDelivery.receipt.title': 'Latest receiver receipt',
  'lots.story.groupDelivery.receipt.loading': 'Checking the latest external-view receipt...',
  'lots.story.groupDelivery.receipt.empty': 'No receiver-side delivery receipt recorded yet for the current promoted snapshot.',
  'lots.story.groupDelivery.receipt.viewedBy': 'Last opened by',
  'lots.story.groupDelivery.receipt.viewedAt': 'Last opened at',
  'lots.story.groupDelivery.receipt.grantedGroupRole': 'Viewer group role at open',
  'lots.story.groupDelivery.receipt.historyTitle': 'Earlier receiver opens',
  'lots.story.groupDelivery.receipt.loadMore': 'Load earlier receiver opens',
  'lots.story.groupDelivery.receipt.loadingMore': 'Loading earlier receiver opens...',
  'lots.story.groupDelivery.receipt.error.loadFailed': 'Unable to load the latest receiver receipt.',
  'lots.story.groupShareShell.title': 'GROUP share shell',
  'lots.story.groupShareShell.subtitle': 'Save one selected target group now, but keep actual GROUP sharing disabled in this slice.',
  'lots.story.groupShareShell.selectedTarget': 'Selected target group',
  'lots.story.groupShareShell.savedTarget': 'Saved target group',
  'lots.story.groupShareShell.notSaved': 'Not saved yet',
  'lots.story.groupShareShell.saveButton': 'Save target group',
  'lots.story.groupShareShell.saving': 'Saving target group...',
  'lots.story.groupShareShell.saved': 'Target group saved for later GROUP share review.',
  'lots.story.groupShareShell.error.saveFailed': 'Unable to save the target group.',
  'lots.story.groupShareReview.title': 'GROUP share review preflight',
  'lots.story.groupShareReview.subtitle': 'This checks whether the current selected group is ready for later owner-tenant GROUP review, without exposing the artifact yet.',
  'lots.story.groupShareReview.loading': 'Checking GROUP share review readiness...',
  'lots.story.groupShareReview.selectedGroup': 'Selected target group',
  'lots.story.groupShareReview.savedGroup': 'Saved target group',
  'lots.story.groupShareReview.savedMatch': 'Matches saved intent',
  'lots.story.groupShareReview.savedMatch.yes': 'Yes',
  'lots.story.groupShareReview.savedMatch.no': 'No',
  'lots.story.groupShareReview.identity': 'Presentation identity',
  'lots.story.groupShareReview.tenantRole': 'Resolved tenant role',
  'lots.story.groupShareReview.visibility': 'Current artifact visibility',
  'lots.story.groupShareReview.readiness': 'Published readiness',
  'lots.story.groupShareReview.eligibleGroups': 'Eligible active groups',
  'lots.story.groupShareReview.partnerOrganizations': 'Partner organizations',
  'lots.story.groupShareReview.blockingReasons': 'Blocking reasons',
  'lots.story.groupShareReview.status.allowed': 'This artifact is ready for later GROUP share review.',
  'lots.story.groupShareReview.status.denied': 'GROUP share review not ready yet for the current selected group.',
  'lots.story.groupShareReview.reviewButton': 'Record owner-tenant review',
  'lots.story.groupShareReview.reviewing': 'Recording review...',
  'lots.story.groupShareReview.reviewedButton': 'Review recorded',
  'lots.story.groupShareReview.reviewRecorded': 'Owner-tenant review recorded for the current saved target group.',
  'lots.story.groupShareReview.reviewedAt': 'Reviewed at',
  'lots.story.groupShareReview.reviewedBy': 'Reviewed by',
  'lots.story.groupShareReview.approvalReadiness': 'Approval readiness',
  'lots.story.groupShareReview.approvalBlockingReasons': 'Approval blocking reasons',
  'lots.story.groupShareReview.approvalStatus.allowed': 'This artifact is ready for one owner-tenant GROUP share approval handoff.',
  'lots.story.groupShareReview.approvalStatus.denied': 'GROUP share approval not ready yet for the current selected group.',
  'lots.story.groupShareReview.approvalButton': 'Record owner-tenant approval',
  'lots.story.groupShareReview.approving': 'Recording approval...',
  'lots.story.groupShareReview.approvedButton': 'Approval recorded',
  'lots.story.groupShareReview.approvalRecorded': 'Owner-tenant approval recorded for the current saved target group.',
  'lots.story.groupShareReview.approvedAt': 'Approved at',
  'lots.story.groupShareReview.approvedBy': 'Approved by',
  'lots.story.groupShareReview.releaseGateReadiness': 'Release-gate readiness',
  'lots.story.groupShareReview.releaseGateBlockingReasons': 'Release-gate blocking reasons',
  'lots.story.groupShareReview.releaseGateStatus.allowed': 'This artifact is ready for one owner-tenant GROUP release gate.',
  'lots.story.groupShareReview.releaseGateStatus.denied': 'GROUP release gate not ready yet for the current selected group.',
  'lots.story.groupShareReview.releaseGateButton': 'Record GROUP release gate',
  'lots.story.groupShareReview.releaseGating': 'Recording release gate...',
  'lots.story.groupShareReview.releaseGatedButton': 'Release gate recorded',
  'lots.story.groupShareReview.releaseGateRecorded': 'GROUP release gate recorded for the current saved target group.',
  'lots.story.groupShareReview.releaseGatedAt': 'Release gate recorded at',
  'lots.story.groupShareReview.releaseGatedBy': 'Release gate recorded by',
  'lots.story.groupShareReview.promotionReadiness': 'Promotion readiness',
  'lots.story.groupShareReview.promotionBlockingReasons': 'Promotion blocking reasons',
  'lots.story.groupShareReview.promotionStatus.allowed': 'This artifact is ready to be promoted to GROUP for the saved target group.',
  'lots.story.groupShareReview.promotionStatus.denied': 'Promote to GROUP is not ready yet for the current selected group.',
  'lots.story.groupShareReview.promotionStatus.promoted': 'This artifact is already promoted to GROUP for the saved target group.',
  'lots.story.groupShareReview.promoteButton': 'Promote to GROUP',
  'lots.story.groupShareReview.promoting': 'Promoting to GROUP...',
  'lots.story.groupShareReview.promotedButton': 'Promoted to GROUP',
  'lots.story.groupShareReview.promoted': 'Artifact promoted to GROUP for the current saved target group.',
  'lots.story.groupShareReview.promotedAt': 'Promoted at',
  'lots.story.groupShareReview.promotedBy': 'Promoted by',
  'lots.story.groupShareReview.reason.artifactNotPublished': 'Publish this artifact privately before later GROUP share review can start honestly.',
  'lots.story.groupShareReview.reason.artifactReadinessBlocked': 'Current published readiness is blocked, so later GROUP share review should stay closed.',
  'lots.story.groupShareReview.reason.noActiveGroupContext': 'No active eligible group context is available for the current actor.',
  'lots.story.groupShareReview.reason.noTargetGroupSelected': 'Select one target group before GROUP share review can be evaluated honestly.',
  'lots.story.groupShareReview.reason.targetGroupNotEligible': 'The selected group is not eligible for the current actor context.',
  'lots.story.groupShareReview.reason.targetGroupNotSaved': 'Save the selected target group before later GROUP share review can be requested honestly.',
  'lots.story.groupShareReview.approvalReason.reviewNotRecorded': 'Record the owner-tenant review before later GROUP share approval can be recorded honestly.',
  'lots.story.groupShareReview.releaseGateReason.approvalNotRecorded': 'Record the owner-tenant approval before later GROUP release gating can be recorded honestly.',
  'lots.story.groupShareReview.promotionReason.releaseGateNotRecorded': 'Record the owner-tenant GROUP release gate before promoting this artifact to GROUP honestly.',
  'lots.story.groupShareReview.promotionReason.alreadyGroupVisible': 'This artifact is already promoted to GROUP for the saved target group.',
  'lots.story.groupShareReview.error.loadFailed': 'Unable to load the GROUP share review preflight.',
  'lots.story.groupShareReview.error.recordFailed': 'Unable to record the owner-tenant review.',
  'lots.story.groupShareReview.error.approvalRecordFailed': 'Unable to record the owner-tenant approval.',
  'lots.story.groupShareReview.error.releaseGateRecordFailed': 'Unable to record the GROUP release gate.',
  'lots.story.groupShareReview.error.promoteFailed': 'Unable to promote the artifact to GROUP.',
  'lots.story.snapshot.title': 'Historical saved version {version}',
  'lots.story.snapshot.readonly': 'This preview is showing the exact saved version recorded for this point in version history. Draft save and private publish stay disabled here; use Open live preview to work from the current server projection.',
  'lots.story.publish.button': 'Publish privately',
  'lots.story.publish.publishing': 'Publishing...',
  'lots.story.publish.published': 'Published privately',
  'lots.story.publish.publishedVersion': 'Version {version} is now the current private published artifact.',
  'lots.story.publish.error.noDraft': 'Save a draft before publishing it privately.',
  'lots.story.publish.error.failed': 'Unable to publish the current lot story draft.',
  'lots.story.revoke.button': 'Revoke private publish',
  'lots.story.revoke.revoking': 'Revoking...',
  'lots.story.revoke.revoked': 'Private publish revoked',
  'lots.story.revoke.revokedVersion': 'Version {version} is no longer visible as the current private published artifact.',
  'lots.story.revoke.error.noPublished': 'Select a current private published artifact before revoking it.',
  'lots.story.revoke.error.failed': 'Unable to revoke the current private published artifact.',
  'lots.story.loading': 'Loading lot story preview...',
  'lots.story.empty': 'No lot story preview is available for this lot family yet.',
  'lots.story.error.loadFailed': 'Unable to load the lot story preview.',
  'lots.story.readiness.ready': 'Ready',
  'lots.story.readiness.warning': 'Warning',
  'lots.story.readiness.blocked': 'Blocked',
  'lots.story.readiness.sourceFreshness.fresh': 'Fresh',
  'lots.story.readiness.sourceFreshness.stale': 'Stale',
  'lots.story.readiness.sourceFreshness.unknown': 'Unknown',
  'lots.story.readinessCue.title': 'Readiness cue: {value}',
  'lots.story.readinessCue.strong': 'Strong',
  'lots.story.readinessCue.caution': 'Caution',
  'lots.story.readinessCue.weak': 'Weak',
  'lots.story.readinessCue.reason.fully-ready': 'All readiness signals are clean.',
  'lots.story.readinessCue.reason.has-blocking-readiness-reasons': 'One or more readiness checks are blocking.',
  'lots.story.readinessCue.reason.pending-lineage-sync': 'Lineage sync is still pending.',
  'lots.story.readinessCue.reason.ambiguous-legacy-matches': 'Ambiguous legacy matches need resolution.',
  'lots.story.readinessCue.reason.source-data-stale': 'Source data is stale.',
  'lots.story.readinessCue.reason.has-warning-readiness-reasons': 'One or more readiness checks are warnings.',
  'lots.story.readinessCue.reason.unresolved-evidence': 'Some evidence categories are unresolved.',
  'lots.story.readinessCue.reason.source-freshness-unknown': 'Source freshness is unknown.',
  'lots.story.reason.warning': 'Warning',
  'lots.story.reason.block': 'Blocked',
  'lots.story.reasons.title': 'Publish readiness checks',
  'lots.story.readinessMetadata.title': 'Readiness metadata',
  'lots.story.readinessMetadata.status': 'Readiness status',
  'lots.story.readinessMetadata.sourceFreshness': 'Source freshness',
  'lots.story.readinessMetadata.pendingLineageSync': 'Pending lineage sync',
  'lots.story.readinessMetadata.unresolvedEvidence': 'Unresolved evidence categories',
  'lots.story.readinessMetadata.ambiguousLegacyMatches': 'Ambiguous legacy matches',
  'lots.story.evidence.title': 'Evidence summary',
  'lots.story.evidence.feedRefs': 'Feed evidence refs',
  'lots.story.evidence.waterRefs': 'Water evidence refs',
  'lots.story.evidence.productionRefs': 'Production evidence refs',
  'lots.story.evidence.reportSnapshotRefs': 'Report snapshot refs',
  'lots.story.evidence.dashboardSnapshotRefs': 'Dashboard snapshot refs',
  'lots.story.evidence.sourceLotRefs': 'Source lot refs',
  'lots.story.evidence.sourceEventRefs': 'Source event refs',
  'lots.story.evidence.sourceFeedRecordRefs': 'Source feed record refs',
  'lots.story.evidence.sourceWaterRecordRefs': 'Source water record refs',
  'lots.story.evidence.sourceProductionRecordRefs': 'Source production record refs',
  'lots.story.value.yes': 'Yes',
  'lots.story.value.no': 'No',
  'lots.story.pendingSync.title': 'Lineage sync still pending',
  'lots.story.pendingSync.message': 'A move, transfer, split, or merge for this lot family is still queued locally. Let that sync finish, then refresh this preview before saving or publishing.',
  'lots.story.crossFarmTrust.title': 'Cross-farm trust is still blocked',
  'lots.story.crossFarmTrust.message': 'This lot family spans multiple farms, but the recorded transfer history does not yet explain the current active descendants. Review and reconcile the transfer lineage before saving or publishing.',
  'lots.story.processingSiteRegistry.title': 'Processing site is not registered',
  'lots.story.processingSiteRegistry.message': 'This lot family points to a processing site that is not registered for its current farm. Register that processing site before saving or publishing.',
  'lots.story.summary.generatedAt': 'Generated',
  'lots.story.summary.lineageRoot': 'Lot family ID',
  'lots.story.summary.lineageRootTechnicalHelp': 'Internal traceability ID',
  'lots.story.summary.lineageRootTechnicalTitle': 'The internal identifier for this lot family. Use it when comparing the same family across exports, support tickets, or other tenants. The lot family ID stays the same across split, merge, move, and transfer events.',
  'lots.story.summary.sourceTenant': 'Source tenant',
  'lots.story.summary.thisTenant': 'This tenant',
  'lots.story.summary.origin': 'Family origin',
  'lots.story.summary.currentSite': 'Current site',
  'lots.story.summary.noCurrentSite': 'Current site is unresolved',
  'lots.story.summary.species': 'Species',
  'lots.story.summary.currentStage': 'Current stage',
  'lots.story.summary.activeDescendantsList': 'Active related lots',
  'lots.story.summary.activeDescendantsEmpty': 'No active related lots are currently projected.',
  'lots.story.summary.activeDescendantsUnknownLocation': 'Unknown location',
  'lots.story.summary.activeDescendantsByLocationCount': '{count} active lots',
  'lots.story.summary.activeDescendantsByLocationLotKeys': 'Lots: {lotKeys}',
  'lots.story.lineageGraph.title': 'Lineage graph (split and merge)',
  'lots.story.lineageGraph.empty': 'No split or merge relationships are available for this preview window.',
  'lots.story.lineageGraph.childCount': '{count} child links',
  'lots.story.lineageGraph.children': 'Children: {children}',
  'lots.story.metrics.title': 'Preview metrics',
  'lots.story.metrics.timelineEvents': 'Timeline events',
  'lots.story.metrics.feedLogs': 'Feed logs',
  'lots.story.metrics.waterLogs': 'Water logs',
  'lots.story.metrics.productionRecords': 'Production records',
  'lots.story.metrics.currentBiomassKg': 'Current biomass (kg)',
  'lots.story.metrics.totalFeedKg': 'Total feed: {value} kg',
  'lots.story.metrics.totalMortality': 'Total daily mortality: {value}',
  'lots.story.metrics.disposition': 'Related lot status',
  'lots.story.metrics.dispositionValue': '{active} active, {inactive} inactive, {harvested} harvested, {other} other',
  'lots.story.timeline.title': 'Timeline preview',
  'lots.story.timeline.empty': 'No lifecycle events are available for this preview window.',
  'lots.story.timeline.filteredEmpty': 'No timeline events match the current filter.',
  'lots.story.timeline.filter.label': 'Timeline filter',
  'lots.story.timeline.filter.all': 'All events',
  'lots.story.timeline.filter.notesOnly': 'Notes only',
  'lots.story.timeline.filter.relationshipOnly': 'Split and merge only',
  'lots.story.timeline.audit.totalEvents': 'Total events',
  'lots.story.timeline.audit.notedEvents': 'Events with notes',
  'lots.story.timeline.audit.latestEventAt': 'Latest event',
  'lots.story.timeline.audit.latestNotedEventAt': 'Latest noted event',
  'lots.story.timeline.audit.recentNotes': 'Recent operator notes',
  'lots.story.timeline.eventTypeBreakdown': 'Event type breakdown',
  'lots.story.timeline.eventTypeCount': '{eventType}: {count}',
  'lots.story.location.from': 'From:',
  'lots.story.location.to': 'To:',

  'lots.table.lot': 'Lot',
  'lots.table.facility': 'Facility',
  'lots.table.productionUnit': 'Production unit',
  'lots.table.status': 'Lot status',
  'lots.table.species': 'Species',
  'lots.table.stockingDate': 'Stocking date',
  'lots.table.harvestDate': 'Harvest date',
  'lots.table.biomassKg': 'Biomass (kg)',
  'lots.table.lineage': 'Lineage',
  'lots.table.actions': 'Actions',
  'lots.table.sectionTitle': 'Lots list',
  'lots.table.loading': 'Loading lots...',
  'lots.table.scope.allFacilities': 'Showing {count} lots across {facilities} facilities and {units} production units.',
  'lots.table.scope.facility': 'Showing {count} lots across {units} production units in this facility.',
  'lots.table.scope.productionUnit': 'Showing {count} lots in this production unit.',
  'lots.table.scope.processingSite': 'Showing {count} lots in this processing site.',
  'lots.table.empty.allFacilities': 'No lots found yet. Create one with “New lot”.',
  'lots.table.empty.facility': 'No lots found in this facility yet. Create one with “New lot”.',
  'lots.table.empty.productionUnit': 'No lots found in this production unit yet. Create one with “New lot”.',
  'lots.table.empty.processingSite': 'No lots found in this processing site yet. Transfer one here or select another site.',

  'lots.form.createTitle': 'Create lot',
  'lots.form.editTitle': 'Edit lot',
  'lots.form.moveTitle': 'Move lot',
  'lots.form.transferTitle': 'Transfer lot',
  'lots.form.lotKeyLabel': 'Lot key',
  'lots.form.lotKeyPlaceholder': 'Example: LOT-123',
  'lots.form.facilityLabel': 'Facility',
  'lots.form.facilityPlaceholder': 'Select facility',
  'lots.form.productionUnitLabel': 'Production unit',
  'lots.form.productionUnitPlaceholder': 'Select unit',
  'lots.form.productionUnitOptionalPlaceholder': 'No unit selected',
  'lots.form.productionUnitOptionalHelp': 'Optional for facility-destination transfers. Leave this blank to keep the snapshot facility-only.',
  'lots.form.processingSiteLabel': 'Processing site',
  'lots.form.processingSitePlaceholder': 'Select processing site',
  'lots.form.processingSiteHelp': 'Only processing sites registered for the selected facility appear here.',
  'lots.form.notice.selectUnit': 'This view can stay facility-wide, but each lot still belongs to one production unit. Choose the target unit before saving.',
  'lots.form.notice.move': 'Saving will record a move to the selected location.',
  'lots.form.notice.transfer': 'Saving will record a transfer to the selected facility and production unit.',
  'lots.form.notice.transferFacility': 'Saving will record the selected facility as the destination. A production unit is optional and only used when you want extra snapshot context.',
  'lots.form.notice.transferProcessingSite': 'Saving will record a transfer to the selected facility and registered processing site.',
  'lots.form.location.current': 'Current location',
  'lots.form.location.target': 'Target location',
  'lots.form.location.pendingUnit': 'Select production unit',
  'lots.form.location.pendingFacilityAndUnit': 'Select facility and production unit',
  'lots.form.location.pendingProcessingSite': 'Select processing site',
  'lots.form.location.pendingFacilityAndProcessingSite': 'Select facility and processing site',
  'lots.form.location.facilityWithContext': '{facility} (facility destination; context unit: {unit})',
  'lots.form.location.facilityWithProcessingSite': '{facility} / {site}',
  'lots.form.transfer.guidance.selectFacility': 'Choose the destination facility first to continue this transfer.',
  'lots.form.transfer.guidance.selectUnit': 'Choose the destination production unit in {facility} to complete this transfer.',
  'lots.form.transfer.guidance.selectSite': 'Choose the registered processing site in {facility} to enable this transfer.',
  'lots.form.transfer.guidance.sameLocation': 'This still matches the current lot location. Choose a different destination to keep this as a transfer.',
  'lots.form.transfer.guidance.moveInstead': 'This target stays inside {facility}, so saving here will record a move instead of a transfer.',
  'lots.form.transfer.guidance.processingSiteSameFacility': 'This processing-site target stays inside {facility}. Processing-site transfers are only supported for cross-facility handoffs right now.',
  'lots.form.transfer.guidance.ready': 'Transfer target is ready: {facility} / {unit}.',
  'lots.form.transfer.guidance.readyFacility': 'Transfer target is ready: {facility} as the destination facility. A production unit is optional and will stay empty unless you choose one.',
  'lots.form.transfer.guidance.readyProcessingSite': 'Transfer target is ready: {facility} / {site}.',
  'lots.form.transferDestinationTypeLabel': 'Transfer destination mode',
  'lots.form.transferDestinationType.productionUnit': 'Production unit placement',
  'lots.form.transferDestinationType.farm': 'Facility destination',
  'lots.form.transferDestinationType.farmHelp': 'Choose this when the lot should arrive at the facility first. A production unit is optional and only kept when you want extra snapshot context.',
  'lots.form.transferDestinationType.processingSite': 'Processing site destination',
  'lots.form.transferDestinationType.processingSiteHelp': 'Choose this when the lot should transfer directly to a registered processing site in the destination facility.',
  'lots.form.processingPlantWriteGuard': 'Processing-plant contexts currently support transfer and read-side review only. Create, edit, and delete remain farm-only until a dedicated plant workflow ships.',
  'lots.form.processingPlantIntakeNote': 'For MVP 2.0 plant intake, create or keep the lot upstream and use Transfer to receive it into this processing site. Direct plant-side lot creation stays post-MVP.',
  'lots.form.destinationSearchLabel': 'Quick destination picker',
  'lots.form.destinationSearchPlaceholder': 'Search facility, production unit, or processing site',
  'lots.form.destinationSearchSuggested': 'Suggested destinations',
  'lots.form.destinationSearchMatches': 'Matching destinations',
  'lots.form.destinationSearchEmpty': 'No destinations match this search yet.',
  'lots.form.destinationContextUnit': 'Context unit: {unit}',
  'lots.form.stageLabel': 'Lot status',
  'lots.form.speciesLabel': 'Species',
  'lots.form.optionalPlaceholder': 'Optional',
  'lots.form.stockingDateLabel': 'Stocking date',
  'lots.form.harvestDateLabel': 'Harvest date',
  'lots.form.biomassKgLabel': 'Biomass (kg)',

  'lots.stage.active': 'Active',
  'lots.stage.inactive': 'Inactive',
  'lots.stage.harvested': 'Harvested',
  'lots.stage.planned': 'Planned',
  'lots.stage.dormant': 'Dormant',

  'lots.confirm.delete': 'Delete lot "{lotKey}"?',
  'lots.confirm.deleteCta': 'Confirm delete',
  'lots.error.unableToLoad': 'Unable to load lots. Try again later.',
  'lots.error.readonlyCannotDelete': 'Read-only access: this token cannot delete lots.',
  'lots.error.couldNotDelete': 'Could not delete lot.',
  'lots.error.lotNotFoundRefresh': 'This lot was not found on the server. Refresh lots to update the list.',
  'lots.error.readonlyCannotCreateOrEdit': 'Read-only access: this token cannot create or edit lots.',
  'lots.error.lotKeyRequired': 'Lot key is required.',
  'lots.error.selectFacility': 'Select a facility.',
  'lots.error.selectProductionUnit': 'Select a production unit.',
  'lots.error.selectProcessingSite': 'Select a processing site.',
  'lots.error.transferSameLocation': 'Choose a different destination before recording this transfer.',
  'lots.error.transferProcessingSiteSameFacility': 'Choose a processing-site destination in a different facility before recording this transfer.',
  'lots.error.biomassMustBeNumber': 'Biomass (kg) must be a number.',
  'lots.error.unableToSave': 'Unable to save.',

  'lots.status.splitSuccess': 'Split lot {source} into {targets}.',
  'lots.status.mergeSuccess': 'Merged {sources} into {target}.',
  'lots.status.deleted': 'Deleted lot {lot}.',
  'lots.status.deletedLocal': 'Deleted lot {lot} on this device. Server sync is still pending.',
  'lots.status.deletedOffline': 'Deleted lot {lot} on this device. It will sync when back online.',
  'lots.status.deletedSynced': 'Deleted lot {lot} and synced the change.',
  'lots.status.updated': 'Updated lot {lot}.',
  'lots.status.updatedLocal': 'Saved changes to lot {lot} on this device. Server sync is still pending.',
  'lots.status.updatedOffline': 'Saved changes to lot {lot} on this device. It will sync when back online.',
  'lots.status.updatedSynced': 'Updated lot {lot} and synced the change.',
  'lots.status.created': 'Created lot {lot}.',
  'lots.status.createdLocal': 'Created lot {lot} on this device. Server sync is still pending.',
  'lots.status.createdOffline': 'Created lot {lot} on this device. It will sync when back online.',
  'lots.status.createdSynced': 'Created lot {lot} and synced the change.',
  'lots.status.refreshing': 'Refreshing lots...',
  'lots.status.refreshed': 'Lots refreshed from the server.',
  'lots.status.refreshFailed': 'Could not refresh lots right now.',

  'lots.sync.retrySynced': 'Lot sync is now caught up.',
  'lots.sync.retryOffline': 'Lot changes are local only while this device is offline.',
  'lots.sync.retryPending': 'Lot changes are still pending sync.',
  'lots.sync.blocked': '{count} lot change(s) need attention before they can sync.',
  'lots.sync.blockedWithReason': '{count} lot change(s) need attention: {reason}',
  'lots.sync.retryResolvedMissing': 'Retry skipped {count} missing lot change(s) and kept syncing the rest.',
  'lots.sync.retryAction': 'Retry sync',
  'lots.sync.retrying': 'Retrying sync...',
  'lots.sync.refreshAction': 'Refresh lots',
  'lots.sync.refreshing': 'Refreshing lots...',
  'lots.sync.retryFailed': 'Lot sync retry failed.',
  'lots.sync.refreshComplete': 'Lots refreshed.',
  'lots.sync.refreshFailed': 'Unable to refresh lots right now.',
  'lots.sync.lastResult': 'Last sync result: {result}',
  'lots.sync.lastAttempted': 'Last attempted: {time}',

  'lots.saving': 'Saving…',
  'lots.button.refreshLots': 'Refresh lots',
  'lots.button.refreshLotsTooltip': 'Refresh the lots list from the server to see changes made on other devices.',
  'lots.button.saveChanges': 'Save changes',
  'lots.button.create': 'Create lot',
  'lots.story.draft.currentArtifactStatus': 'Current artifact status',
  'lots.story.draft.revokePublishedHint': 'This lot already has a published artifact. You can revoke it from here without opening the library detail panel.',
  'lots.button.move': 'Move lot',
  'lots.button.transfer': 'Transfer lot',
  'lots.newLot.button': 'New lot',
  'lots.newLot.hint': 'Create a lot of fish or of eggs (egg arrival record).',
  'lots.newLot.title': 'New lot',
  'lots.newLot.question': 'Fish or eggs?',
  'lots.newLot.fish': 'Fish',
  'lots.newLot.fishHelp': 'Fry, fingerlings or larger fish stocked into a unit.',
  'lots.newLot.eggs': 'Eggs',
  'lots.newLot.eggsHelp': 'Eyed eggs arriving at the hatchery: records the egg arrival (count, TUA, origin, health certificate, disinfection).',
  'lots.newLot.change': 'Change',
  'lots.list.filterLabel': 'Lot status',
  'lots.list.filter.all': 'All',
  'lots.list.searchPlaceholder': 'Search lot, species or unit',
  'lots.list.emptyFilter': 'No lots match this status or search.',
  'lots.list.showAll': 'Show all lots',
  'lots.actions.menu': 'Actions',
  'lots.actions.harvest': 'Harvest',
  'lots.actions.split': 'Split lot',
  'lots.actions.merge': 'Merge with other lots',
  'lots.detail.title': 'Lot {lot}',
  'lots.detail.tabsLabel': 'Lot sections',
  'lots.detail.tab.summary': 'Summary',
  'lots.detail.tab.hatchery': 'Incubation',
  'lots.detail.tab.projection': 'Projection',
  'lots.detail.tab.history': 'History',
  'lots.detail.tab.costs': 'Costs',
  'lots.detail.tab.traceability': 'Traceability',
  'lots.detail.kind': 'Lot of',
  'lots.detail.openHatchery': 'See incubation (TUA, hatch, survival) →',
  'lots.detail.openProjection': 'See projection (harvest date, biomass, feed) →',
  'lots.detail.lineageRoot': 'Origin lot (lineage root)',
  'lots.detail.parents': 'Came from (parent lots)',
  'lots.detail.children': 'Went into (child lots)',
  'lots.detail.traceabilityHint': 'Every create, edit, move, transfer, split and merge of this lot, with date and notes, is in History.',
  'lots.detail.openHistory': 'Open History',

  // Lot costs (T3-B-03c) — manual entry section on the Lots screen.
  'lots.costs.title': 'Costs',
  'lots.costs.subtitle': 'Log Genetics, Health, and Labor costs against a lot. Feed costs are derived automatically from feed events with a unit cost in Warehouse.',
  'lots.costs.lotSelect': 'Lot',
  'lots.costs.noLotsInScope': 'No lots in the current facility filter.',
  'lots.costs.addButton': 'Add cost',
  'lots.costs.empty': 'No costs recorded for this lot yet.',
  'lots.costs.loading': 'Loading costs…',
  'lots.costs.loadError': 'Could not load cost events.',
  'lots.costs.saveError': 'Could not save this cost.',
  'lots.costs.saved': 'Cost saved.',
  'lots.costs.deleted': 'Cost deleted.',
  'lots.costs.deleteError': 'Could not delete this cost.',
  'lots.costs.deleteButton': 'Delete',
  'lots.costs.deleteHint': 'Delete this cost event',
  'lots.costs.confirmDelete': 'Confirm delete',
  'lots.costs.autoDerivedNote': 'auto-derived from feed events',
  'lots.costs.totalLabel': 'Total for this lot:',
  'lots.costs.form.category': 'Category',
  'lots.costs.form.categoryOption.G': 'Genetics (initial stock)',
  'lots.costs.form.categoryOption.N': 'Nutrition (feed)',
  'lots.costs.form.categoryOption.H': 'Health',
  'lots.costs.form.categoryOption.L': 'Labor',
  'lots.costs.form.amount': 'Amount',
  'lots.costs.form.currency': 'Currency',
  'lots.costs.form.date': 'Date',
  'lots.costs.form.nature': 'Nature',
  'lots.costs.form.natureOption.fixed': 'Fixed',
  'lots.costs.form.natureOption.variable': 'Variable',
  'lots.costs.form.classification': 'Classification',
  'lots.costs.form.classificationOption.direct': 'Direct (COGS)',
  'lots.costs.form.classificationOption.indirect': 'Indirect (OPEX)',
  'lots.costs.form.notes': 'Notes (optional)',
  'lots.costs.form.save': 'Save cost',
  'lots.costs.form.saving': 'Saving…',
  'lots.costs.form.cancel': 'Cancel',
  'lots.costs.alertAmountRequired': 'Amount is required.',
  'lots.costs.alertAmountInvalid': 'Amount must be a number (0 or greater).',
  'lots.costs.alertCurrencyInvalid': 'Currency must be a 3-letter ISO code.',
  'lots.costs.alertDateInvalid': 'Date is required.',
  'lots.costs.form.currencyMismatch': 'This lot already has events in {existingCurrencies}; are you sure this one should be in {draftCurrency}?',
  'lots.costs.form.currencyMismatchDismiss': 'Dismiss',

  // Warehouse
  'warehouse.title': 'Warehouse',
  'warehouse.subtitle': 'Track on-hand inventory and derive run-rate from Feed logs when available.',
  'warehouse.contextFacility': 'Facility',
  'warehouse.contextProductionUnit': 'Production unit',
  'warehouse.contextProductionUnitOptional': 'Production unit (optional)',
  'warehouse.allUnits': 'All units',
  'warehouse.selectFacility': 'Select a facility',
  'warehouse.selectFacilityFirst': 'Select a facility first',
  'warehouse.loadingFacilities': 'Loading facilities…',
  'warehouse.derivationWindowPrefix': 'Daily-rate window: last',
  'warehouse.derivationWindowSuffix': 'days of Feed events',
  'warehouse.derivationWindowFormula': 'Avg/day comes from linked-type usage in that window. Est. on hand subtracts usage since the item’s recorded received-on date (cap {capDays} days).',
  'warehouse.usageErrorUnable': 'Unable to derive usage from feed logs (offline data may be unavailable).',
  'warehouse.alertSelectFacilityFirst': 'Select a facility first',
  'warehouse.alertItemNameRequired': 'Item name is required',
  'warehouse.alertOnHandInvalid': 'On hand must be a valid number (0 or greater)',
  'warehouse.alertReorderInvalid': 'Reorder point must be a valid number (0 or greater)',
  'warehouse.alertUnitCostInvalid': 'Unit cost must be a valid number (0 or greater)',
  'warehouse.alertCurrencyInvalid': 'Currency must be a 3-letter ISO code (e.g. USD)',
  'inventory.form.unitCost': 'Unit cost',
  'inventory.form.unitCostHelp': 'Per unit; used to auto-cost feed events',
  'inventory.form.unitCostHelpChemical': 'Per unit; used for the cost of treatments (charged to the lot as Health).',
  'inventory.form.unitCostHelpOther': 'Per unit; reference cost of the item.',
  'inventory.form.currency': 'Currency',
  'warehouse.alertReceivedOnRequired': 'Received on is required',
  'warehouse.alertExpiresBeforeReceived': 'Expires on cannot be earlier than Received on',
  'warehouse.alertEditItemNotFound': 'Could not find the item you are editing',
  'warehouse.alertSaveFailed': 'Unable to save warehouse item right now.',
  'warehouse.alertDeleteFailed': 'Unable to delete warehouse item right now.',
    'warehouse.alertItemSavedLocal': 'Warehouse item saved on this device. Server sync is still pending.',
    'warehouse.alertItemSavedOffline': 'Warehouse item saved on this device. It will sync when back online.',
    'warehouse.alertItemSavedSynced': 'Warehouse item saved and synced.',
    'warehouse.alertItemDeletedLocal': 'Warehouse item deleted on this device. Server sync is still pending.',
    'warehouse.alertItemDeletedOffline': 'Warehouse item deleted on this device. It will sync when back online.',
    'warehouse.alertItemDeletedSynced': 'Warehouse item deleted and synced.',
  'warehouse.alertViewerCannotCreateTask': 'Your role cannot create tasks.',
  'warehouse.alertReorderTaskAlreadyExists': 'A reorder task already exists for this item.',
  'warehouse.alertTaskCreated': 'Task created.',
  'warehouse.alertTaskCreateFailed': 'Unable to create task right now.',
  'warehouse.sync.pending': 'Inventory changes are queued and waiting to sync.',
  'warehouse.sync.offline': 'Inventory changes are queued locally while this device is offline.',
  'warehouse.sync.blocked': '{count} inventory change(s) are blocked.',
  'warehouse.sync.blockedWithReason': '{count} inventory change(s) are blocked: {reason}',
  'warehouse.sync.retryAction': 'Retry sync',
  'warehouse.sync.retrying': 'Retrying sync...',
  'warehouse.sync.refreshAction': 'Refresh inventory',
  'warehouse.sync.retrySynced': 'Inventory changes are fully synced.',
  'warehouse.sync.retryPending': 'Inventory changes are still queued for sync.',
  'warehouse.sync.retryOffline': 'Inventory changes are saved locally and will sync when connection is restored.',
  'warehouse.sync.retryResolvedMissing': 'Sync completed after skipping {count} missing inventory change(s).',
  'warehouse.sync.lastResult': 'Last sync result: {result}',
  'warehouse.sync.lastAttempted': 'Last attempted: {time}',
  'warehouse.sync.retryFailed': 'Unable to retry inventory sync right now.',
  'warehouse.sync.refreshFailed': 'Unable to refresh inventory sync status right now.',
  'warehouse.confirmDelete': 'Delete this item from the warehouse list?',
  'warehouse.emptySelectFacility': 'Select a facility to view its warehouse items.',
  'warehouse.emptyNoItems': 'No items yet for this facility.',
  'warehouse.emptyNoItemsInTab': 'No items in this category yet.',
  'warehouse.button.receive': 'Receive',
  'warehouse.receive.title': 'Receive: {item}',
  'warehouse.receive.date': 'Delivery date',
  'warehouse.receive.quantity': 'Quantity',
  'warehouse.receive.supplier': 'Supplier',
  'warehouse.receive.lotNumber': 'Lot number',
  'warehouse.receive.lotNumberHelp': 'The feed lot number from the bag or delivery note. It is saved on every feeding of this feed (traceability).',
  'warehouse.receive.stockHelp': 'The quantity is added to stock. The delivery is kept in the history below.',
  'warehouse.receive.save': 'Save delivery',
  'warehouse.receive.summaryTitle': 'Delivery into the store of: {farm}',
  'warehouse.receive.summarySupplier': 'supplier {supplier}',
  'warehouse.receive.summaryLot': 'lot {lot}',
  'warehouse.receive.confirm': 'Confirm delivery',
  'warehouse.receive.back': 'Back',
  'warehouse.receive.history': 'Deliveries',
  'warehouse.receive.latest': 'Last delivery {date}: {quantity} {unit}',
  'warehouse.receive.lotShort': 'lot {lot}',
  'warehouse.receive.error.date': 'Choose the delivery date.',
  'warehouse.receive.error.quantity': 'Enter a quantity greater than 0.',
  'warehouse.mix.prepare': 'Prepare mix',
  'warehouse.mix.title': 'Prepare mix',
  'warehouse.mix.editTitle': 'Edit mix',
  'warehouse.mix.help': 'A mix is a recipe of 2 or more feeds (e.g. a diet transition). No stock moves now: when the crew feeds the mix, each feed is recorded and taken from stock by its percentage.',
  'warehouse.mix.name': 'Mix name',
  'warehouse.mix.namePlaceholder': 'e.g. Transition 80/20',
  'warehouse.mix.feed': 'Feed {n}',
  'warehouse.mix.chooseFeed': 'Choose a feed',
  'warehouse.mix.addFeed': '+ Add feed',
  'warehouse.mix.removeFeed': 'Remove feed',
  'warehouse.mix.total': 'Total: {total} %',
  'warehouse.mix.stockHelp': 'Stock is taken from each feed only when the mix is fed.',
  'warehouse.mix.save': 'Save mix',
  'warehouse.mix.badge': 'Mix:',
  'warehouse.mix.missingFeed': '(deleted feed)',
  'warehouse.mix.invalidHint': 'Recipe not valid: it is not offered in Crew Mode. Edit it.',
  'warehouse.mix.needTwoFeeds': 'Add at least 2 feeds to the inventory first.',
  'warehouse.mix.error.tooFew': 'Choose at least 2 feeds.',
  'warehouse.mix.error.duplicate': 'Each feed can appear only once.',
  'warehouse.mix.error.percent': 'Each percentage must be greater than 0 and less than 100.',
  'warehouse.mix.error.sum': 'The percentages must add up to 100 %.',
  'warehouse.mix.error.unknownFeed': 'Choose a feed on every row.',
  'warehouse.mix.error.nameTaken': 'Another item already uses this name.',
  'warehouse.table.item': 'Item',
  'warehouse.table.category': 'Category',
  'warehouse.table.onHand': 'On hand',
  'warehouse.table.unitCost': 'Unit cost',
  'warehouse.table.estimatedOnHand': 'Est. on hand',
  'warehouse.table.avgPerDay': 'Avg/day',
  'warehouse.table.avgPerDayTooltip': 'Avg/day = total kg logged for the linked feed type in the last {days} days ÷ {days}.',
  'warehouse.table.daysCover': 'Days cover',
  'warehouse.table.reorder': 'Reorder',
  'warehouse.table.actions': 'Actions',
  'warehouse.linkedFeedTypeLabel': 'Linked feed log type:',
  'warehouse.linkedFeedTypeUsageInWindow': '{totalKg} kg logged in last {days}d',
  'warehouse.linkedFeedTypeNoUsageInWindow': 'no usage logged in last {days}d',
  'warehouse.form.addTitle': 'Add item',
  'warehouse.form.editTitle': 'Edit item',
  'warehouse.field.name': 'Name',
  'warehouse.field.category': 'Category',
  'warehouse.field.unit': 'Unit',
  'warehouse.field.onHand': 'On hand',
  'warehouse.field.reorderPoint': 'Reorder point (optional)',
  'warehouse.field.receivedOn': 'Received on',
  'warehouse.field.expiresOn': 'Expires on',
  'warehouse.field.expiresOnOptional': 'Expires on (optional)',
  'warehouse.help.reorderPoint': "Minimum stock: when on hand reaches this number, it's time to reorder.",
  'warehouse.help.expiresOn': 'Use this for expiry / best-before dates. Leave blank if not applicable.',
  'warehouse.reorderNow': 'Reorder now',
  'warehouse.reorderNowTooltip': 'On hand is at (or below) the reorder point.',
  'warehouse.estimatedOnHandLabel': 'Est. on hand:',
  'warehouse.estimatedOnHandTooltip': 'Computed from {anchorDate} (recorded received-on date) minus Feed event usage since that date.',
  'warehouse.estimatedOnHandTooltipCapped': 'The recorded received-on date is older than {capDays} days; the estimate only subtracts usage since {anchorDate} (lookup window cap), so it may overstate true on-hand stock.',
  'warehouse.field.feedTypeKey': 'Feed type key (optional)',
  'warehouse.field.notes': 'Notes (optional)',
  'warehouse.placeholder.itemName': 'e.g., Feed 45% protein',
  'warehouse.placeholder.feedTypeKey': "Matches Feed log 'feedType' (defaults to Name)",
  'warehouse.help.feedTypeKey': 'Used to auto-derive avg daily usage for feed items.',
  'warehouse.help.feedTypeKeySuggestions': 'Suggestions are based on feed types seen in the last {days} days.',
  'warehouse.button.createTask': 'Create task',
  'warehouse.button.creatingTask': 'Creating…',
  'warehouse.button.edit': 'Edit',
  'warehouse.button.delete': 'Delete',
  'warehouse.button.cancel': 'Cancel',
  'warehouse.button.saveChanges': 'Save changes',
  'warehouse.button.addItem': 'Add item',
  'warehouse.section.inventory': 'Inventory',
  'warehouse.category.feed': 'Feed',
  'warehouse.category.chemical': 'Chemical',
  'warehouse.category.equipment': 'Equipment',
  'warehouse.category.other': 'Other',

  'warehouse.taskCreateViewerBlockedTooltip': 'Viewers cannot create tasks.',
  'warehouse.taskTitleReorder': 'Reorder: {item}',
  'warehouse.taskDescriptionReorder': 'Reorder {item}.\n\nOn hand: {onHand} {unit}\nReorder point: {reorderPoint}{estimatedOnHand}\n\nNote: Estimated on-hand is computed from the item’s recorded received-on date minus Feed event usage since that date (cap {capDays} days).',
  // Pre-validation polish (#58/#60)
  'app.downloadLogs': 'Download logs',
  'app.newVersion.banner': 'A new version of the app is available — tap here to update. Saved records are kept.',
  'app.downloadDiagnostics': 'Download diagnostics (support)',
  'app.downloadDiagnostics.hint': 'Technical file for support; it does not contain the farm data.',
  'common.species': 'Species',
  'common.selectPlaceholder': 'Select {label}',
  'species.trout.label': 'Rainbow Trout',
  'species.tilapia.label': 'Nile Tilapia',
  'species.salmon.label': 'Atlantic Salmon',
  'species.shrimp.label': 'Pacific White Shrimp',
  'species.trout.description': 'Cold-water preset for salmonids; includes FCR and length tracking.',
  'species.tilapia.description': 'Warm-water preset with feed protein and temperature checkpoints.',
  'species.salmon.description': 'Marine net-pen preset with salinity capture.',
  'species.shrimp.description': 'Crustacean preset (PL terminology) with salinity + temperature emphasis.',
  'water.tempStatus.noData': 'No data',
  'water.tempStatus.tooCold': 'Too cold',
  'water.tempStatus.tooWarm': 'Too warm',
  'water.tempStatus.optimalRange': 'Optimal range',
  'water.tempStatus.optimalForSfr': 'Optimal for SFR',
  'mortalityManagement.species.trout.description': 'Uses the rainbow trout mortality curve.',
  'mortalityManagement.species.salmon.description': 'Uses the Atlantic salmon mortality curve.',
  'mortalityManagement.species.tilapia.description': 'Uses the tilapia mortality curve.',
  'mortalityManagement.species.shrimp.description': 'Uses the Pacific white shrimp mortality curve.',
  'activity.facility.saved': 'Facility saved',
  'activity.facility.updated': 'Facility updated',
  'activity.facility.deleted': 'Facility deleted',
  'activity.productionUnit.saved': 'Production unit saved',
  'activity.productionUnit.updated': 'Production unit updated',
  'activity.productionUnit.deleted': 'Production unit deleted',
  'activity.task.saved': 'Task saved',
  'activity.task.updated': 'Task updated',
  'activity.task.deleted': 'Task deleted',
  'activity.mortalityMatrix.saved': 'Mortality matrix saved',
  'activity.mortalityMatrix.updated': 'Mortality matrix updated',
  'activity.mortalityMatrix.deleted': 'Mortality matrix deleted',
  'activity.sfrMatrix.saved': 'SFR matrix saved',
  'activity.sfrMatrix.updated': 'SFR matrix updated',
  'activity.sfrMatrix.deleted': 'SFR matrix deleted',
  'activity.lot.saved': 'Lot saved',
  'activity.lot.updated': 'Lot updated',
  'activity.lot.deleted': 'Lot deleted',
  'activity.text.waterLogsUpdated': 'Water logs updated',
  'activity.text.waterLogDeleted': 'Water log deleted',
  'activity.text.productionRecordUpdated': 'Production record updated',
  'activity.text.productionRecordsUpdated': 'Production records updated',
  'activity.text.productionRecordDeleted': 'Production record deleted',
  'activity.text.productionTableMigrated': 'Production table migrated',
  'activity.text.taskCreated': 'Task created',
  'activity.text.taskDeleted': 'Task deleted',
  'activity.text.syncQueued': 'Sync queued',
  'activity.text.queuedRows': 'Queued {count} row(s) for sync',
  'activity.text.migratedRows': 'Migrated {count} row(s) from legacy storage into offline DB',
  // Pre-validation PR 2 (#58/#67): report dataset/column labels, filter vs breakdown help
  'reports.dataset.tasks': 'Tasks',
  'reports.dataset.inventory': 'Inventory',
  'reports.dataset.feed': 'Feed',
  'reports.dataset.feedEvents': 'Feed events (actual fed)',
  'reports.dataset.feedDailyTotals': 'Feed daily totals (derived)',
  'reports.dataset.productionDatatable': 'Production table',
  'reports.dataset.water': 'Water',
  'reports.dataset.lots': 'Lots',
  'reports.dataset.lotCostPerKg': 'Cost per kg (per lot)',
  'reports.dataset.weightSampleIndividual': 'Individual weight/length samples (CV, Fulton K)',
  'reports.dataset.lotGrowthPerformance': 'Lot growth performance (SGR, FCR planned, SFR — weekly)',
  'reports.dataset.mortalityRecording': 'Mortality recording (days recorded / without record — weekly)',
  'reports.dataset.mortalityByCause': 'Mortality by cause (weekly, per lot)',
  'reports.column.cause': 'Cause',
  'reports.column.causeCode': 'Cause code',
  'reports.column.deaths': 'Deaths',
  'reports.column.weekDeaths': 'Deaths in the week (all causes)',
  'reports.column.causeSharePct': 'Share of the week’s deaths (%)',
  'mortality.cause.none': 'No cause',
  'biomass.row.mortalityCauses': 'Causes: {causes}',
  'reports.column.daysInPeriod': 'Days in period',
  'reports.column.daysRecorded': 'Days recorded',
  'reports.column.daysNoMortality': 'Days with no mortality',
  'reports.column.daysNotChecked': 'Days not checked',
  'reports.column.daysWithoutRecord': 'Days without record',
  'reports.dataset.lotProjection': 'Lot projection (harvest date, biomass, feed to harvest)',
  'reports.column.projectionStatus': 'Projection status',
  'reports.column.lastSamplingDate': 'Last sampling',
  'reports.column.lastSamplingWeightG': 'Last sampling weight (g)',
  'reports.column.estimatedWeightTodayG': 'Estimated weight today (g)',
  'reports.column.weightIn30DaysG': 'Weight in 30 days (g)',
  'reports.column.targetWeightG': 'Target weight (g)',
  'reports.column.daysToHarvest': 'Days to harvest',
  'reports.column.biomassNowKg': 'Biomass now (kg)',
  'reports.column.stockAtHarvest': 'Stock at harvest',
  'reports.column.biomassAtHarvestKg': 'Biomass at harvest (kg)',
  'reports.column.feedToHarvestKg': 'Feed to harvest (kg)',
  'reports.column.projectedFcr': 'Projected FCR',
  'reports.column.tgc': 'TGC',
  'reports.column.tgcSource': 'TGC source',
  'reports.column.meanTemperatureC': 'Mean temperature (°C)',
  'reports.column.mortalityRatePctPerDay': 'Mortality (%/day, recorded days)',
  'reports.column.mortalityDaysRecorded': 'Mortality days recorded',
  'reports.column.mortalityWindowDays': 'Days in mortality window',
  'reports.column.mortalityCompletenessPct': 'Mortality completeness (%)',
  'reports.column.lowConfidence': 'Low confidence',
  'reports.column.mixName': 'Mix',
  'reports.column.feedLotNumber': 'Feed lot number',
  'reports.column.feedingResponse': 'Feeding response',
  'reports.column.biomassKg': 'Biomass (kg)',
  'reports.column.biomassKgDerived': 'Biomass (kg, derived)',
  'reports.column.category': 'Category',
  'reports.column.completed': 'Completed',
  'reports.column.convertedCostPerKgMajor': 'Converted cost per kg',
  'reports.column.convertedCurrency': 'Converted currency',
  'reports.column.convertedTotalCostsCents': 'Converted total costs (cents)',
  'reports.column.convertedTotalCostsMajor': 'Converted total costs',
  'reports.column.costEventCount': 'Cost event count',
  'reports.column.costG': 'Costs — Genetics (G)',
  'reports.column.costH': 'Costs — Health (H)',
  'reports.column.costL': 'Costs — Labor (L)',
  'reports.column.costN': 'Costs — Nutrition/feed (N)',
  'reports.column.costOther': 'Costs — Other (I/M/A/F/R)',
  'reports.column.costPerKgMajor': 'Cost per kg',
  'reports.column.createdAt': 'Created at',
  'reports.column.currency': 'Currency',
  'reports.column.currencyBreakdown': 'Currency breakdown (MIXED)',
  'reports.column.dailyMortality': 'Daily mortality',
  'reports.column.date': 'Date',
  'reports.column.description': 'Description',
  'reports.column.dissolvedOxygen': 'Dissolved oxygen',
  'reports.column.dissolvedOxygenSaturation': 'DO saturation',
  'reports.column.dueAt': 'Due at',
  'reports.column.eventCount': 'Event count',
  'reports.column.expectedFcrMax': 'Expected FCR max',
  'reports.column.expectedFcrMin': 'Expected FCR min',
  'reports.column.expectedFcrRangeLabel': 'Expected FCR range',
  'reports.column.expiresOn': 'Expires on',
  'reports.column.farmId': 'Farm ID',
  'reports.column.farmName': 'Facility',
  'reports.column.fcr': 'FCR',
  'reports.column.fcrPlanned': 'FCR (planned)',
  'reports.column.feedConversionRatio': 'Feed ÷ harvested kg',
  'reports.column.feedConversionRatioInFlight': 'Feed to date ÷ current biomass',
  'reports.column.feedConversionRatioInFlightBand': 'Feed ÷ current biomass band',
  'reports.column.economicFcr': 'eFCR (feed ÷ net production)',
  'reports.column.biologicalFcr': 'bFCR (feed ÷ net production + dead fish)',
  'reports.column.startBiomassKg': 'Biomass at start (kg)',
  'reports.column.netProductionKg': 'Net production (kg)',
  'reports.column.deadBiomassKg': 'Biomass of dead fish (kg)',
  'reports.columnHelp.economicFcr': 'Economic FCR: kg of feed per kg produced. Net production = harvested kg + biomass at the end − biomass at the start. Dead or missing fish count as a loss, so eFCR rises with mortality. Use it to compare cycles and costs.',
  'reports.columnHelp.biologicalFcr': 'Biological FCR: what the fish really converted. Adds to net production the weight the dead fish had (deaths × mean weight that day). Always equal to or below eFCR; the gap shows how much feed went into mortality.',
  'reports.columnHelp.startBiomassKg': 'With a report start date: the biomass at the end of the day before. Otherwise, or when the lot started later: first count × mean weight that day.',
  'reports.columnHelp.netProductionKg': 'Harvested kg + biomass at the end − biomass at the start.',
  'reports.columnHelp.deadBiomassKg': 'Deaths in the period × mean weight on their day. Blank when there were deaths and the lot has no sample at all.',
  'reports.columnHelp.feedConversionRatio': 'Feed ÷ harvested kg, without subtracting the stocked biomass. Reads below eFCR for a lot stocked as juveniles.',
  'reports.columnHelp.feedConversionRatioInFlight': 'Feed to date ÷ current biomass, without subtracting the stocked biomass. A quick indicator; compare with eFCR.',
  'reports.column.feedEventCount': 'Feed event count',
  'reports.column.feedKg': 'Feed (kg)',
  'reports.column.feedProteinPct': 'Feed protein (%)',
  'reports.column.feedType': 'Feed type',
  'reports.column.feedTypes': 'Feed types',
  'reports.column.fishStock': 'Stock (counts only)',
  'reports.column.fishStockDerived': 'Stock',
  'reports.column.fxAsOf': 'FX as of',
  'reports.column.fxConversionReason': 'FX conversion reason',
  'reports.column.fxConversionStatus': 'FX conversion status',
  'reports.column.fxRateAsOf': 'FX rate as of',
  'reports.column.fxRateSnapshotId': 'FX rate snapshot ID',
  'reports.column.fxRateSourceId': 'FX rate source ID',
  'reports.column.fxRateSourceVersion': 'FX rate source version',
  'reports.column.fxRateValue': 'FX rate value',
  'reports.column.fxRoundingPolicyVersion': 'FX rounding policy version',
  'reports.column.harvestDate': 'Harvest date',
  'reports.column.harvestEventCount': 'Harvest event count',
  'reports.column.health': 'Health',
  'reports.column.healthDetail': 'Health detail',
  'reports.column.id': 'ID',
  'reports.column.lastRecordedAt': 'Last recorded at',
  'reports.column.lengthReadingCount': 'Readings with length',
  'reports.column.lot': 'Lot',
  'reports.column.lotId': 'Lot ID',
  'reports.column.lotKey': 'Lot key',
  'reports.column.lotOrigin': 'Lot (origin)',
  'reports.column.lotUnit': 'Lot · Unit',
  'reports.column.manualDissolvedOxygen': 'Manual DO',
  'reports.column.manualDissolvedOxygenSaturation': 'Manual DO saturation',
  'reports.column.meanLengthMm': 'Mean length (mm)',
  'reports.column.meanWeightGrams': 'Mean weight (g)',
  'reports.column.mortalityRatePct': 'Mortality rate (%)',
  'reports.column.name': 'Name',
  'reports.column.notes': 'Notes',
  'reports.column.onHand': 'On hand',
  'reports.column.ph': 'pH',
  'reports.column.productionUnitId': 'Production unit ID',
  'reports.column.productionUnitName': 'Production unit',
  'reports.column.readingCount': 'Individual readings',
  'reports.column.receivedOn': 'Received on',
  'reports.column.recordedAt': 'Recorded at',
  'reports.column.reorderPoint': 'Reorder point',
  'reports.column.salinity': 'Salinity',
  'reports.column.salinityPpt': 'Salinity (ppt)',
  'reports.column.sfrPercent': 'SFR (%)',
  'reports.dataset.mortalityEvents': 'Mortality records (one row per record)',
  'reports.dataset.deletedLots': 'Deleted lots (audit)',
  'reports.column.recordedAtLocal': 'Date and time',
  'reports.column.recordKind': 'Record',
  'reports.column.recordedBy': 'Recorded by',
  'reports.column.captureSourceLabel': 'Recorded on',
  'reports.column.lateEntry': 'Late entry',
  'reports.column.createdAtLocal': 'Created',
  'reports.column.deletedAtLocal': 'Deleted on',
  'reports.column.deletedBy': 'Deleted by',
  'reports.column.restored': 'Restored',
  'reports.column.restoredAtLocal': 'Restored on',
  'reports.column.restoredBy': 'Restored by',
  'reports.column.countRecords': 'Counts',
  'reports.column.mortalityRecords': 'Mortality records',
  'reports.column.weightSamples': 'Weight samples',
  'reports.column.feedRecords': 'Feed records',
  'reports.column.waterRecords': 'Water readings',
  'reports.column.firstRecordLocal': 'First record',
  'reports.column.lastRecordLocal': 'Last record',
  'reports.columnHelp.deletedBy': 'Kept from this version on; earlier deletions show blank.',
  'reports.mortalityEvents.kind.deaths': 'Deaths',
  'reports.mortalityEvents.kind.noMortality': 'No mortality',
  'reports.mortalityEvents.kind.notChecked': 'Not checked',
  'reports.mortalityEvents.source.phone': 'Phone',
  'reports.mortalityEvents.source.web': 'Web',
  'reports.mortalityEvents.lateEntry.yes': 'Yes',
  'reports.deletedLots.restored.yes': 'Yes',
  'reports.deletedLots.restored.no': 'No',
  'reports.stockView.title': 'Stock',
  'reports.stockView.recorded': 'As recorded',
  'reports.stockView.reconciled': 'Reconciled with exact counts',
  'reports.stockView.intro': 'Corrects, in the report, the stock of the days before an exact count. Records do not change.',
  'reports.stockView.howTo': 'How does it work? See an example',
  'reports.stockView.example': 'The lot should have 10,000 fish and the count gave 9,600. 400 are missing that nobody recorded. If the previous count was 20 days ago, Sunday removes 20 more fish each day, until it reaches 400 on the count day.',
  'reports.stockView.useFor': 'Use it to: see more realistic biomass, FCR and mortality % in those weeks.',
  'reports.stockView.noChange': 'Does not change: SGR, which uses the mean weight, or any record.',
  'reports.stockView.needs': 'Needs: an exact count that differs from what was expected. The spread reaches the previous exact count or the lot\'s start; another date can be chosen in Lots → History.',
  'reports.stockView.none': 'This report has no counts with a difference. Reconciled looks the same as As recorded.',
  'reports.stockView.summary': 'Reconciled stock · {counts} count(s) · {fish} fish spread',
  'reports.stockView.columnSuffix': '(reconciled)',
  'reports.column.countDifference': 'Count difference',
  'reports.column.countDifferencePct': 'Count difference (%)',
  'reports.column.countDifferenceFrom': 'Difference spread from',
  'reports.columnHelp.countDifference': 'On an exact count\'s day: fish counted − fish expected. Negative = fish missing that no recorded death, harvest or move explains.',
  'reports.columnHelp.countDifferenceFrom': 'Day from which "Stock reconciled" spreads the difference: the previous exact count or the lot\'s start.',
  'reports.column.sgrClassicPercent': 'SGR classic (100·ln, %/day)',
  'reports.column.sgrDays': 'Days between samples',
  'reports.columnHelp.sgrPercent': 'How much the mean weight grew per day, in %. Example: 2.5 g to 3.0 g in 7 days = 2.64 % per day. Uses the mean weight of this sample and the lot\'s next one, and the real days between them: SGR = 100 × (e^g − 1), g = (ln W2 − ln W1) ÷ days. A count or deaths do not change SGR.',
  'reports.columnHelp.sgrClassicPercent': 'SGR as many feed tables and papers compute it: 100 × (ln W2 − ln W1) ÷ days. Use it to compare with them; it reads slightly below SGR (2.60 vs 2.64 for 2.5 g to 3.0 g in 7 days).',
  'reports.columnHelp.sgrDays': 'Days between this week\'s sample and the lot\'s next sample.',
  'reports.columnHelp.meanWeightGrams': 'Mean weight of the week\'s latest sample.',
  'reports.column.sgrPercent': 'SGR (%/day)',
  'reports.column.sharePctG': 'Share % — Genetics',
  'reports.column.sharePctH': 'Share % — Health',
  'reports.column.sharePctL': 'Share % — Labor',
  'reports.column.sharePctN': 'Share % — Nutrition/feed',
  'reports.column.sharePctOther': 'Share % — Other',
  'reports.column.sourceKey': 'Source key',
  'reports.column.harvestCount': 'Harvested (fish)',
  'reports.column.avgLengthMm': 'Mean length (mm)',
  'reports.column.mort': 'Treatment',
  'reports.column.meanFultonK': "Mean Fulton's K",
  'reports.column.upVol': 'Notes',
  'reports.columnHelp.fishStockDerived': 'The exact count on the day there is one; on other days, calculated from recorded deaths, harvests and moves.',
  'reports.columnGroup.produccion': 'Production',
  'reports.columnGroup.alimento': 'Feed',
  'reports.columnGroup.agua': 'Water',
  'reports.columnGroup.costos': 'Costs',
  'reports.columnGroup.registros': 'Records',
  'reports.columnPicker.available': 'Available',
  'reports.columnPicker.chosen': 'In the report (drag or use ↑ ↓)',
  'reports.columnPicker.search': 'Search column…',
  'reports.columnPicker.emptyChosen': 'Tick columns in the list on the left.',
  'reports.columnPicker.noMatch': 'No column matches.',
  'reports.columnPicker.moveUp': 'Move {label} up',
  'reports.columnPicker.moveDown': 'Move {label} down',
  'reports.columnPicker.remove': 'Remove {label}',
  'reports.columnPicker.legend': '# number · abc text · 📅 date · 🕐 date and time · 🧮 calculated',
  'reports.columnKind.number': 'Number',
  'reports.columnKind.text': 'Text',
  'reports.columnKind.date': 'Date',
  'reports.columnKind.datetime': 'Date and time',
  'reports.columnKind.calculated': 'Calculated',
  'reports.pager.showing': 'Showing {from}–{to} of {total}',
  'reports.pager.rows': 'Rows',
  'reports.pager.previous': 'Previous',
  'reports.pager.next': 'Next',
  'reports.value.species.trout': 'Trout',
  'reports.value.species.tilapia': 'Tilapia',
  'reports.value.species.shrimp': 'Shrimp',
  'reports.value.species.salmon': 'Salmon',
  'reports.value.band.below-range': 'Below range',
  'reports.value.band.in-range': 'In range',
  'reports.value.band.above-range': 'Above range',
  'reports.value.band.unavailable': 'No data',
  'reports.value.yesNo.yes': 'Yes',
  'reports.value.yesNo.no': 'No',
  'reports.value.yesNo.true': 'Yes',
  'reports.value.yesNo.false': 'No',
  'reports.value.stage.Active': 'Active',
  'reports.value.stage.Inactive': 'Inactive',
  'reports.value.stage.Harvested': 'Harvested',
  'reports.value.stage.Planned': 'Planned',
  'reports.value.stage.Dormant': 'Dormant',
  'reports.value.tgcSource.observed': 'Observed',
  'reports.value.tgcSource.office': 'Office',
  'reports.value.projectionStatus.ok': 'Ready',
  'reports.value.projectionStatus.noSampling': 'No sampling',
  'reports.value.projectionStatus.noGrowthRate': 'No growth rate',
  'reports.value.projectionStatus.noTemperature': 'No temperature',
  'reports.value.projectionStatus.noStock': 'No stock',
  'reports.value.projectionStatus.hatcheryStage': 'In hatchery',
  'lots.countDifferences.title': 'Count differences',
  'lots.countDifferences.subtitle': 'Reconciled stock spreads each difference over the days before the count. Records do not change.',
  'lots.countDifferences.row': 'Count {date} · Difference {fish} fish',
  'lots.countDifferences.from': 'Spread from',
  'lots.countDifferences.fromDefault': 'Default: {date}',
  'lots.countDifferences.save': 'Save date',
  'lots.countDifferences.useDefault': 'Back to the default',
  'lots.countDifferences.saved': 'Date saved.',
  'lots.countDifferences.outOfRange': 'Choose a date between {min} and {max}.',
  'lots.countDifferences.saveFailed': 'Could not save: {error}',
  'reports.column.species': 'Species',
  'reports.column.speciesKey': 'Species',
  'reports.column.stage': 'Stage',
  'reports.column.stockingDate': 'Stocking date',
  'reports.column.tags': 'Tags',
  'reports.column.temperature': 'Temperature',
  'reports.column.temperatureC': 'Temperature (C)',
  'reports.column.title': 'Title',
  'reports.column.totalCostsMajor': 'Total costs',
  'reports.column.totalFeedKg': 'Total feed (kg)',
  'reports.column.totalHarvestCount': 'Total harvest count',
  'reports.column.totalHarvestKg': 'Total harvest (kg)',
  'reports.column.harvestKg': 'Harvested (kg)',
  'reports.column.unit': 'Unit',
  'reports.column.updatedAt': 'Updated at',
  'reports.column.usageFeedType': 'Usage feed type',
  'reports.column.week': 'Week',
  'reports.column.weekDisplay': 'Week display',
  'reports.column.weight': 'Weight',
  'reports.column.weightCvPercent': 'Weight CV (%)',
  'reports.help.filterHint': 'The filter decides which rows go into the report: facilities, production units, lots and dates. It never changes how rows are grouped — that is the breakdown, chosen in each Panel widget.',
  'dashboardNew.help.breakdownTitle': 'How to group? (breakdown)',
  'dashboardNew.help.breakdownHint': 'The breakdown decides how the report\'s rows are split: Rows (e.g. date by week) and Columns (e.g. one line per Lot · Unit). It never removes rows — to limit the data, edit the report\'s filter in Reports.',
  'dashboardNew.help.reportFilter': 'The data (filter) comes from this report; edit it in Reports.',
  'dashboardNew.help.rowField': 'Each distinct value becomes a table row and a chart point (e.g. Date grouped by week).',
  'dashboardNew.help.columnField': 'Splits each row by this field; with one line per column, each value is a chart series. For lots use "Lot · Unit".',
  'dashboardNew.widgets.filterPrefix': 'Filter',
  'dashboardNew.widgets.breakdownPrefix': 'Breakdown',
  'dashboardNew.widgets.scopeAll': 'all data',
  'dashboardNew.widgets.scopeFacilities': '{count} facility(ies)',
  'dashboardNew.widgets.scopeUnits': '{count} unit(s)',
  'dashboardNew.widgets.scopeLots': '{count} lot(s)',
  'dashboardNew.widgets.scopeFrom': 'from {date}',
  'dashboardNew.widgets.scopeTo': 'to {date}',
  'dashboardNew.widgets.mergedLotsWarning': '"{lot}" groups {count} different lots ({units}). To see them separately, use "Lot · Unit".',
  'density.noVolume': 'The unit has no water volume: set it in Edit facility → Units.',
  'density.overLimitTitle': 'Above the density limit for this weight and unit type (Configuración → Capacidad de carga).',
  'density.limitLabel': 'limit {limit}',
  'costs.facilityWide.option': 'General costs (no lot)',
  'costs.facilityWide.totalLabel': 'Facility-wide costs (not allocated to lots)',
  'costs.facilityWide.empty': 'No facility-wide costs yet (electricity, salaries, rent…).',
  'density.unitType.floatingCage': 'Floating cage',
  'density.unitType.circularTank': 'Circular tank',
  'density.unitType.raceway': 'Raceway',
  'density.unitType.pond': 'Pond',
  'density.unitType.incubator': 'Incubator',
  'editFarm.productionUnits.type': 'Type',
  'editFarm.productionUnits.typeNone': '— not set —',
  'editFarm.productionUnits.typeOther': 'Other: {type}',
  'editFarm.density.title': 'Density limits (kg/m³)',
  'editFarm.density.weightBand': 'Average weight',
  'editFarm.density.upTo': 'up to',
  'editFarm.density.moreThan': 'more than {weight} g',
  'editFarm.density.allWeights': 'any weight',
  'editFarm.density.addBand': '+ Add weight band',
  'editFarm.density.removeBand': 'Remove',
  'editFarm.density.invalid': 'Use positive numbers; each band\'s weight must be higher than the one above.',
  'editFarm.density.help': 'The farm\'s maximum density per average weight and unit type. Empty = no limit. Alimento (whole facility) and Biomasa show the density in red above the limit. Egg and yolk-sac lots are not checked.',
  'editFarm.details.currency.label': 'Currency',
  'editFarm.details.currency.none': '— not set (USD) —',
  'editFarm.details.currency.PEN': 'PEN — Peruvian sol',
  'editFarm.details.currency.USD': 'USD — US dollar',
  'editFarm.details.currency.help': 'Default for new costs and Inventario prices. Records already saved keep their own currency.',
  'feed.view.ariaLabel': 'Feed view',
  'feed.view.farm': 'Whole facility',
  'feed.view.unit': 'By unit',
  'feed.farmTable.intro': 'One row per unit: the recommended ration (SFR × biomass) next to the unit\'s plan and what was fed yesterday. The crew feeds the plan ("Dado según plan").',
  'feed.farmTable.loading': 'Loading the facility…',
  'feed.farmTable.col.unit': 'Unit',
  'feed.farmTable.col.lot': 'Lot',
  'feed.farmTable.col.fish': 'Fish / eggs',
  'feed.farmTable.col.weight': 'Avg weight (g)',
  'feed.farmTable.col.biomass': 'Biomass (kg)',
  'feed.farmTable.col.temperature': 'Temp (°C)',
  'feed.farmTable.measuredIn': '({unit})',
  'feed.farmTable.col.sfr': 'SFR %',
  'feed.farmTable.col.recommended': 'Recommended kg/day',
  'feed.farmTable.col.plan': 'Plan kg/day',
  'feed.farmTable.col.fedYesterday': 'Fed yesterday (kg)',
  'feed.farmTable.col.fedToday': 'Fed today (kg)',
  'feed.farmTable.eggsDash': '— eggs',
  'feed.farmTable.eggsNoRation': 'Lots still as eggs or yolk-sac fry get no ration.',
  'feed.farmTable.feedings': '{count} feedings',
  'feed.farmTable.col.density': 'kg/m³',
  'feed.farmTable.col.flowPerKg': 'L/min/kg',
  'feed.farmTable.noLot': 'No lot',
  'feed.farmTable.eggs': 'eggs',
  'feed.farmTable.total': 'Total',
  'feed.farmTable.useRecommended': 'Use recommended as plan',
  'feed.farmTable.useRecommendedLot': '{lot}: use recommended as plan in its {count} units',
  'feed.farmTable.noPlan': 'No plan',
  'feed.farmTable.noPlanHelp': 'Set the unit\'s feed plan (feed and feedings/day) first in Edit facility → Units.',
  'feed.farmTable.planUpdated': 'Plan updated: {changes}. The crew sees it the next time the phone opens online.',
  'feed.farmTable.planChange': '{unit} {kg} kg/day (was {before})',
  'feed.farmTable.planAlreadyRecommended': 'The plan already was the recommended one: nothing changed.',
  'feed.farmTable.planSaveError': 'The plan could not be saved. Try again.',
  'feed.farmTable.help': 'Recommended = SFR (Matriz SFR, by weight and the latest temperature) × biomass. Egg and yolk-sac lots have no ration. Density is red above the farm\'s limit (Edit facility → Culture); "—" = the unit has no volume.',
  'editFarm.plans.copy.button': 'Copy to the lot\'s other units',
  'editFarm.plans.copy.title': 'Copy feed item, feedings/day, sampling and water parameters to:',
  'editFarm.plans.copy.kg': 'Also copy the same kg/day',
  'editFarm.plans.copy.help': 'Without it, each unit keeps its own kg/day (a unit with no plan yet takes this one\'s). To use each unit\'s recommended ration: Alimento → Whole facility.',
  'editFarm.plans.copy.run': 'Copy to {count} unit(s)',
  'editFarm.plans.copy.done': 'Plans copied to {count} unit(s).',
  'editFarm.plans.point.label': 'Water (T° and O2) measured in',
  'editFarm.plans.point.self': 'This unit',
  'editFarm.plans.point.missing': 'a unit that no longer exists',
  'editFarm.plans.point.help': 'Choose the pond where this unit\'s water is measured. If the unit has no reading of its own today, Sunday uses that pond\'s reading to warn whether feeding is allowed, calculate the ration and the available oxygen, and add up TUA.',
  'editFarm.plans.copyRules.button': 'Copy only rules and measuring point…',
  'editFarm.plans.copyRules.title': 'Copy "When not to feed" ({rules}) and "Oxygen (calculation)" to:',
  'editFarm.plans.copyRules.all': 'All',
  'editFarm.plans.copyRules.none': 'None',
  'editFarm.plans.copyRules.point': 'These units use the water measured in {unit}',
  'editFarm.plans.copyRules.help': 'Feed, feedings, recording mode, sampling and water parameters do not change.',
  'editFarm.plans.copyRules.done': 'Rules copied to {count} unit(s).',
  'lots.hatchery.costPerThousand': 'Cost per 1,000 fry',
  'lots.hatchery.costPerThousandDetail': 'This unit’s costs {total} ÷ live fry × 1,000 (facility-wide costs not included)',
  'lots.hatchery.costPerThousandNone': 'No costs on this lot yet (Costos or the lot\'s Costos tab)',
  'lots.costs.group.option': '{lot} · whole lot ({count} units)',
  'lots.costs.group.unitOption': '{lot} · only {unit}',
  'lots.costs.group.formTitle': 'Cost for the whole lot {lot} ({count} units)',
  'lots.costs.group.splitByCount': 'Split by the eggs / fish stocked in each unit:',
  'lots.costs.group.splitEvenly': 'Split evenly (a unit has no stocked count):',
  'lots.costs.group.saved': 'Cost saved and split across {count} units.',
  'lots.costs.group.splitInto': 'split across {count} units',
  'lots.costs.group.totalLabel': 'Lot total ({count} units)',
  'lots.costs.group.shareNote': 'Share of a whole-lot cost ({total}). Edit or delete it under “whole lot”.',
  'lots.costs.edit.button': 'Edit',
  'lots.costs.edit.title': 'Edit cost',
  'lots.costs.edit.groupTitle': 'Edit whole-lot cost ({count} units) — the total is split again',
  'lots.costs.edit.categoryLocked': 'The category can’t be changed; delete the cost and record it again.',
  'lots.costs.edit.saved': 'Cost updated.',
  'lots.stockUnits.registerCostEggs': 'Record the eggs’ cost →',
  'lots.stockUnits.registerCostFish': 'Record the seed cost →',
  'lots.hatchery.costPerThousandWholeLot': 'Whole lot ({count} incubators)',
  'lots.hatchery.costPerThousandThisUnit': 'This incubator',
  'reports.facilityCosts.note': 'summed for the facility and dates of this report, not allocated to lots (not in any cost per kg).',
  'lots.detail.tab.treatments': 'Treatments',
  'lots.actions.treatment': 'Treatment…',
  'lots.treatments.method.bath': 'Bath',
  'lots.treatments.method.feed': 'In feed',
  'lots.treatments.method.injection': 'Injection',
  'lots.treatments.method.other': 'Other',
  'lots.treatments.dialogTitle': 'Record treatment · {lot}',
  'lots.treatments.field.lots': 'Lots / units',
  'lots.treatments.field.appliedAt': 'Date and time',
  'lots.treatments.field.method': 'Type',
  'lots.treatments.field.product': 'Product',
  'lots.treatments.field.productPick': '— choose from Inventario (Chemicals) —',
  'lots.treatments.field.productOption': '{name} · stock {onHand} {unit}',
  'lots.treatments.field.productOther': 'Other (not in Inventario)',
  'lots.treatments.field.productName': 'Product name',
  'lots.treatments.field.noChemicals': 'No chemicals in Inventario yet: add them under Inventario → Chemicals to take the stock and cost from there.',
  'lots.treatments.field.dose': 'Dose / concentration',
  'lots.treatments.field.quantity': 'Quantity used per unit',
  'lots.treatments.field.duration': 'Duration (min)',
  'lots.treatments.field.reason': 'Reason',
  'lots.treatments.field.prescribedBy': 'Prescribed / authorized by',
  'lots.treatments.field.withdrawalDays': 'Withdrawal (days)',
  'lots.treatments.field.notes': 'Notes',
  'lots.treatments.reason.fungus': 'Fungus',
  'lots.treatments.reason.parasites': 'Parasites',
  'lots.treatments.reason.bacteria': 'Bacteria',
  'lots.treatments.reason.prevention': 'Prevention',
  'lots.treatments.totalQuantity': 'Total from stock: {total} {unit} ({count} units)',
  'lots.treatments.costPreview': 'Cost per unit (Salud): {cost}',
  'lots.treatments.help': 'Saved online (office). The quantity comes out of Inventario and its cost goes to the lot as Salud.',
  'lots.treatments.error.lots': 'Tick at least one lot.',
  'lots.treatments.error.date': 'Enter the date and time.',
  'lots.treatments.error.product': 'Choose or write the product.',
  'lots.treatments.error.number': 'Quantity and duration must be positive numbers.',
  'lots.treatments.error.reason': 'Write the reason.',
  'lots.treatments.error.withdrawal': 'Withdrawal must be whole days (0 or more).',
  'lots.treatments.error.stock': 'Not enough stock: {onHand} {unit} left.',
  'lots.treatments.error.partial': 'Saved for {saved} of {total} lots; check the list and record the rest again.',
  'lots.treatments.error.offline': 'No connection: treatments are saved online. Try again with signal.',
  'lots.treatments.error.save': 'The treatment could not be saved. Try again.',
  'lots.treatments.loadError': 'Treatments could not be loaded (they need a connection).',
  'lots.treatments.title': 'Treatments',
  'lots.treatments.add': 'Record treatment',
  'lots.treatments.empty': 'No treatments recorded.',
  'lots.treatments.deletedList': 'Deleted ({count})',
  'lots.treatments.deleteReason': 'Reason for deleting (kept in the record)',
  'lots.treatments.row.dose': 'Dose {dose}',
  'lots.treatments.row.quantity': '{quantity} {unit} used',
  'lots.treatments.row.duration': '{minutes} min',
  'lots.treatments.row.reason': 'Reason: {reason}',
  'lots.treatments.row.prescribedBy': 'by {name}',
  'lots.treatments.row.withdrawal': 'Withdrawal {days} d — until {until}',
  'lots.treatments.row.noWithdrawal': 'No withdrawal period',
  'lots.treatments.row.deleted': 'Deleted: {reason}',
  'lots.treatments.withdrawalRunning': 'Withdrawal period running until {until}: do not harvest.',
  'lots.treatments.withdrawalWarning': 'Inside the withdrawal period of {product} until {until}. Harvesting now breaks the withdrawal period.',
  'density.unitType.fibreglassTank': 'Fibreglass tank',
  'lots.unitOccupied.warning': 'This unit already holds lot {lots}. The farm keeps one lot per unit: move or harvest it first, unless this is part of a split or merge.',
  'lots.unitOccupied.short': 'already holds {lots}',
  'lots.hatchery.field.vonBayerCount': 'Eggs in 30.5 cm (Von Bayer trough)',
  'lots.hatchery.arrival.vonBayerDiameter': 'Egg diameter from the Von Bayer count: {mm} mm (304.8 mm ÷ eggs).',
  'lots.stockUnits.kind': 'Fish or eggs?',
  'lots.stockUnits.kindFish': '🐟 Fish',
  'lots.stockUnits.kindEggs': '🥚 Eggs',
  'lots.stockUnits.totalEggs': 'Eggs that arrived',
  'lots.stockUnits.eggArrival': 'Egg arrival record (same for every incubator)',
  'lots.stockUnits.eggArrivalLater': 'If you don’t have all the data yet, you can complete it later in Lot → Incubation.',
  'lots.stockUnits.incubators': 'Incubators and eggs per incubator',
  'lots.stockUnits.eggsPerUnit': 'Eggs',
  'lots.stockUnits.averageWeightHint': 'Default for every unit; a unit\'s own weight (next to its count) wins.',
  'lots.stockUnits.weightPerUnit': 'Weight (g), optional',
  'lots.stockUnits.weightPerUnitFor': 'Arrival weight (g) for {unit}',
  'lots.stockUnits.error.eggCountsNeeded': 'Give the number of eggs for every incubator (they become each lot\'s stock).',
  'crewMode.feed.givePlanDetail': '1 of {feedings} feedings · {kgDay} kg/day',
  'feed.farmTable.today': 'today',
  'feed.farmTable.daysAgo': '{days} d ago',
  'feed.farmTable.staleTitle': 'This value is {days} days old: record a new sampling or water reading.',
  'feed.farmTable.eggCount': '{count} eggs',
  'createMember.title': 'New team member',
  'createMember.lead': 'Choose what kind of member this is; only the fields it needs are shown.',
  'createMember.kindQuestion': 'What kind of member?',
  'createMember.kind.operator': 'Operator (PIN only)',
  'createMember.kind.operatorHelp': 'Records in Crew Mode on a crew phone with a 4-digit PIN. No email or password.',
  'createMember.kind.person': 'Person with web access',
  'createMember.kind.personHelp': 'Office or supervisor: signs in at the web with email or username and password.',
  'createMember.kind.device': 'Crew phone (device account)',
  'createMember.kind.deviceHelp': 'One per shared phone. The phone signs in once; then each operator only types their PIN.',
  'createMember.field.name': 'Full name',
  'createMember.field.deviceName': 'Phone name',
  'createMember.devicePlaceholderName': 'Crew phone 1',
  'createMember.field.pin': 'PIN (4 digits, optional now)',
  'createMember.field.email': 'Email',
  'createMember.field.emailPlaceholder': 'person@example.com',
  'createMember.field.deviceEmailPlaceholder': 'an address you control, e.g. you+phone1@…',
  'createMember.field.username': 'Username',
  'createMember.field.password': 'Temporary password',
  'createMember.field.passwordPlaceholder': 'At least {min} characters',
  'createMember.field.role': 'Role',
  'createMember.role.viewer': 'Viewer — sees everything, changes nothing',
  'createMember.role.contributor': 'Contributor — records data',
  'createMember.role.editor': 'Editor — records and edits configuration',
  'createMember.note.operator': 'After creating, the operator appears on the PIN pad of every crew phone the next time it opens online.',
  'createMember.note.device': 'Role: contributor (the lowest that can save records). Never sign in on a crew phone with an admin account.',
  'createMember.note.person': 'The password is temporary: share the username through a safe channel, or send them an activation link from Consola del dueño so they choose their own.',
  'createMember.error.name': 'Enter a name.',
  'createMember.error.email': 'Enter a valid email address.',
  'createMember.error.usernameLength': 'Username must be 3 to 40 characters.',
  'createMember.error.usernameFormat': 'Username allows letters, digits, dot, dash and underscore.',
  'createMember.error.password': 'Password must be at least {min} characters.',
  'createMember.error.credentials': 'Email, username and password are all needed for this member type.',
  'createMember.error.generic': 'The member could not be created.',
  'createMember.error.emailTaken': 'That email is already used by another account.',
  'createMember.error.usernameTaken': 'That username already exists. Choose another.',
  'createMember.submit': 'Create member',
  'createMember.creating': 'Creating…',
  'createMember.created.title': '{name} created',
  'createMember.created.operatorWithPin': 'PIN saved. The operator can record on any crew phone: tap the app icon, choose their name and type the PIN.',
  'createMember.created.operatorNoPin': 'No PIN yet: set it in Consola del dueño (PIN on their row) before they record.',
  'createMember.created.deviceTitle': 'Set up the phone (once):',
  'createMember.created.deviceStep1': 'On the phone, open Chrome at app.sunflowsoftware.com/crew.',
  'createMember.created.deviceStep2': 'Sign in with the username {username} and this password; the phone remembers it.',
  'createMember.created.deviceStep3': 'Chrome menu → Add to Home screen. From then on each operator taps the icon and types their PIN.',
  'createMember.created.deviceStepIphone': 'On an iPhone: open Safari at the same address → Share → Add to Home Screen.',
  'createMember.created.personNext': 'Share the username {username} and the temporary password through a safe channel, or send an activation link from Consola del dueño so they choose their own. The password cannot be shown again.',
  'createMember.created.username': 'Username: {username}',
  'createMember.created.copyUsername': 'Copy username',
  'createMember.created.another': 'Add another member',
  'createMember.created.back': 'Back to Consola del dueño',
  'createMember.technicalDetails': 'Technical details',
  'qa.carryingCapacity': 'Carrying capacity',
  'editFarm.density.movedHint': 'Density limits and minimum water flow are now named tables (one per genetics or season), and each lot keeps the table it was given:',
  'editFarm.density.openCapacity': 'open Capacidad de carga →',
  'capacity.title': 'Carrying capacity',
  'capacity.subtitle': 'Maximum density and minimum water flow by weight, unit type and temperature.',
  'capacity.table': 'Table:',
  'capacity.default': 'default',
  'capacity.defaultBadge': 'Facility default',
  'capacity.noTables': 'No tables yet',
  'capacity.newTableOption': '(new table, not saved)',
  'capacity.empty': 'This facility has no table yet. Use «New table».',
  'capacity.action.new': 'New table',
  'capacity.action.duplicate': 'Duplicate',
  'capacity.action.makeDefault': 'Use as default',
  'capacity.action.delete': 'Delete',
  'capacity.action.deleteConfirm': 'Delete «{name}»? Tap again',
  'capacity.action.applyToLots': 'Apply to those lots too ({count})',
  'capacity.copyName': '{name} (copy)',
  'capacity.usedBy': 'Used by: {lots}',
  'capacity.usedByLot': '{lot} ({units}), copy of {date}',
  'capacity.unused': 'No active lot has its own copy of this table.',
  'capacity.withoutCopy': '{count} active lot(s) without their own table use the default.',
  'capacity.copiesNote': 'Those lots keep the copy they were given: saving changes here applies to new lots. To give them the new values, press «Apply to those lots too».',
  'capacity.field.name': 'Table name',
  'capacity.field.namePlaceholder': 'e.g. OP 2025-10',
  'capacity.density.title': '1 · Maximum density (kg/m³)',
  'capacity.density.help': 'By average weight and unit type. Empty = no limit. Amber from 90 % of the limit, red above it (Biomasa, Alimento). Eggs and yolk-sac alevins are checked with section 3.',
  'capacity.flow.title': '2 · Minimum water flow (L/min per kg)',
  'capacity.flow.help': 'By average weight and water temperature (warmer water needs more flow). Empty = no minimum. Compared in Agua with the unit\'s latest flow and that day\'s temperature; amber when the margin is under 10 %, red below the minimum.',
  'capacity.flow.addTemperature': '+ Add temperature band',
  'capacity.flow.anyTemperature': 'any temperature',
  'capacity.flow.moreThan': 'more than {temperature} °C',
  'capacity.flow.upToTemperature': 'up to (°C)',
  'capacity.flow.weightUpTo': 'up to {weight} g',
  'capacity.flow.cellLabel': 'Minimum L/min per kg',
  'capacity.hatchery.title': '3 · Incubation: minimum flow per 1,000 eggs / yolk-sac alevins',
  'capacity.hatchery.help': 'Technical reference (trout): 1 L/min per 1,000 eyed eggs. Compared in Lote → Incubación and Agua with the unit\'s latest flow.',
  'capacity.error.name': 'Give the table a name.',
  'capacity.error.nameTaken': 'Another table already uses this name.',
  'capacity.error.values': 'Use positive numbers (or leave the cell empty).',
  'capacity.error.weights': 'Each weight band must be greater than the previous one.',
  'capacity.error.temperatures': 'Each temperature band needs a value greater than the previous one.',
  'capacity.status.saved': '«{name}» saved.',
  'capacity.status.default': '«{name}» is now the facility default.',
  'capacity.status.deleted': '«{name}» deleted.',
  'capacity.status.applied': 'New values applied to {count} lot(s).',
  'capacity.status.saveFailed': 'Could not save. Check the connection and try again.',
  'capacity.picker.label': 'Carrying capacity',
  'capacity.picker.default': '{name} (facility default)',
  'capacity.picker.none': 'No table',
  'capacity.picker.help': 'Density and flow limits for this lot\'s genetics (Configuración → Capacidad de carga). The lot keeps a copy.',
  'capacity.lot.copy': '{name} · copy of {date}',
  'capacity.lot.facilityDefault': '{name} (facility default, no copy of its own)',
  'capacity.lot.change': 'Change',
  'capacity.lot.changeHelp': 'Applies to the whole arrival: every unit of this lot and its arrival record.',
  'capacity.flow.minimumLabel': 'minimum {minimum}',
  'capacity.status.overLimit': '▲ over the limit',
  'capacity.status.nearLimit': 'near the limit',
  'capacity.status.underMinimum': '▼ under the minimum',
  'capacity.status.nearMinimum': 'near the minimum',
  'density.nearLimitTitle': 'At 90 % or more of the density limit for this weight and unit type (Capacidad de carga).',
  'water.capacity.fish': '{lot}: flow {flow} L/min ({date}) · {perKg} L/min per kg · minimum {minimum} at {temperature} °C ({table})',
  'water.capacity.hatchery': '{lot}: flow {flow} L/min ({date}) · {perThousand} L/min per 1,000 · minimum {minimum} ({table})',
  'lots.hatchery.flowMinimum': 'Minimum {minimum} L/min per 1,000 ({table})',
  'lots.hatchery.field.broodstockGroup': 'Broodstock group / origin',
  'lots.hatchery.field.spawningDate': 'Spawning (fertilisation) date',
  'lots.hatchery.input.broodstockGroup': 'broodstock {group}',
  'lots.hatchery.input.spawningDate': 'spawned {date}',
  'reports.column.temperatureDaysBorrowed': 'Of these, from another unit of the same arrival or the measuring point',
  'reports.column.broodstockGroup': 'Broodstock group / origin',
  'reports.column.spawningDate': 'Spawning date',
  'lots.move.dateLabel': 'Move date',
  'lots.move.dateHelp': 'Day and time the fish were moved (farm time).',
  'lots.move.sameLot.title': '{unit} already holds {lot}. The fish join that lot.',
  'lots.move.sameLot.fishLabel': 'Fish moved',
  'lots.move.sameLot.loadingStock': 'Working out the stock…',
  'lots.move.sameLot.sourceStock': 'of {count} in {unit} on that date',
  'lots.move.sameLot.sourceStockUnknown': '{unit} has no count on that date.',
  'lots.move.sameLot.preview': '{source}: {sourceBefore} → {sourceAfter} · {target}: {targetBefore} → {targetAfter}',
  'lots.move.sameLot.targetStockUnknown': '{unit} has no count on that date: record a Count in Biomass after the move.',
  'lots.move.sameLot.help': 'Each unit gets a count with its new stock. {unit} stays active with the fish that remain.',
  'lots.move.sameLot.closesSource': 'All fish move: {unit} is closed (Inactive) and its fish carry on in {target}.',
  'lots.move.sameLot.countNote': 'Move: {count} fish from {source} to {target}',
  'lots.move.sameLot.done': 'Move recorded: {count} fish of {lot} from {source} to {target}.',
  'lots.move.sameLot.doneClosed': 'Move recorded: {count} fish of {lot} from {source} to {target}. {source} was closed.',
  'lots.move.arrival.label': 'Fish arriving in {unit}',
  'lots.move.arrival.help': 'Lot stock in {unit} on that date: {count}. Change it if a different number was counted; it is saved as a count in the new unit.',
  'lots.move.arrival.helpUnknown': 'The lot has no count on that date. Type how many arrive in {unit}, or leave it empty and record a count later.',
  'lots.move.arrival.countNote': 'Arrival by move from {source}',
  'lots.move.arrival.offline': 'Move saved offline: once back online, record a count of {lot} in {unit} in Biomass.',
  'lots.move.error.arrivalInvalid': 'Type the arriving amount as a whole number, or leave it empty.',
  'lots.move.checkingDestination': 'Checking which lots are in the destination unit…',
  'lots.move.error.dateRequired': 'Enter the day and time of the move.',
  'lots.move.error.future': 'The move date is in the future. Use the day and time the fish were moved.',
  'lots.move.error.beforeArrival': 'The move date is before {lot} arrived in its current unit. Use a later date.',
  'lots.move.error.recordsAfter': '{lot} has records in {unit} after that date (latest: {date}). Use that date or a later one.',
  'lots.move.error.fishRequired': 'Enter how many fish move (a whole number above 0).',
  'lots.move.error.fishMoreThanSource': 'That is more fish than {unit} holds ({count}).',
  'lots.move.error.nameTaken': 'That unit already has a lot called {lot}. Choose another unit or another name.',
  'lots.move.error.sameLotInvalid': 'Fish can only join the same active lot in another unit of the same facility.',
  'lots.move.error.offline': 'Adding fish to another lot needs a connection. Try again when you are online.',
  'lots.move.editUnitLocked': 'To change the unit use',
  'lots.history.facilityLevel': '{facility} (facility)',
  'lots.history.stageChange': 'Stage: {from} → {to} ({date})',
  'lots.history.eggArrival': 'Egg arrival: {details}',
  'lots.history.eggArrival.eggs': '{eggs} eggs',
  'lots.history.eggArrival.tua': '{tua} TU at arrival',
  'lots.history.stageSet': 'Stage: {to} ({date})',
  'lots.history.capacityTable': 'Carrying-capacity table assigned',
  'lots.history.restored': 'Lot restored (it had been deleted)',
  'lots.deleted.title': 'Deleted lots ({count})',
  'lots.deleted.help': 'A deleted lot can be restored as it was, in its same unit. Its records were never removed.',
  'lots.deleted.row': '{lot} · {unit} · deleted on {date}',
  'lots.deleted.restore': 'Restore',
  'lots.deleted.restoring': 'Restoring…',
  'lots.deleted.restored': '{lot} restored in {unit}.',
  'lots.deleted.offline': 'Restoring a lot needs a connection.',
  'lots.deleted.error': 'Could not restore {lot}: {reason}',
  'lots.deleted.empty': 'No deleted lots in this facility.',
  'lots.history.dataUpdated': 'Lot data updated',
  'lots.history.fishMove': 'Move: {count} fish from {source} to {target}',
  // Findings #199: Importar (Hoja diaria).
  'import.page.title': 'Import records',
  'import.page.subtitle': 'Load the daily records of earlier lots (counts, samplings, mortality and feed) from a CSV sheet.',
  'import.page.waterHint': 'Water readings:',
  'import.page.waterLink': 'water import (advanced, admins only)',
  'import.daily.before.title': 'Before importing',
  'import.daily.before.step1': 'Create each lot in Lots with its real stocking date (New lot, Stock into several units or Eggs received).',
  'import.daily.before.lotsLink': 'Go to Lots',
  'import.daily.before.step2': 'Record its moves in Lots → Actions → Move lot, with the real move date, oldest first.',
  'import.daily.before.step3': 'Download the template, fill it in (one line per unit, lot and day) and load it here. Check the preview and save.',
  'import.daily.before.noLots': 'Importing never creates lots or moves: if a lot does not exist in that unit on that date, the line tells you what to do in Lots.',
  'import.daily.facility': 'Facility',
  'import.daily.template.download': 'Download template (CSV)',
  'import.daily.template.help': 'Open it in Excel, fill it in and save it as CSV. Dates like 2026-09-20 or 20/09/2026; decimals with a point or a comma. Conteo: fish alive at the end of the day, already without the deaths of that day.',
  'import.daily.template.fileName': 'daily-sheet-template.csv',
  'import.daily.template.arrival': 'Arrival',
  'import.daily.template.sample': 'Sample of 50 fish',
  'import.daily.column.date': 'Date',
  'import.daily.column.unit': 'Unit',
  'import.daily.column.lot': 'Lot',
  'import.daily.column.count': 'Count',
  'import.daily.column.method': 'Method',
  'import.daily.column.weight': 'Average weight (g)',
  'import.daily.column.mortality': 'Mortality',
  'import.daily.column.feed': 'Feed (kg)',
  'import.daily.column.notes': 'Notes',
  'import.daily.method.exact': 'exact',
  'import.daily.kind.count': 'Count',
  'import.daily.kind.weight': 'Weight',
  'import.daily.kind.mortality': 'Mortality',
  'import.daily.kind.feed': 'Feed',
  'import.daily.file': 'CSV file',
  'import.daily.fileHelp': 'Choose the daily sheet file. Nothing is saved until you press Save.',
  'import.daily.fileLoaded': 'File loaded: {name}',
  'import.daily.loading': 'Checking the lots and their moves…',
  'import.daily.loadError': 'The lots of this facility could not be checked: {reason}',
  'import.daily.readOnly': 'You can check the file, but only an editor or admin can save.',
  'import.daily.preview.title': 'Preview',
  'import.daily.preview.totals': '{lines} lines: {counts} counts, {weights} samplings, {mortalities} mortalities, {feeds} feedings · {errors} lines with errors',
  'import.daily.preview.fixFirst': 'Fix the lines with errors in your file and load it again. Nothing is saved while there are errors.',
  'import.daily.preview.onlyProblems': 'Show only lines with errors or warnings',
  'import.daily.preview.line': 'Line',
  'import.daily.preview.values': 'Data',
  'import.daily.preview.status': 'Status',
  'import.daily.preview.ready': 'Ready',
  'import.daily.save': 'Save {count} lines',
  'import.daily.saving': 'Saving…',
  'import.daily.saveError': 'Could not save: {reason}',
  'import.daily.saved.title': 'Import saved',
  'import.daily.saved.summary': '{counts} counts, {weights} samplings, {mortalities} mortalities and {feeds} feedings.',
  'import.daily.saved.synced': 'Everything was saved on the server.',
  'import.daily.saved.offline': 'Offline: the records stay on this device and are sent when the connection returns.',
  'import.daily.saved.blocked': '{count} records could not be sent to the server. Check the sync bar.',
  'import.daily.saved.review': 'Check them in',
  'import.daily.saved.biomass': 'Biomass',
  'import.daily.saved.database': 'Database',
  'import.daily.issue.empty': 'The file is empty.',
  'import.daily.issue.missingColumn': 'The "{column}" column is missing. Use the template.',
  'import.daily.issue.noValueColumns': 'The file has no data column (Count, Average weight (g), Mortality or Feed (kg)). Use the template.',
  'import.daily.issue.dateMissing': 'The date is missing.',
  'import.daily.issue.dateInvalid': 'The date "{value}" cannot be read. Use 2026-09-20 or 20/09/2026.',
  'import.daily.issue.dateFuture': 'The date {date} is in the future.',
  'import.daily.issue.unitMissing': 'The unit is missing.',
  'import.daily.issue.unitUnknown': 'Unit "{unit}" does not exist in {facility}.',
  'import.daily.issue.lotNameMissing': 'The lot is missing.',
  'import.daily.issue.methodInvalid': 'Method "{value}" is not valid: use exact or estimation.',
  'import.daily.issue.methodWithoutCount': 'There is a method but no count.',
  'import.daily.issue.countInvalid': 'Count "{value}" is not valid: it must be a whole number.',
  'import.daily.issue.weightInvalid': 'Weight "{value}" is not valid: grams per fish, above 0.',
  'import.daily.issue.mortalityInvalid': 'Mortality "{value}" is not valid: it must be a whole number.',
  'import.daily.issue.feedInvalid': 'Feed "{value}" is not valid: kilos, for example 0.7.',
  'import.daily.issue.noValues': 'The line has no data: fill in Count, Weight, Mortality or Feed.',
  'import.daily.issue.duplicateLine': 'Same unit, lot and day as line {line}: put the data in one line.',
  'import.daily.issue.lotAmbiguous': 'There is more than one lot {lot} in {unit} on {date}. Check the lots in Lots.',
  'import.daily.issue.lotDeleted': 'Lot {lot} in {unit} was deleted. Restore it in Lots → Deleted lots, or fix the line.',
  'import.daily.issue.beforeLotInUnit': 'On {date} lot {lot} was not in {unit} yet (it is there since {from}). Check the date, or the stocking or move date in Lots.',
  'import.daily.issue.lotLeftUnit': 'On {date} lot {lot} was no longer in {unit} (it left on {to}). Check the date or the unit.',
  'import.daily.issue.lotInOtherUnit': 'Lot {lot} was never in {unit} (it is in {units}). If it came to {unit} by a move, record it first in Lots → Move lot with its real date.',
  'import.daily.issue.lotMissingSuggestion': 'Lot {lot} does not exist in {unit}. Did you mean {suggestion}?',
  'import.daily.issue.lotMissing': 'Lot {lot} does not exist in {facility}. Create it first in Lots with its stocking date and load the file again.',
  'import.daily.issue.alreadyOnScreens': 'That day this lot already has {kinds} recorded in the app; the sheet\'s one is added as well.',
  'datatable.lot.createInLotsHint': 'If the lot does not exist, create it in Lots with its stocking date.',
  'water.lot.createInLotsHint': 'If the lot does not exist, create it in Lots with its stocking date.',
  'lots.history.fieldChange': '{field}: {from} → {to}',
  'lots.history.disinfection': 'Disinfection:',
  'lots.history.stockTraits': 'Stock traits',
  'lots.history.yes': 'yes',
  'lots.history.no': 'no',
  // Findings #204: deaths during a move.
  'lots.move.died.label': 'Died during the move',
  'lots.move.died.help': 'Optional. Recorded as Mortality in {unit} (cause Handling), just before the move. If the team already entered them in Mortality, do not repeat them here.',
  'lots.move.died.invalid': 'Enter a whole number of fish (or leave it empty).',
  'lots.move.died.note': 'Mortality during the move from {source} to {target}',
  'lots.move.died.previewDeaths': '{count} died during the move (mortality in {unit})',
  'lots.move.error.fishAndDeathsMoreThanSource': 'The fish moved plus those that died are more than {unit} holds ({count}).',
  'lots.move.arrival.short': '{arrival} arrive of the {expected} that left: {missing} missing.',
  'lots.move.arrival.shortDied': 'They died during the move: record {missing} as mortality',
  'lots.move.arrival.shortCount': 'If they did not die (the earlier count was wrong), leave the number as it is: it stays a count correction.',
  // Findings #210 / #211: transfer protocol, stop rules and oxygen
  'crewMode.feed.hold.stopTitle': 'Do not feed now',
  'crewMode.feed.hold.tempLow': 'Temperature {value} °C ({time}) — minimum {limit} °C',
  'crewMode.feed.hold.tempHigh': 'Temperature {value} °C ({time}) — maximum {limit} °C',
  'crewMode.feed.hold.doLow': 'Inlet O2 {value} mg/L ({time}) — minimum {limit} mg/L',
  'crewMode.feed.hold.stopHelp': 'Tell the supervisor. Measure again before the next feeding.',
  'crewMode.feed.hold.fastTitle': 'Fasting today',
  'crewMode.feed.hold.fastHelp': 'Move on {date}: do not feed this lot today.',
  'crewMode.feed.hold.measure': 'Measure water',
  'crewMode.feed.hold.override': 'Record anyway (the supervisor authorised it)',
  'crewMode.feed.hold.overrideConfirm': 'Did the supervisor authorise feeding? The record keeps the reason.',
  'crewMode.feed.hold.overrideYes': 'Yes, record anyway',
  'crewMode.feed.hold.missing': 'No {params} reading today: measure before feeding.',
  'crewMode.feed.hold.missing.temperature': 'temperature',
  'crewMode.feed.hold.missing.dissolvedOxygen': 'oxygen',
  'crewMode.feed.hold.missingWithPoint': 'No {params} reading today here or in {unit}: measure before feeding.',
  'crewMode.feed.hold.measuredIn': 'measured in {unit}',
  'crewMode.feed.reduced.title': 'Reduced ration (day {day} of {of})',
  'crewMode.feed.reduced.give': 'Give {kg} kg today (normal plan {plan} kg)',
  'crewMode.feed.reduced.giveNoPlan': 'Give {kg} kg today',
  'crewMode.feed.reduced.noKg': 'Give less than the plan: the office had no temperature to work it out.',
  'crewMode.feed.reduced.backToNormal': 'They already eat normally',
  'crewMode.feed.reduced.backToNormalDone': 'Back to the normal plan. It is noted with the next record.',
  'editFarm.plans.rulesLabel': 'When not to feed',
  'editFarm.plans.rules.temp': 'Temperature outside',
  'editFarm.plans.rules.tempMin': 'Minimum temperature (°C)',
  'editFarm.plans.rules.tempMax': 'Maximum temperature (°C)',
  'editFarm.plans.rules.tempTo': 'to',
  'editFarm.plans.rules.do': 'Inlet O2 below',
  'editFarm.plans.rules.help': 'Uses this unit\'s last reading of today. The crew sees "No alimentar ahora" and can record only if the supervisor authorises it. Without a reading today the phone asks to measure first (it does not block).',
  'editFarm.plans.rulesSummary.none': 'no rules',
  'editFarm.plans.rulesSummary.tempRange': 'temperature outside {min}–{max} °C',
  'editFarm.plans.rulesSummary.tempBelow': 'temperature below {min} °C',
  'editFarm.plans.rulesSummary.tempAbove': 'temperature above {max} °C',
  'editFarm.plans.rulesSummary.do': 'inlet O2 < {min} mg/L',
  'editFarm.plans.oxygenLabel': 'Oxygen (calculation)',
  'editFarm.plans.oxygen.outletMin': 'Minimum at the outlet (mg/L)',
  'editFarm.plans.oxygen.suggested': 'suggested {value}',
  'editFarm.plans.oxygen.perKg': 'kg O2 per kg of feed',
  'editFarm.plans.oxygen.help': 'For "O2 alcanza" in Alimento → Toda la instalación: flow × 1440 × (inlet O2 − minimum at the outlet) ÷ kg O2 per kg of feed. Empty = the suggested value and 0.25. Oxygen use rises for hours after a meal: if it runs short, split the ration into more feedings.',
  'editFarm.plans.error.rules': 'Check the rules: temperatures between −5 and 40 °C (minimum below maximum) and O2 between 0.1 and 20 mg/L.',
  'editFarm.plans.error.oxygen': 'Check the oxygen: minimum at the outlet between 0.1 and 20 mg/L and kg O2 per kg of feed between 0.05 and 1.',
  'editFarm.transferProtocol.title': 'Transfer protocol',
  'editFarm.transferProtocol.col.maxWeight': 'Weight up to (g)',
  'editFarm.transferProtocol.col.fastDays': 'Fasting before (days)',
  'editFarm.transferProtocol.col.reducedDays': 'Reduced ration (days)',
  'editFarm.transferProtocol.col.deltaC': 'Calculation T° −Δ °C',
  'editFarm.transferProtocol.openBand': 'heavier',
  'editFarm.transferProtocol.addRow': 'Add row',
  'editFarm.transferProtocol.removeRow': 'Remove',
  'editFarm.transferProtocol.help': 'One row per weight band (leave "Peso hasta" empty for the heaviest fish). Lotes → lot → "Programar traslado" uses it for the fasting days; Trasladar lote proposes the band from the lot\'s last weight and works out the reduced ration: the ration for the fish that arrive at the destination\'s last temperature minus Δ °C. Below the SFR table it is extrapolated from its two lowest temperatures.',
  'editFarm.transferProtocol.invalid': 'Check the rows: weight above 0 (only one row without weight, no repeated weights), whole days from 0 to 30 and Δ from 0 to 20 °C.',
  'feed.farmTable.col.oxygenCapacity': 'O2 supports (kg/day)',
  'feed.farmTable.oxygenHelp': 'Feed the water can carry: last flow × 1440 × (last inlet O2 − minimum at the outlet) ÷ kg O2 per kg of feed (Editar instalación → Unidades → plan). "—" = no flow or oxygen reading. Red = the plan is above it.',
  'feed.farmTable.overOxygenTitle': 'The plan is above what the oxygen supports: lower it or split it into more feedings.',
  'feed.farmTable.stoppedToday': 'today',
  'feed.farmTable.stoppedTitle': 'Today\'s last reading breaks the unit\'s "Cuándo no alimentar" rule: the crew sees "No alimentar ahora".',
  'feed.farmTable.fastingToday': 'Fasting today (move)',
  'feed.farmTable.reducedToday': 'Reduced ration day {day} of {of}: {kg} kg/day',
  'lots.transferPlan.title': 'Transfer',
  'lots.transferPlan.schedule': 'Schedule move',
  'lots.transferPlan.change': 'Change',
  'lots.transferPlan.clear': 'Remove',
  'lots.transferPlan.date': 'Move date',
  'lots.transferPlan.band': 'Protocol',
  'lots.transferPlan.proposedBand': 'Row {band} (by the last weight)',
  'lots.transferPlan.chooseBand': 'Choose a row',
  'lots.transferPlan.bandOption': 'Row {band}: {weight} · fasting {fast} d · reduced {days} d · −{delta} °C',
  'lots.transferPlan.openBand': 'heavier',
  'lots.transferPlan.planned': 'Planned move: {date} · Fasting: {fasting} · Reduced ration {days} days at T° −{delta} °C',
  'lots.transferPlan.noFasting': 'none',
  'lots.transferPlan.help': 'The phone shows "Ayuno hoy" on the fasting days. Record the move itself in Trasladar lote.',
  'lots.transferPlan.lastMove': 'Last move: {date}.',
  'lots.transferPlan.ration': 'Reduced ration {kg} kg/day for {days} days (T° −{delta} °C).',
  'lots.transferPlan.rationNoKg': 'Reduced ration for {days} days, no kg (no temperature, weight or stock at the move).',
  'lots.transferPlan.extrapolated': 'Below the SFR table (extrapolated).',
  'lots.transferPlan.today': 'Today: day {day} of {of}.',
  'lots.transferPlan.fastingRecord': 'Fasting',
  'lots.transferPlan.fastingKept': '{date}: no feed recorded ✓',
  'lots.transferPlan.fastingFed': '{date}: {kg} kg recorded',
  'lots.transferPlan.invalid': 'Choose a date from today and a protocol row.',
  'lots.transferPlan.saveFailed': 'Could not save. Try again.',
  'lots.transferProtocol.title': 'Transfer protocol',
  'lots.transferProtocol.proposed': '{band} (last weight {weight} g)',
  'lots.transferProtocol.noWeight': 'Choose a row (the lot has no weight)',
  'lots.transferProtocol.none': 'No protocol for this move',
  'lots.transferProtocol.band.upTo': 'up to {max} g',
  'lots.transferProtocol.band.between': '{min}–{max} g',
  'lots.transferProtocol.band.above': 'over {min} g',
  'lots.transferProtocol.band.all': 'all weights',
  'lots.transferProtocol.fastingPlanned': 'Planned fasting: {days} (move planned for {date}).',
  'lots.transferProtocol.noFasting': 'Move planned for {date}, without fasting.',
  'lots.transferProtocol.noReducedDays': 'This row has no reduced-ration days.',
  'lots.transferProtocol.ration': 'Reduced ration for {days} days: {kg} kg/day ({fish} fish × {weight} g at {temperature} − {delta} = {calc} °C).',
  'lots.transferProtocol.rationExtrapolated': 'Reduced ration for {days} days: {kg} kg/day ({fish} fish × {weight} g at {temperature} − {delta} = {calc} °C), below the SFR table (extrapolated).',
  'lots.transferProtocol.rationMissing': 'Reduced ration for {days} days, without kg: missing {missing}. The phone shows the reduced days with the normal plan.',
  'lots.transferProtocol.missing.temperature': 'a temperature in the destination unit',
  'lots.transferProtocol.missing.weight': 'a weight of the lot',
  'lots.transferProtocol.missing.stock': 'the number of fish',
  'lots.transferProtocol.fromDay': 'From {date}.',
  'lots.transferProtocol.saveFailed': 'The move was saved, but not its reduced ration. Tell us the lot and the date.',
} as const

export type TranslationKey = keyof typeof en
